import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Topic, Comment, User, MapDrawing, BasemapId, TopicVisibility, TopicAudienceMember, ProfileTab, TopicDraft, CommentDraft, DraftItem } from '../../types';
import GeoLabel from '../common/GeoLabel';
import GeometryLabel from '../common/GeometryLabel';
import { getCachedLabel, reverseGeocode } from '../../utils/revgeo';
import { lineLengthKm, polygonAreaSqM } from '../../utils/geometry';
import {
  buildEditorialDrawings,
  buildEditorialFeaturesFromDrawings,
  inferEditorialStyleFromDrawings,
  getEditorialBounds,
  getEditorialRepresentativeLocation,
  limitEditorialFeatures,
  parseEditorialText,
  summarizeEditorialProperties,
  EDITORIAL_CATEGORY_PALETTE_OPTIONS,
  EDITORIAL_CHORO_PALETTE_OPTIONS,
  EDITORIAL_POINT_SHAPE_OPTIONS,
  EDITORIAL_LINE_STYLE_OPTIONS
} from '../../utils/editorialImport';
import type { EditorialStyleConfig, EditorialImportResult, EditorialDrawing } from '../../utils/editorialImport';
// import VoteButton from '../common/VoteButton';
import { ChatIcon, ArrowLeftIcon, ShareIcon, FlagIcon, EditIcon, DeleteIcon, MultiSelectIcon, ChevronDownIcon, LiveDotIcon, TrendingUpIcon, MessagePlusIcon, PlusIcon, DiskIcon, PointIcon, LineIcon, PolygonIcon, ReplyIcon, SearchIcon, SortIcon, PinIcon, LockIcon, AppLogo } from '../common/Icons';
import UserMenu from '../common/UserMenu';
import MessagesModal from '../Messages/MessagesModal';
import VoteButton from '../common/VoteButton';
import SaveButton from '../common/SaveButton';
import LinkifiedText from '../common/LinkifiedText';
import { usersApi, messagesApi, adminApi, topicsApi } from '../../services/api';
import { useNotifications } from '../Notifications/NotificationProvider';
import { searchApi } from '../../services/api';
import { BASEMAP_OPTIONS, DEFAULT_BASEMAP, BasemapConfig } from '../../constants/basemaps';
import type { SearchGroupResult, SearchUserResult, SearchTopicResult, SearchCommentResult, SearchGroup } from '../../types';
import ProfileContentPanel from './ProfileContentPanel';

const COMMENT_SORT_OPTIONS: Array<{ value: 'oldest' | 'newest' | 'popularity'; label: string; helper?: string }> = [
  { value: 'newest', label: 'Newest First', helper: 'Latest activity on top' },
  { value: 'oldest', label: 'Oldest First', helper: 'Chronological order' },
  { value: 'popularity', label: 'Most Popular', helper: 'Highest votes first' },
];

const COMMENT_PAGE_SIZE = 20;
const DEFAULT_EDITORIAL_STYLE: EditorialStyleConfig = {
  point: { mode: 'auto', shape: 'pin', sizeMin: 4, sizeMax: 12 },
  line: { mode: 'auto' },
  polygon: { mode: 'auto' }
};

type SuggestionItem = {
  id: string;
  group: SearchGroup;
  label: string;
  description?: string;
  payload: SearchUserResult | SearchTopicResult | SearchCommentResult;
};

const wasCommentEdited = (comment: Comment): boolean => {
  try {
    const createdAt = new Date(comment.createdAt).getTime();
    const updatedAt = comment.updatedAt ? new Date(comment.updatedAt).getTime() : createdAt;
    return updatedAt - createdAt > 10000;
  } catch {
    return false;
  }
};

const getIsPortrait = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia) {
      const portraitQuery = window.matchMedia('(orientation: portrait)');
      if (typeof portraitQuery.matches === 'boolean') {
        return portraitQuery.matches;
      }
    }
    return window.innerHeight >= window.innerWidth;
  } catch {
    return false;
  }
};

type LineEstimateMode = 'distance' | 'walking' | 'cycling' | 'driving';
type LineEstimateUnit = 'km' | 'mi' | 'm' | 'ft';

const LINE_ESTIMATE_SPEEDS_KMH: Record<Exclude<LineEstimateMode, 'distance'>, number> = {
  walking: 5,
  cycling: 15,
  driving: 50
};

const LINE_UNIT_FACTORS: Record<LineEstimateUnit, number> = {
  km: 1,
  mi: 0.621371192,
  m: 1000,
  ft: 3280.839895
};

const LINE_UNIT_LABELS: Record<LineEstimateUnit, string> = {
  km: 'km',
  mi: 'mi',
  m: 'm',
  ft: 'ft'
};

const LINE_UNIT_OPTIONS: Array<{ value: LineEstimateUnit; label: string }> = [
  { value: 'km', label: LINE_UNIT_LABELS.km },
  { value: 'mi', label: LINE_UNIT_LABELS.mi },
  { value: 'm', label: LINE_UNIT_LABELS.m },
  { value: 'ft', label: LINE_UNIT_LABELS.ft }
];

type AreaEstimateUnit = 'sq_km' | 'sq_mi' | 'ha' | 'sq_m' | 'acres';

const AREA_UNIT_FACTORS: Record<AreaEstimateUnit, number> = {
  sq_m: 1,
  sq_km: 1 / 1_000_000,
  ha: 1 / 10_000,
  sq_mi: 1 / 2_589_988.110336,
  acres: 1 / 4_046.8564224
};

const AREA_UNIT_LABELS: Record<AreaEstimateUnit, string> = {
  sq_m: 'm²',
  sq_km: 'km²',
  ha: 'ha',
  sq_mi: 'mi²',
  acres: 'ac'
};

const AREA_UNIT_OPTIONS: Array<{ value: AreaEstimateUnit; label: string }> = [
  { value: 'sq_km', label: AREA_UNIT_LABELS.sq_km },
  { value: 'sq_mi', label: AREA_UNIT_LABELS.sq_mi },
  { value: 'ha', label: AREA_UNIT_LABELS.ha },
  { value: 'acres', label: AREA_UNIT_LABELS.acres },
  { value: 'sq_m', label: AREA_UNIT_LABELS.sq_m }
];

const formatLineDistance = (distanceKm: number, unit: LineEstimateUnit): string => {
  if (!Number.isFinite(distanceKm)) return '';
  const factor = LINE_UNIT_FACTORS[unit];
  const label = LINE_UNIT_LABELS[unit];
  const value = distanceKm * factor;
  const abs = Math.abs(value);
  let decimals = 2;
  if (unit === 'm' || unit === 'ft') {
    if (abs >= 100) {
      decimals = 0;
    } else if (abs >= 10) {
      decimals = 1;
    } else {
      decimals = 2;
    }
  } else if (abs >= 100) {
    decimals = 0;
  } else if (abs >= 10) {
    decimals = 1;
  } else if (abs >= 1) {
    decimals = 2;
  } else {
    decimals = 3;
  }
  return `${value.toFixed(decimals)} ${label}`;
};

const formatLineDuration = (minutes: number): string => {
  if (!Number.isFinite(minutes)) return '';
  if (minutes < 1) return '<1 min';
  const rounded = Math.round(minutes);
  if (rounded < 60) return `${rounded} min`;
  const hours = Math.floor(rounded / 60);
  const mins = rounded % 60;
  if (mins === 0) return `${hours} hr`;
  return `${hours} hr ${mins} min`;
};

const formatArea = (areaSqM: number, unit: AreaEstimateUnit): string => {
  if (!Number.isFinite(areaSqM)) return '';
  const factor = AREA_UNIT_FACTORS[unit];
  const label = AREA_UNIT_LABELS[unit];
  const value = areaSqM * factor;
  const abs = Math.abs(value);
  let decimals = 2;
  if (abs >= 100) {
    decimals = 0;
  } else if (abs >= 10) {
    decimals = 1;
  } else if (abs >= 1) {
    decimals = 2;
  } else {
    decimals = 3;
  }
  return `${value.toFixed(decimals)} ${label}`;
};


// Profile content panel component for user menu


interface TopicPanelProps {
  topics: Topic[];
  comments: Comment[];
  drawings?: MapDrawing[];
  allDrawings?: MapDrawing[];
  loading?: boolean;
  error?: any;
  selectedTopic?: Topic | null;
  onTopicSelect: (topic: Topic, focusCommentId?: string) => void;
  onBackToTopics: () => void;
  onCreateTopic?: () => void;
  onCancelCreate?: () => void;
  onSubmitCreate?: (topicData: {
    title: string;
    content: string;
    basemap: BasemapId;
    location: { latitude: number; longitude: number; country?: string; region?: string; city?: string };
    language: string;
    visibility: TopicVisibility;
    audienceUserIds?: string[];
    isNsfw?: boolean;
    isEditorial?: boolean;
    isAnonymous?: boolean;
    editorialDrawings?: EditorialDrawing[];
  }) => Promise<void> | void;
  onEditorialChange?: (isEditorial: boolean) => void;
  onEditorialPreviewDrawingsChange?: (drawings: EditorialDrawing[]) => void;
  onEditorialPreviewFocusDrawing?: (drawing: { id: string; geometry: string; type: 'POINT' | 'LINE' | 'POLYGON' }) => void;
  onApplyEditorialChanges?: (topicId: string, drawings: EditorialDrawing[]) => Promise<boolean> | boolean;
  savingTopic?: boolean;
  onTopicVote: (topicId: string, voteType: 'upvote' | 'downvote') => void;
  onCommentVote: (commentId: string, voteType: 'upvote' | 'downvote') => void;
  onCreateComment?: () => void;
  onPrepareNewComment?: () => void;
  onSubmitComment?: (commentData: { content: string; latitude?: number; longitude?: number; isAnonymous?: boolean }) => void;
  savingComment?: boolean;
  onConfirmIfDirty?: () => boolean;
  onSubmitReply?: (parentId: string, replyData: { content: string }) => void;
  onCancelComment?: () => void;
  userVotes: {
    topics: { [topicId: string]: 'upvote' | 'downvote' | null };
    comments: { [commentId: string]: 'upvote' | 'downvote' | null };
  };
  userRole: 'READER' | 'AUTHOR' | 'MODERATOR';
  isExpanded: boolean;
  isCreating?: boolean;
  isCreatingComment?: boolean;
  showUserMenu?: boolean;
  onShowUserMenu?: (show: boolean) => void;
  userMenuView?: 'menu' | 'profile' | 'messages' | 'notifications' | null;
  onUserMenuViewChange?: (view: 'menu' | 'profile' | 'messages' | 'notifications' | null) => void;
  onExpandPanel?: () => void;
  onCollapsePanel?: () => void;
  onTopicsTabChange?: (tab: 'popular' | 'live' | 'private') => void;
  selectedLocation?: { lat: number; lng: number };
  commentLocation?: { lat: number; lng: number } | null;
  selectedBasemap?: BasemapId;
  onBasemapChange?: (id: BasemapId) => void;
  basemapOptions?: BasemapConfig[];
  // Drawing controls for topic creation
  drawingMode?: 'none' | 'marker' | 'line' | 'polygon';
  onSetDrawingMode?: (mode: 'none' | 'marker' | 'line' | 'polygon') => void;
  onUndoPoint?: () => void;
  onClearPath?: () => void;
  onFinishDrawing?: () => void; // Only used in creation mode, not editing
  hasPendingDrawing?: boolean;
  getDraftGeometry?: (options?: { silent?: boolean }) => any | null; // Function to check if there's unfinished drawing
  hasPathPointsInProgress?: () => boolean; // Function to check if there are pathPoints being drawn
  lineEstimatePreference?: { enabled: boolean; mode: LineEstimateMode; unit: LineEstimateUnit };
  onLineEstimatePreferenceChange?: (pref: { enabled: boolean; mode: LineEstimateMode; unit: LineEstimateUnit }) => void;
  areaEstimatePreference?: { enabled: boolean; unit: AreaEstimateUnit };
  onAreaEstimatePreferenceChange?: (pref: { enabled: boolean; unit: AreaEstimateUnit }) => void;
  // Share/Report
  onShareTopic?: (topicId: string) => void;
  onReportTopic?: (topicId: string) => void;
  onReportComment?: (commentId: string) => void;
  // Focus map to entry
  onFocusTopicLocation?: () => void;
  onFocusCommentLocation?: (comment: Comment) => void;
  onShareComment?: (commentId: string) => void;
  // Comment selection for drawings
  selectedComment?: Comment | null;
  onCommentSelect?: (comment: Comment | null) => void;
  onCommentSelectById?: (commentId: string) => void;
  commentClickCounter?: number;
  selectedCommentId?: string | null;
  onBackToSearch?: () => void;
  // Current user for edit/delete permissions
  currentUserId?: string;
  currentUser?: User | null;
  // Edit/Delete callbacks
  onEditTopic?: (topicId: string, update: { content?: string; visibility?: TopicVisibility; audienceUserIds?: string[] }) => void;
  onDeleteTopic?: (topicId: string) => void;
  onEditComment?: (commentId: string, newContent: string) => void;
  onDeleteComment?: (commentId: string) => void;
  // Moderator-specific delete functions
  onModeratorDeleteTopic?: (topicId: string, reason?: string) => void;
  onModeratorDeleteComment?: (commentId: string, reason?: string) => void;
  // Auth controls
  onLogout?: () => void;
  // Multi-select drawings support
  isMultiSelectActive?: boolean;
  onToggleMultiSelect?: () => void;
  multiSelectedCommentIds?: string[];
  onMultiSelectedChange?: (ids: string[]) => void;
  commentsWithDrawings?: string[];
  // Global search (managed by App). Optional for tests/local use.
  searchQuery?: string;
  onSearchChange?: (q: string) => void;
  spatialSearchCenter?: { lat: number; lng: number } | null;
  spatialSearchRadiusKm?: number;
  spatialSearchPicking?: boolean;
  onSpatialSearchPickRequest?: () => void;
  onSpatialSearchCenterClear?: () => void;
  onSpatialSearchRadiusChange?: (radiusKm: number) => void;
  // Shape editing controls
  isEditingShape?: boolean;
  editingDrawingData?: any;
  onSaveEditShape?: () => Promise<boolean> | boolean;
  onCancelEditShape?: () => void;
  onStartEditDrawing?: (drawing: any) => void;
  // Close any open popups/modals
  onClosePopups?: () => boolean; // Returns true if any popups were closed
  // Profile modal props
  profileUserId?: string | null;
  profileTab?: ProfileTab | null;
  onSetProfileUserId?: (userId: string | null) => void;
  onProfileTabChange?: (tab: ProfileTab) => void;
  // Draft support
  commentDrafts?: Record<string, CommentDraft>;
  resumeTopicDraft?: TopicDraft | null;
  resumeCommentDraft?: CommentDraft | null;
  onConsumeTopicResumeDraft?: () => void;
  onConsumeCommentResumeDraft?: (topicId?: string) => void;
  onSaveTopicDraft?: (draft: Omit<TopicDraft, 'id' | 'type' | 'userId' | 'updatedAt'> & { id?: string }) => TopicDraft | null;
  onDeleteTopicDraft?: (draftId?: string) => void;
  onSaveCommentDraft?: (draft: { topicId: string; topicTitle?: string; content: string; location?: { lat: number; lng: number }; drawing?: any }) => void;
  onDeleteCommentDraft?: (topicId: string) => void;
  drafts?: DraftItem[];
  onResumeDraft?: (draft: DraftItem) => void;
  pendingDrawing?: any | null;
  pendingEditorialDrawingsCount?: number;
  onApplyTopicDraftLocation?: (draft?: TopicDraft | null) => void;
  onApplyCommentDraftLocation?: (draft?: CommentDraft | null) => void;
}

const TopicPanel: React.FC<TopicPanelProps> = ({
  topics,
  comments,
  drawings = [],
  allDrawings,
  loading = false,
  error = null,
  selectedTopic,
  onTopicSelect,
  onBackToTopics,
  onCreateTopic,
  onCancelCreate,
  onSubmitCreate,
  onEditorialChange,
  onEditorialPreviewDrawingsChange,
  onEditorialPreviewFocusDrawing,
  onApplyEditorialChanges,
  savingTopic = false,
  onTopicVote,
  onCommentVote,
  onCreateComment,
  onPrepareNewComment,
  onSubmitComment,
  savingComment = false,
  onConfirmIfDirty,
  onSubmitReply,
  onCancelComment,
  userVotes,
  userRole,
  isExpanded,
  isCreating = false,
  isCreatingComment = false,
  showUserMenu = false,
  onShowUserMenu,
  userMenuView = null,
  onUserMenuViewChange,
  onExpandPanel,
  onCollapsePanel,
  onTopicsTabChange,
  selectedLocation,
  commentLocation,
  selectedBasemap = DEFAULT_BASEMAP,
  onBasemapChange,
  basemapOptions = BASEMAP_OPTIONS,
  drawingMode,
  onSetDrawingMode,
  onUndoPoint,
  onClearPath,
  onFinishDrawing,
  hasPendingDrawing,
  getDraftGeometry,
  hasPathPointsInProgress,
  lineEstimatePreference,
  onLineEstimatePreferenceChange,
  areaEstimatePreference,
  onAreaEstimatePreferenceChange,
  onShareTopic,
  onReportTopic,
  onReportComment,
  onFocusTopicLocation,
  onFocusCommentLocation,
  onShareComment,
  selectedComment,
  onCommentSelect,
  onCommentSelectById,
  commentClickCounter = 0,
  selectedCommentId = null,
  onBackToSearch,
  currentUserId,
  currentUser,
  onEditTopic,
  onDeleteTopic,
  onEditComment,
  onDeleteComment,
  onModeratorDeleteTopic,
  onModeratorDeleteComment,
  onLogout,
  isMultiSelectActive = false,
  onToggleMultiSelect,
  multiSelectedCommentIds = [],
  onMultiSelectedChange,
  commentsWithDrawings = [],
  // controlled search props (optional)
  searchQuery: controlledSearch,
  onSearchChange,
  spatialSearchCenter,
  spatialSearchRadiusKm = 10,
  spatialSearchPicking = false,
  onSpatialSearchPickRequest,
  onSpatialSearchCenterClear,
  onSpatialSearchRadiusChange,
  // Shape editing controls
  isEditingShape = false,
  editingDrawingData,
  onSaveEditShape,
  onCancelEditShape,
  onStartEditDrawing,
  onClosePopups,
  // Profile modal props
  profileUserId,
  profileTab = null,
  onSetProfileUserId,
  onProfileTabChange,
  commentDrafts = {},
  resumeTopicDraft,
  resumeCommentDraft,
  onConsumeTopicResumeDraft,
  onConsumeCommentResumeDraft,
  onSaveTopicDraft,
  onDeleteTopicDraft,
  onSaveCommentDraft,
  onDeleteCommentDraft,
  drafts = [],
  onResumeDraft,
  pendingDrawing,
  pendingEditorialDrawingsCount,
  onApplyTopicDraftLocation,
  onApplyCommentDraftLocation
}) => {
  const [isPortrait, setIsPortrait] = useState<boolean>(() => getIsPortrait());
  const [activeTab, setActiveTab] = useState<'popular' | 'live' | 'private'>('popular');
  useEffect(() => {
    onTopicsTabChange?.(activeTab);
  }, [activeTab, onTopicsTabChange]);
  // search is controlled by parent (App) when provided; otherwise fallback to local state
  const [localSearch, setLocalSearch] = useState('');
  const searchQuery = controlledSearch ?? localSearch;
  const setSearchQuery = onSearchChange ?? setLocalSearch;
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchErrorMessage, setSearchErrorMessage] = useState<string | null>(null);
  const normalizedSearch = useMemo(() => searchQuery.trim().toLowerCase(), [searchQuery]);
  const [userResults, setUserResults] = useState<SearchGroupResult<SearchUserResult>>({ items: [], hasMore: false, total: 0 });
  const [topicResults, setTopicResults] = useState<SearchGroupResult<SearchTopicResult>>({ items: [], hasMore: false, total: 0 });
  const [commentResults, setCommentResults] = useState<SearchGroupResult<SearchCommentResult>>({ items: [], hasMore: false, total: 0 });
  const [searchSuggestions, setSearchSuggestions] = useState<SuggestionItem[]>([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isSearchDrawerOpen, setIsSearchDrawerOpen] = useState(false);
  const [groupPages, setGroupPages] = useState<{ users: number; topics: number; comments: number }>({ users: 1, topics: 1, comments: 1 });
  const [loadingGroups, setLoadingGroups] = useState<Partial<Record<SearchGroup, boolean>>>({});
  const searchAbortRef = useRef<AbortController | null>(null);
  const suggestionHideTimeout = useRef<number | null>(null);
  const SEARCH_PAGE_SIZE = 5;
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const spatialFiltersRef = useRef<HTMLDivElement | null>(null);
  const wasExpandedBeforeSearchRef = useRef(false);
  const restoreSearchAfterTopicRef = useRef(false);
  const [commentSortBy, setCommentSortBy] = useState<'oldest' | 'newest' | 'popularity'>('oldest');
  const [visibleCommentCount, setVisibleCommentCount] = useState<number>(COMMENT_PAGE_SIZE);
  const [showSpatialFilters, setShowSpatialFilters] = useState(false);
  const [forceCollapsedAfterSearch, setForceCollapsedAfterSearch] = useState(false);
  const highlightSearchText = React.useCallback((text: string) => {
    if (!text || !normalizedSearch) return text;
    const lower = text.toLowerCase();
    const parts: Array<string | { value: string }> = [];
    let index = 0;
    let matchIndex = lower.indexOf(normalizedSearch, index);

    while (matchIndex !== -1) {
      if (matchIndex > index) {
        parts.push(text.slice(index, matchIndex));
      }
      parts.push({ value: text.slice(matchIndex, matchIndex + normalizedSearch.length) });
      index = matchIndex + normalizedSearch.length;
      matchIndex = lower.indexOf(normalizedSearch, index);
    }

    if (index < text.length) {
      parts.push(text.slice(index));
    }

    return parts.map((part, idx) => {
      if (typeof part === 'string') {
        return <React.Fragment key={idx}>{part}</React.Fragment>;
      }
      return <mark key={idx}>{part.value}</mark>;
    });
  }, [normalizedSearch]);
  const openSearchDrawer = React.useCallback(() => {
    if (!isSearchDrawerOpen) {
      wasExpandedBeforeSearchRef.current = isExpanded;
    }
    setForceCollapsedAfterSearch(false);
    setIsSearchDrawerOpen(true);
    onExpandPanel?.();
  }, [isExpanded, isSearchDrawerOpen, onExpandPanel]);
  const handleCloseSearchDrawer = React.useCallback(() => {
    setIsSearchDrawerOpen(false);
    setIsSearchFocused(false);
    setShowSpatialFilters(false);
    setSearchQuery('');
    setSearchSuggestions([]);
    setUserResults({ items: [], hasMore: false, total: 0 });
    setTopicResults({ items: [], hasMore: false, total: 0 });
    setCommentResults({ items: [], hasMore: false, total: 0 });
    setGroupPages({ users: 1, topics: 1, comments: 1 });
    setSearchErrorMessage(null);
    setSearchLoading(false);
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
      searchAbortRef.current = null;
    }
    onSpatialSearchCenterClear?.();
    restoreSearchAfterTopicRef.current = false;
    if (!selectedTopic) {
      setForceCollapsedAfterSearch(true);
      onCollapsePanel?.();
    } else {
      setForceCollapsedAfterSearch(false);
      onExpandPanel?.();
    }
    wasExpandedBeforeSearchRef.current = false;
  }, [onCollapsePanel, onSpatialSearchCenterClear, onExpandPanel, selectedTopic, setSearchQuery]);
  const spatialSearchFilter = useMemo(() => {
    if (spatialSearchCenter && typeof spatialSearchRadiusKm === 'number') {
      const clampedRadius = Math.min(Math.max(spatialSearchRadiusKm, 0), 500);
      if (clampedRadius > 0) {
        return { lat: spatialSearchCenter.lat, lng: spatialSearchCenter.lng, radiusKm: clampedRadius };
      }
    }
    return null;
  }, [spatialSearchCenter, spatialSearchRadiusKm]);
  const spatialStatusText = useMemo(() => {
    if (spatialSearchPicking) return 'Click the map to choose a center';
    if (spatialSearchFilter) {
      return `Within ${spatialSearchFilter.radiusKm.toFixed(1)} km of ${spatialSearchFilter.lat.toFixed(3)}, ${spatialSearchFilter.lng.toFixed(3)}`;
    }
    return 'No location filter applied';
  }, [spatialSearchFilter, spatialSearchPicking]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const updateOrientation = () => setIsPortrait(getIsPortrait());
    updateOrientation();

    let mediaQuery: MediaQueryList | null = null;

    try {
      if (window.matchMedia) {
        mediaQuery = window.matchMedia('(orientation: portrait)');
        if (mediaQuery && 'addEventListener' in mediaQuery) {
          mediaQuery.addEventListener('change', updateOrientation);
        } else if (mediaQuery) {
          const legacyMediaQuery = mediaQuery as MediaQueryList & {
            addListener?: (listener: (this: MediaQueryList, ev: MediaQueryListEvent) => void) => void;
          };
          legacyMediaQuery.addListener?.(updateOrientation);
        }
      }
    } catch {
      // ignore matchMedia errors
    }

    window.addEventListener('resize', updateOrientation);

    return () => {
      if (mediaQuery) {
        if ('removeEventListener' in mediaQuery) {
          mediaQuery.removeEventListener('change', updateOrientation);
        } else {
          const legacyMediaQuery = mediaQuery as MediaQueryList & {
            removeListener?: (listener: (this: MediaQueryList, ev: MediaQueryListEvent) => void) => void;
          };
          legacyMediaQuery.removeListener?.(updateOrientation);
        }
      }
      window.removeEventListener('resize', updateOrientation);
    };
  }, []);

  useEffect(() => {
    setVisibleCommentCount(COMMENT_PAGE_SIZE);
  }, [selectedTopic?.id, commentSortBy]);

  useEffect(() => {
    if (!selectedTopic && restoreSearchAfterTopicRef.current) {
      restoreSearchAfterTopicRef.current = false;
      setForceCollapsedAfterSearch(false);
      openSearchDrawer();
    }
  }, [selectedTopic, openSearchDrawer]);


  useEffect(() => {
    if (spatialSearchPicking) {
      setShowSpatialFilters(true);
    }
  }, [spatialSearchPicking]);

  useEffect(() => {
    if (showSpatialFilters) {
      onExpandPanel?.();
    }
  }, [showSpatialFilters, onExpandPanel]);

  useEffect(() => {
    if (isSearchDrawerOpen) {
      setShowSpatialFilters(true);
    }
  }, [isSearchDrawerOpen]);

  useEffect(() => {
    const trimmed = searchQuery.trim();
    const hasSpatialOnly = !trimmed && Boolean(spatialSearchFilter);
    if (!trimmed && !hasSpatialOnly) {
      if (searchAbortRef.current) {
        searchAbortRef.current.abort();
        searchAbortRef.current = null;
      }
      setSearchLoading(false);
      setSearchErrorMessage(null);
      setUserResults({ items: [], hasMore: false, total: 0 });
      setTopicResults({ items: [], hasMore: false, total: 0 });
      setCommentResults({ items: [], hasMore: false, total: 0 });
      setSearchSuggestions([]);
      setGroupPages({ users: 1, topics: 1, comments: 1 });
      return;
    }

    const controller = new AbortController();
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }
    searchAbortRef.current = controller;
    setSearchLoading(true);
    setSearchErrorMessage(null);

    const timer = window.setTimeout(async () => {
      try {
        const globalResponse = await searchApi.searchAll(trimmed, {
          limit: SEARCH_PAGE_SIZE,
          ...(spatialSearchFilter ?? {})
        });

        if (controller.signal.aborted) return;

        const usersGroup = globalResponse.users ?? { items: [], hasMore: false, total: 0 };
        const topicsGroup = globalResponse.topics ?? { items: [], hasMore: false, total: 0 };
        const commentsGroup = globalResponse.comments ?? { items: [], hasMore: false, total: 0 };

        setUserResults({
          items: usersGroup.items ?? [],
          hasMore: Boolean(usersGroup.hasMore),
          total: usersGroup.total ?? usersGroup.items?.length ?? 0
        });
        setTopicResults({
          items: topicsGroup.items ?? [],
          hasMore: Boolean(topicsGroup.hasMore),
          total: topicsGroup.total ?? topicsGroup.items?.length ?? 0
        });
        setCommentResults({
          items: commentsGroup.items ?? [],
          hasMore: Boolean(commentsGroup.hasMore),
          total: commentsGroup.total ?? commentsGroup.items?.length ?? 0
        });

        setGroupPages({ users: 1, topics: 1, comments: 1 });

        const suggestions: SuggestionItem[] = [];
        const addSuggestion = (item: SuggestionItem | null) => {
          if (item) suggestions.push(item);
        };

        const topTopic = topicsGroup.items?.[0];
        const topicDescription = topTopic
          ? (typeof topTopic.distanceKm === 'number'
            ? `${topTopic.distanceKm.toFixed(1)} km away`
            : (topTopic.location || topTopic.excerpt))
          : null;
        addSuggestion(topTopic ? {
          id: `topic-${topTopic.id}`,
          group: 'topics',
          label: topTopic.title,
          description: topicDescription ?? undefined,
          payload: topTopic
        } : null);

        const topComment = commentsGroup.items?.[0];
        const commentDescription = topComment
          ? (typeof topComment.distanceKm === 'number'
            ? `${topComment.distanceKm.toFixed(1)} km away`
            : (topComment.location || (topComment.topic ? topComment.topic.title : 'Comment')))
          : null;
        addSuggestion(topComment ? {
          id: `comment-${topComment.id}`,
          group: 'comments',
          label: topComment.snippet,
          description: commentDescription ?? undefined,
          payload: topComment
        } : null);

        const topUser = usersGroup.items?.find(u => u.username);
        addSuggestion(topUser ? {
          id: `user-${topUser.id}`,
          group: 'users',
          label: `@${topUser.username}`,
          description: `${topUser.reputation} reputation`,
          payload: topUser
        } : null);

        setSearchSuggestions(suggestions);
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Global search failed:', error);
          setSearchErrorMessage('Search failed. Please try again.');
        }
      } finally {
        if (!controller.signal.aborted) {
          setSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [searchQuery, spatialSearchFilter]);

  useEffect(() => {
    return () => {
      if (suggestionHideTimeout.current) {
        window.clearTimeout(suggestionHideTimeout.current);
      }
    };
  }, []);

  const handleLoadMore = async (group: SearchGroup) => {
    const trimmed = searchQuery.trim();
    const hasSpatialOnly = !trimmed && Boolean(spatialSearchFilter);
    if (!trimmed && !hasSpatialOnly) return;
    if (loadingGroups[group]) return;

    setLoadingGroups(prev => ({ ...prev, [group]: true }));

    try {
      const nextPage = groupPages[group] + 1;
      const response = await searchApi.searchGroup(trimmed, group, {
        limit: SEARCH_PAGE_SIZE,
        page: nextPage,
        ...(spatialSearchFilter ?? {})
      });
      const data = response[group];
      if (!data) return;

      if (group === 'users') {
        const groupData = data as SearchGroupResult<SearchUserResult>;
        setUserResults(prev => ({
          items: [...prev.items, ...groupData.items.filter(item => !prev.items.some(existing => existing.id === item.id))],
          hasMore: groupData.hasMore,
          total: groupData.total ?? prev.total
        }));
      } else if (group === 'topics') {
        const groupData = data as SearchGroupResult<SearchTopicResult>;
        setTopicResults(prev => ({
          items: [...prev.items, ...groupData.items.filter(item => !prev.items.some(existing => existing.id === item.id))],
          hasMore: groupData.hasMore,
          total: groupData.total ?? prev.total
        }));
      } else if (group === 'comments') {
        const groupData = data as SearchGroupResult<SearchCommentResult>;
        setCommentResults(prev => ({
          items: [...prev.items, ...groupData.items.filter(item => !prev.items.some(existing => existing.id === item.id))],
          hasMore: groupData.hasMore,
          total: groupData.total ?? prev.total
        }));
      }

      setGroupPages(prev => ({ ...prev, [group]: nextPage }));
    } catch (error) {
      console.error('Failed to load more search results:', error);
      setSearchErrorMessage('Failed to load more results.');
    } finally {
      setLoadingGroups(prev => ({ ...prev, [group]: false }));
    }
  };
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [unreadDMs, setUnreadDMs] = useState(0);
  const [pendingReports, setPendingReports] = useState<number>(0);
  // Add state for user menu content views
  type UserMenuView = 'menu' | 'profile' | 'messages' | 'notifications';
  const [userMenuContent, setUserMenuContent] = useState<UserMenuView>('menu');
  const pendingUserMenuIntentRef = useRef<null | 'menu' | 'profile' | 'messages'>(null);

  // Debug wrapper for setUserMenuContent
  const [panelProfileUserId, setPanelProfileUserId] = useState<string | null>(null);
  const [userMenuEntry, setUserMenuEntry] = useState<'menu' | 'profile-panel' | 'profile-menu' | 'profile-external' | 'profile-from-messages' | 'messages' | 'messages-from-profile' | 'notifications'>('menu');
  const [profileInitialTab, setProfileInitialTab] = useState<'topics' | 'comments' | 'drafts' | 'notifications' | 'moderation' | 'mod-activities' | 'saved' | null>(null);
  const ignoreProfilePropRef = useRef(false);
  const effectiveProfileUserId = ignoreProfilePropRef.current ? null : profileUserId;
  const hasProfileTarget = Boolean(panelProfileUserId || effectiveProfileUserId);
  const resolvedProfileTab = hasProfileTarget ? (profileTab ?? profileInitialTab) : profileInitialTab;
  const [wasExpandedBeforeProfile, setWasExpandedBeforeProfile] = useState(false);
  const [topicBeforeProfile, setTopicBeforeProfile] = useState<Topic | null>(null);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showAccessRightsModal, setShowAccessRightsModal] = useState<string | null>(null);
  const sortToggleRef = useRef<HTMLButtonElement | null>(null);
  const sortMenuRef = useRef<HTMLDivElement | null>(null);
  const currentSortLabel = useMemo(() => {
    return COMMENT_SORT_OPTIONS.find((opt) => opt.value === commentSortBy)?.label || 'Oldest First';
  }, [commentSortBy]);
  const sortMenu = showSortMenu ? (
    <div
      ref={sortMenuRef}
      className="comment-sort-menu"
      role="menu"
      aria-label="Comment sort options"
    >
      {COMMENT_SORT_OPTIONS.map((option) => {
        const active = option.value === commentSortBy;
        return (
          <button
            key={option.value}
            type="button"
            className={`comment-sort-option ${active ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setCommentSortBy(option.value);
              setShowSortMenu(false);
            }}
            role="menuitemradio"
            aria-checked={active}
          >
            <span className="comment-sort-option-label">{option.label}</span>
            {option.helper && (
              <span className="comment-sort-option-helper">{option.helper}</span>
            )}
          </button>
        );
      })}
    </div>
  ) : null;
  // State for message target user
  const [messageTargetUserId, setMessageTargetUserId] = useState<string | null>(null);
  const profileReturnContextRef = useRef<{ mode: 'messages'; overlay: boolean; userId: string | null } | null>(null);
  const pendingMessageTargetRef = useRef<string | null>(null);
  const lastMessagingProfileUserIdRef = useRef<string | null>(null);
  const messagesReturnProfileIdRef = useRef<string | null>(null);
  const lastActiveMessagePeerIdRef = useRef<string | null>(null);
  const messagesEntryFromProfileRef = useRef<boolean>(false);
  const messagesOriginRef = useRef<'inbox' | 'profile'>('inbox');
  const lastProfileMessageTargetRef = useRef<string | null>(null);
  const lastProfileOriginStorageKey = 'messages:lastProfileOrigin';
  const rememberProfileOrigin = (userId: string) => {
    lastProfileMessageTargetRef.current = userId;
    try { sessionStorage.setItem(lastProfileOriginStorageKey, userId); } catch { }
  };
  const readProfileOrigin = (): string | null => {
    if (lastProfileMessageTargetRef.current) return lastProfileMessageTargetRef.current;
    try {
      const v = sessionStorage.getItem(lastProfileOriginStorageKey);
      return v || null;
    } catch {
      return null;
    }
  };
  const clearProfileOrigin = () => {
    lastProfileMessageTargetRef.current = null;
    try { sessionStorage.removeItem(lastProfileOriginStorageKey); } catch { }
  };
  const getLastMessagePeerFromStorage = () => {
    try {
      const raw = sessionStorage.getItem('messages:lastPeerId');
      return raw ? String(raw) : null;
    } catch {
      return null;
    }
  };
  // Global notifications badge count
  const { persistentUnreadCount, notify } = useNotifications();
  const [shouldReturnToSearch, setShouldReturnToSearch] = useState(false);
  const effectiveCurrentUserId = currentUserId ?? currentUser?.id ?? null;
  const [messagesInstanceKey, setMessagesInstanceKey] = useState(0);
  const clearProfileView = (options?: { collapse?: boolean; closeMenu?: boolean }) => {
    setPanelProfileUserId(null);
    onSetProfileUserId?.(null);
    setProfileInitialTab(null);
    setUserMenuEntry('menu');
    ignoreProfilePropRef.current = true;
    ignoreMenuPropRef.current = 'menu';
    setUserMenuContent('menu');
    onUserMenuViewChange?.('menu');
    try {
      sessionStorage.removeItem('returnToProfile');
    } catch { }
    if (options?.collapse !== false) {
      onCollapsePanel?.();
    }
    if (options?.closeMenu) {
      onShowUserMenu?.(false);
    }
  };

  useEffect(() => {
    if (!profileUserId) {
      ignoreProfilePropRef.current = false;
    }
  }, [profileUserId]);

  // Apply deferred user menu intent only when parent actually opened it
  useEffect(() => {
    if (!showUserMenu) return;
    const intent = pendingUserMenuIntentRef.current;
    if (!intent) return;
    pendingUserMenuIntentRef.current = null;
    if (intent === 'menu') {
      setUserMenuContent('menu');
      setPanelProfileUserId(null);
      onSetProfileUserId?.(null);
      setUserMenuEntry('menu');
      onCollapsePanel?.();
      return;
    }
    if (intent === 'profile') {
      setUserMenuContent('profile');
      setPanelProfileUserId(currentUser?.id || null);
      onSetProfileUserId?.(currentUser?.id || null);
      onExpandPanel?.();
      setUserMenuEntry('profile-panel');
      return;
    }
    if (intent === 'messages') {
      setUserMenuContent('messages');
      messagesEntryFromProfileRef.current = false;
      messagesOriginRef.current = 'inbox';
      onExpandPanel?.();
      setUserMenuEntry('messages');
      return;
    }
  }, [showUserMenu, currentUser?.id, onSetProfileUserId, onExpandPanel, onCollapsePanel]);

  const controlledMenuViewRef = useRef<typeof userMenuView>(null);
  const suppressMenuEmitRef = useRef(false);
  const ignoreMenuPropRef = useRef<UserMenuView | null>(null);

  useEffect(() => {
    if (!showUserMenu) {
      controlledMenuViewRef.current = null;
      suppressMenuEmitRef.current = false;
      return;
    }
    if (!userMenuView) {
      controlledMenuViewRef.current = null;
      suppressMenuEmitRef.current = false;
      return;
    }
    if (ignoreMenuPropRef.current) {
      if (userMenuView === ignoreMenuPropRef.current) {
        ignoreMenuPropRef.current = null;
      } else {
        return;
      }
    }

    if (controlledMenuViewRef.current !== userMenuView) {
      controlledMenuViewRef.current = userMenuView;
      suppressMenuEmitRef.current = true;
    } else if (userMenuContent === userMenuView) {
      return;
    }

    if (userMenuContent !== userMenuView) {
      setUserMenuContent(userMenuView);
    }

    if (userMenuView === 'profile') {
      if (!panelProfileUserId && !effectiveProfileUserId) {
        const fallbackUserId = currentUserId || currentUser?.id || null;
        setPanelProfileUserId(fallbackUserId);
        if (fallbackUserId) {
          onSetProfileUserId?.(fallbackUserId);
        }
      }
      const targetProfileId = panelProfileUserId ?? effectiveProfileUserId ?? null;
      const activeCurrentUserId = currentUserId ?? currentUser?.id ?? null;
      const isSelfProfile = Boolean(targetProfileId && targetProfileId === activeCurrentUserId);
      setUserMenuEntry((prev) => {
        if (prev === 'profile-from-messages') return prev;
        if (!targetProfileId) return 'profile-panel';
        return isSelfProfile ? 'profile-panel' : 'profile-external';
      });
      onExpandPanel?.();
    } else if (userMenuView === 'messages') {
      setUserMenuEntry((prev) => (prev === 'messages-from-profile' ? prev : 'messages'));
      onExpandPanel?.();
    } else if (userMenuView === 'notifications') {
      setUserMenuEntry('notifications');
      onExpandPanel?.();
    } else {
      setUserMenuEntry('menu');
      onCollapsePanel?.();
    }
  }, [
    userMenuView,
    userMenuContent,
    showUserMenu,
    panelProfileUserId,
    profileUserId,
    effectiveProfileUserId,
    currentUserId,
    currentUser?.id,
    onSetProfileUserId,
    onExpandPanel,
    onCollapsePanel
  ]);

  useEffect(() => {
    if (suppressMenuEmitRef.current) {
      if (userMenuContent === userMenuView) {
        suppressMenuEmitRef.current = false;
      }
      return;
    }
    if (!showUserMenu) return;
    if (userMenuView === userMenuContent) return;
    onUserMenuViewChange?.(userMenuContent);
  }, [showUserMenu, userMenuContent, userMenuView, onUserMenuViewChange]);

  useEffect(() => {
    if (!showSortMenu) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        sortMenuRef.current?.contains(target) ||
        sortToggleRef.current?.contains(target)
      ) {
        return;
      }
      setShowSortMenu(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showSortMenu]);

  useEffect(() => {
    if (!showSortMenu) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowSortMenu(false);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [showSortMenu]);

  const openProfilePanel = (
    userId: string,
    entry: typeof userMenuEntry = 'profile-external',
    opts?: { returnToSearch?: boolean; preserveReturnContext?: boolean }
  ) => {


    if (!opts?.preserveReturnContext) {
      profileReturnContextRef.current = null;
    }
    if (!currentUserId) {

      notify({ type: 'warning', message: 'Sign up or sign in to view user profiles.', persistent: false });
      return;
    }


    setShouldReturnToSearch(Boolean(opts?.returnToSearch));
    // Only save expanded state if not coming from messages
    if (entry !== 'profile-from-messages') {
      setWasExpandedBeforeProfile(isExpanded);
      setTopicBeforeProfile(selectedTopic || null);
    }
    setPanelProfileUserId(userId);
    setUserMenuContent('profile');
    setUserMenuEntry(entry);
    onSetProfileUserId?.(userId);
    onExpandPanel?.();
    // Use microtask to ensure state updates apply before showing menu
    setTimeout(() => {
      onShowUserMenu?.(true);
    }, 0);
  };

  const handleViewProfileFromMessages = (userId: string) => {
    if (!userId) return;

    setMessageTargetUserId(userId);
    const cameFromOverlay = messagesOpen && userMenuContent !== 'messages';
    profileReturnContextRef.current = { mode: 'messages', overlay: cameFromOverlay, userId };
    try {
      sessionStorage.setItem('returnToProfile', JSON.stringify({
        entry: 'profile-from-messages',
        messageUserId: userId,
        userId,
        overlay: cameFromOverlay
      }));
    } catch { }
    if (cameFromOverlay) {
      setMessagesOpen(false);
    }
    openProfilePanel(userId, 'profile-from-messages', { preserveReturnContext: true });
    if (!cameFromOverlay) {
      setUserMenuContent('profile');
    }
  };

  const navigateToTopic = async (topicId: string, commentId?: string): Promise<Topic | null> => {
    const existing = topics.find(t => t.id === topicId);
    if (existing) {
      onTopicSelect(existing, commentId);
      if (commentId) {
        onCommentSelectById?.(commentId);
      }
      return existing;
    }

    try {
      setLoadingGroups(prev => ({ ...prev, topics: true }));
      const topic = await topicsApi.getTopic(topicId);
      onTopicSelect(topic as Topic, commentId);
      if (commentId) {
        onCommentSelectById?.(commentId);
      }
      return topic as Topic;
    } catch (error) {
      console.error('Failed to load topic from search:', error);
      setSearchErrorMessage('Unable to open the selected topic.');
      return null;
    } finally {
      setLoadingGroups(prev => ({ ...prev, topics: false }));
    }
  };

  const handleUserResultSelect = (user: SearchUserResult) => {
    openProfilePanel(user.id, 'profile-external', { returnToSearch: true });
    setIsSearchFocused(false);
  };

  const handleTopicResultSelect = async (topic: SearchTopicResult) => {
    restoreSearchAfterTopicRef.current = true;
    await navigateToTopic(topic.id);
    setIsSearchFocused(false);
    setIsSearchDrawerOpen(false);
    setShowSpatialFilters(false);
    setForceCollapsedAfterSearch(false);
  };

  const handleBackToTopics = React.useCallback(() => {
    // Do not clear search state here so we can restore it if needed
    onBackToTopics();
  }, [onBackToTopics]);

  const handleLogoClick = React.useCallback(() => {
    setIsSearchDrawerOpen(false);
    setIsSearchFocused(false);
    setShowSpatialFilters(false);
    setForceCollapsedAfterSearch(false);
    setUserMenuContent('menu');
    setUserMenuEntry('menu');
    setPanelProfileUserId(null);
    setProfileInitialTab(null);
    onSetProfileUserId?.(null);
    onUserMenuViewChange?.('menu');
    if (typeof onShowUserMenu === 'function') {
      onShowUserMenu(false);
    }
    handleBackToTopics();
  }, [handleBackToTopics, onShowUserMenu, onSetProfileUserId, onUserMenuViewChange]);

  const handleCommentResultSelect = async (comment: SearchCommentResult) => {
    if (comment.topic?.id) {
      restoreSearchAfterTopicRef.current = true;
      await navigateToTopic(comment.topic.id, comment.id);
      setIsSearchFocused(false);
      setIsSearchDrawerOpen(false);
      setShowSpatialFilters(false);
      setForceCollapsedAfterSearch(false);
    } else {
      setSearchErrorMessage('Comment is missing topic data.');
    }
  };

  const handleSuggestionSelect = (suggestion: SuggestionItem) => {
    switch (suggestion.group) {
      case 'users':
        handleUserResultSelect(suggestion.payload as SearchUserResult);
        break;
      case 'topics':
        handleTopicResultSelect(suggestion.payload as SearchTopicResult);
        break;
      case 'comments':
        handleCommentResultSelect(suggestion.payload as SearchCommentResult);
        break;
      default:
        break;
    }
  };

  const handleSpatialRadiusChange = (value: number) => {
    if (!onSpatialSearchRadiusChange) return;
    const cleaned = Number.isFinite(value) ? value : spatialSearchRadiusKm;
    const clamped = Math.min(Math.max(cleaned ?? 0, 0.1), 500);
    onSpatialSearchRadiusChange(clamped);
  };

  const showSpatialPanel = showSpatialFilters || spatialSearchPicking;
  const hasQuery = Boolean(searchQuery.trim());
  const hasSpatialFilter = Boolean(spatialSearchFilter);
  const hasActiveSearchFilter = hasQuery || hasSpatialFilter;
  useEffect(() => {
    if (hasActiveSearchFilter && !selectedTopic) {
      openSearchDrawer();
    }
  }, [hasActiveSearchFilter, openSearchDrawer, selectedTopic]);

  const formatDistance = (distance?: number | null) => {
    if (!Number.isFinite(distance ?? NaN)) return null;
    const d = distance as number;
    if (d >= 100) return `${d.toFixed(0)} km away`;
    if (d >= 10) return `${d.toFixed(1)} km away`;
    return `${d.toFixed(2)} km away`;
  };

  const shouldShowSearchDrawer = isSearchDrawerOpen || isSearchFocused || showSpatialPanel;
  // Keep the body in search mode whenever the drawer is open/focused so topics/tabs stay hidden
  const isSearchMode = shouldShowSearchDrawer;
  const hasSearchResults =
    userResults.items.length + topicResults.items.length + commentResults.items.length > 0;
  const isPanelExpanded = isCreating || (isExpanded && !forceCollapsedAfterSearch) || isSearchMode;

  useEffect(() => {
    if (!selectedTopic && !isSearchDrawerOpen && !showSpatialPanel && !showUserMenu) {
      onCollapsePanel?.();
    }
  }, [selectedTopic, isSearchDrawerOpen, showSpatialPanel, showUserMenu, onCollapsePanel]);

  useEffect(() => {
    if (selectedTopic) {
      setForceCollapsedAfterSearch(false);
    }
  }, [selectedTopic]);

  useEffect(() => {
    if (isCreating) {
      setForceCollapsedAfterSearch(false);
      onExpandPanel?.();
    }
  }, [isCreating, onExpandPanel]);

  // Helper functions to find drawings
  const getTopicDrawing = (topicId: string): MapDrawing | undefined => {
    return drawings.find(d => d.topicId === topicId && !d.commentId);
  };

  const getCommentDrawing = (commentId: string): MapDrawing | undefined => {
    return drawings.find(d => d.commentId === commentId);
  };

  // When user menu opens, decide initial view:
  // - If a target profile is specified (either local or from parent), show Profile directly
  // - Otherwise, default to the main menu
  useEffect(() => {
    if (!showUserMenu) return;

    if (ignoreProfilePropRef.current) {
      if (!panelProfileUserId && !effectiveProfileUserId && !resolvedProfileTab) {
        ignoreProfilePropRef.current = false;
      } else {
        return;
      }
    }

    const shouldShowProfile = Boolean(panelProfileUserId || effectiveProfileUserId || resolvedProfileTab);
    const targetProfileId = panelProfileUserId ?? effectiveProfileUserId ?? null;
    const isSelfProfile = Boolean(
      targetProfileId && effectiveCurrentUserId && targetProfileId === effectiveCurrentUserId
    );

    if (shouldShowProfile) {
      if (userMenuContent !== 'profile' && userMenuContent !== 'messages') {
        setUserMenuContent('profile');
      }
      // Don't overwrite the entry while the user is in the Messages view; otherwise
      // "Message from profile" loses its intent marker.
      if (userMenuContent === 'messages') {
        return;
      }
      if (panelProfileUserId || resolvedProfileTab) {
        setUserMenuEntry((prev) => {
          if (isSelfProfile) {
            return 'profile-panel';
          }
          if (panelProfileUserId) {
            return prev === 'profile-external' || prev === 'profile-panel' || prev === 'profile-menu' || prev === 'profile-from-messages'
              ? prev
              : 'profile-panel';
          }
          return 'profile-panel';
        });
      } else if (effectiveProfileUserId) {
        setUserMenuEntry(isSelfProfile ? 'profile-panel' : 'profile-external');
      }
      return;
    }

    if (userMenuContent === 'profile') {
      setUserMenuContent('menu');
      setUserMenuEntry('menu');
    }
  }, [showUserMenu, panelProfileUserId, profileUserId, resolvedProfileTab, hasProfileTarget, userMenuContent, effectiveCurrentUserId, effectiveProfileUserId]);

  // Clear local target when menu closes to avoid stale profiles
  useEffect(() => {
    if (!showUserMenu) {
      setPanelProfileUserId(null);
      setProfileInitialTab(null);
      ignoreProfilePropRef.current = false;
    }
  }, [showUserMenu]);

  // Clear initial tab hint when leaving profile view
  useEffect(() => {
    if (userMenuContent !== 'profile' && profileInitialTab) {
      setProfileInitialTab(null);
    }
  }, [userMenuContent, profileInitialTab]);

  useEffect(() => {
    if (profileTab) {
      setProfileInitialTab(null);
    }
  }, [profileTab]);

  useEffect(() => {
    if (!selectedTopic) return;
    if (selectedTopic.visibility !== 'PUBLIC' && activeTab !== 'private') {
      setActiveTab('private');
    }
  }, [selectedTopic, activeTab]);

  useEffect(() => {
    if (!selectedTopic) return;
    if (selectedTopic.visibility === 'PUBLIC' && activeTab === 'private') {
      setActiveTab('popular');
    }
  }, [selectedTopic, activeTab]);

  // Clear message target user only when we actually leave the messages view.
  // (Prevents accidental clearing during same-tick transitions like profile -> messages.)
  const prevUserMenuContentRef = useRef<UserMenuView>(userMenuContent);
  useEffect(() => {
    const prev = prevUserMenuContentRef.current;
    prevUserMenuContentRef.current = userMenuContent;
    if (prev === 'messages' && userMenuContent !== 'messages') {
      setMessageTargetUserId(null);
      messagesReturnProfileIdRef.current = null;
      messagesEntryFromProfileRef.current = false;
      messagesOriginRef.current = 'inbox';
      clearProfileOrigin();
    }
    if (prev !== 'messages' && userMenuContent === 'messages') {
      // entering messages from a non-profile path should default to inbox origin
      if (!messagesEntryFromProfileRef.current) {
        messagesOriginRef.current = 'inbox';
      }
      // if we intentionally came from a profile, mark the origin
      if (userMenuEntry === 'messages-from-profile' || messagesEntryFromProfileRef.current) {
        messagesOriginRef.current = 'profile';
      }
    }
  }, [userMenuContent, userMenuEntry]);

  // If we navigated to Messages from a user's profile, ensure we keep the target user id.
  // This makes "Message" from profile jump directly into that chat, even if some other state
  // update temporarily cleared the target.
  useEffect(() => {
    if (userMenuContent !== 'messages') return;
    const candidate = pendingMessageTargetRef.current || null;
    if (!candidate) return;
    const me = currentUserId ?? currentUser?.id ?? null;
    if (candidate === me) {
      pendingMessageTargetRef.current = null;
      return;
    }
    if (messageTargetUserId !== candidate) {
      setMessageTargetUserId(candidate);
      return;
    }
    pendingMessageTargetRef.current = null;
    if (messagesEntryFromProfileRef.current) {
      messagesOriginRef.current = 'profile';
      rememberProfileOrigin(candidate);
    }
  }, [userMenuContent, messageTargetUserId, currentUserId, currentUser?.id]);

  useEffect(() => {
    if (userMenuContent !== 'messages') return;
    const me = currentUserId ?? currentUser?.id ?? null;
    if (messageTargetUserId && messageTargetUserId !== me) {
      messagesReturnProfileIdRef.current = messageTargetUserId;
      if (messagesOriginRef.current === 'profile' || messagesEntryFromProfileRef.current) {
        rememberProfileOrigin(messageTargetUserId);
      }
    }
  }, [userMenuContent, userMenuEntry, messageTargetUserId, currentUserId, currentUser?.id]);

  // Keep local panel profile user id in sync with parent-provided prop when available
  useEffect(() => {
    // Only adopt parent-provided user id when user menu is visible and we are not suppressing it
    if (!showUserMenu) return;
    if (ignoreProfilePropRef.current) return;
    setPanelProfileUserId(profileUserId ?? null);
  }, [profileUserId, showUserMenu]);
  // Poll unread DM count when logged in
  useEffect(() => {
    let timer: any;
    const fetchCount = async () => {
      try {
        if (!currentUserId) { setUnreadDMs(0); return; }
        const res = await messagesApi.unreadCount();
        setUnreadDMs(res.count || 0);
      } catch { }
    };
    fetchCount();
    if (currentUserId) {
      timer = setInterval(fetchCount, 30000);
    }
    return () => { if (timer) clearInterval(timer); };
  }, [currentUserId]);

  // Listen for real-time unread message count updates
  useEffect(() => {
    if (!currentUserId) return;

    const handleUnreadUpdate = (event: CustomEvent) => {
      const { count } = event.detail;
      setUnreadDMs(count);
    };

    window.addEventListener('unread_messages_update', handleUnreadUpdate as EventListener);
    return () => {
      window.removeEventListener('unread_messages_update', handleUnreadUpdate as EventListener);
    };
  }, [currentUserId]);

  // Poll pending reports for moderators
  useEffect(() => {
    if (!currentUser?.role || currentUser.role !== 'MODERATOR') {
      setPendingReports(0);
      return;
    }
    let timer: any;
    const fetchReports = async () => {
      try {
        const r = await adminApi.getReports();
        setPendingReports(Array.isArray(r.data) ? r.data.length : 0);
      } catch { }
    };
    fetchReports();
    timer = setInterval(fetchReports, 30000);
    return () => timer && clearInterval(timer);
  }, [currentUser?.role]);

  // Edit mode state
  const [editingTopicId, setEditingTopicId] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState<string>('');
  const [savingEdit, setSavingEdit] = useState(false);
  const savingEditRef = useRef(false);
  // Multi-select helpers
  const drawingsSet = new Set(commentsWithDrawings || []);
  const multiCount = multiSelectedCommentIds?.length || 0;

  const [topicVisibility, setTopicVisibility] = useState<TopicVisibility>('PUBLIC');
  const [customAudience, setCustomAudience] = useState<Array<{ id: string; username?: string | null; email?: string }>>([]);
  const [followerOptions, setFollowerOptions] = useState<Array<{ id: string; username?: string | null; email?: string }>>([]);
  const [audienceSearchTerm, setAudienceSearchTerm] = useState('');
  const [audienceSearchResults, setAudienceSearchResults] = useState<SearchUserResult[]>([]);
  const [isAudienceSearching, setIsAudienceSearching] = useState(false);
  const [editingTopicVisibility, setEditingTopicVisibility] = useState<TopicVisibility | null>(null);
  const [editingTopicOriginalVisibility, setEditingTopicOriginalVisibility] = useState<TopicVisibility | null>(null);
  const [editingCustomAudience, setEditingCustomAudience] = useState<Array<{ id: string; username: string | null; email?: string }>>([]);
  const [editingAudienceSearchTerm, setEditingAudienceSearchTerm] = useState('');
  const [editingAudienceSearchResults, setEditingAudienceSearchResults] = useState<SearchUserResult[]>([]);
  const [isEditingAudienceSearching, setIsEditingAudienceSearching] = useState(false);
  const [editingAudienceLoading, setEditingAudienceLoading] = useState(false);
  const followerIdSet = useMemo(() => new Set(followerOptions.map(f => f.id)), [followerOptions]);
  const customAudienceIdSet = useMemo(() => new Set(customAudience.map(u => u.id)), [customAudience]);
  const editingCustomAudienceIdSet = useMemo(() => new Set(editingCustomAudience.map(u => u.id)), [editingCustomAudience]);

  const [isEditorial, setIsEditorial] = useState(false);
  const [editorialImport, setEditorialImport] = useState<EditorialImportResult | null>(null);
  const [editorialImportUserModified, setEditorialImportUserModified] = useState(false); // Track if user actively changed editorial data
  // Track loaded existing editorial info for UI display without triggering preview regeneration
  const [editorialLoadedInfo, setEditorialLoadedInfo] = useState<{ pointCount: number; lineCount: number; polygonCount: number; warning: string } | null>(null);
  const [editorialStyle, setEditorialStyle] = useState<EditorialStyleConfig>(DEFAULT_EDITORIAL_STYLE);
  const [editorialError, setEditorialError] = useState<string | null>(null);
  const [editorialLoading, setEditorialLoading] = useState(false);
  const editorialPropertyKeys = useMemo(() => {
    if (!editorialImport) return { numericKeys: [], categoricalKeys: [] };
    return summarizeEditorialProperties(editorialImport.features || []);
  }, [editorialImport]);
  const editorialDrawings = useMemo(() => {
    if (!editorialImport) return [];
    return buildEditorialDrawings(editorialImport.features || [], editorialStyle);
  }, [editorialImport, editorialStyle]);

  // Track if we've already loaded editorial style for this edit session
  const editorialStyleLoadedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!editingTopicId || !selectedTopic?.isEditorial) {
      editorialStyleLoadedRef.current = null;
      return;
    }
    // Skip if we've already loaded for this topic
    if (editorialStyleLoadedRef.current === selectedTopic.id) return;
    if (editorialImport) return; // User uploaded new data

    const sourceDrawings = (allDrawings ?? drawings).filter(
      (drawing) => drawing.topicId === selectedTopic.id && !drawing.commentId
    );
    if (!sourceDrawings.length) return;

    // Mark as loaded for this topic
    editorialStyleLoadedRef.current = selectedTopic.id;

    // Count drawing types for UI display
    const pointCount = sourceDrawings.filter(d => d.type === 'POINT').length;
    const lineCount = sourceDrawings.filter(d => d.type === 'LINE').length;
    const polygonCount = sourceDrawings.filter(d => d.type === 'POLYGON').length;
    const inferredStyle = inferEditorialStyleFromDrawings(sourceDrawings);
    setEditorialStyle(inferredStyle);
    const derivedFeatures = buildEditorialFeaturesFromDrawings(sourceDrawings);
    if (derivedFeatures.length > 0) {
      const hasProperties = derivedFeatures.some((feature) => feature.properties && Object.keys(feature.properties).length > 0);
      setEditorialImport({
        source: 'existing',
        features: derivedFeatures,
        warnings: hasProperties
          ? []
          : ['Loaded existing curated drawings, but no attribute data was found. Upload the original file to enable categorized/choropleth styling.']
      });
      setEditorialImportUserModified(false);
      setEditorialLoadedInfo(null);
    } else {
      setEditorialImport(null);
      setEditorialLoadedInfo({
        pointCount,
        lineCount,
        polygonCount,
        warning: 'Loaded existing curated drawings. Upload original data to use categorized/choropleth styling.'
      });
    }
  }, [editingTopicId, selectedTopic?.id, selectedTopic?.isEditorial, editorialImport, allDrawings, drawings]);

  // Shape editing helpers
  const toggleMultiFor = (id: string) => {
    if (!onMultiSelectedChange) return;
    const has = multiSelectedCommentIds.includes(id);
    if (has) {
      onMultiSelectedChange(multiSelectedCommentIds.filter(cid => cid !== id));
    } else {
      if (multiSelectedCommentIds.length >= 10) return; // cap
      onMultiSelectedChange([...multiSelectedCommentIds, id]);
    }
  };

  // Helper functions for edit/delete
  const canEdit = (userId: string) => currentUserId && currentUserId === userId;
  const isModerator = currentUser?.role === 'MODERATOR';

  const startEditTopic = (topic: Topic) => {
    // Prevent editing if already editing something else
    if (editingCommentId || editingTopicId) {
      return;
    }

    setEditingTopicId(topic.id);
    setEditContent(topic.content);
    setEditingTopicOriginalVisibility(topic.visibility);
    setEditingTopicVisibility(topic.visibility);
    setEditingAudienceSearchTerm('');
    setEditingAudienceSearchResults([]);
    setIsEditingAudienceSearching(false);
    if (topic.basemap && onBasemapChange && selectedBasemap !== topic.basemap) {
      onBasemapChange(topic.basemap as BasemapId);
    }

    const existingAudience = topic.audiences;
    if (topic.visibility === 'CUSTOM') {
      type AudienceSummary = { id: string; username: string | null; email?: string };
      const mapAudience = (audiences?: TopicAudienceMember[]) => {
        if (!audiences) {
          setEditingCustomAudience([]);
          return;
        }
        const members = audiences
          .map<AudienceSummary | null>((entry) => {
            const id = entry.user?.id || entry.userId;
            if (!id || id === currentUserId) return null;
            return {
              id,
              username: entry.user?.username ?? null,
              email: entry.user?.email || undefined
            };
          })
          .filter((u): u is AudienceSummary => u !== null);
        setEditingCustomAudience(members);
      };

      if (existingAudience && existingAudience.length) {
        mapAudience(existingAudience);
        setEditingAudienceLoading(false);
      } else {
        setEditingAudienceLoading(true);
        topicsApi.getTopic(topic.id)
          .then((full) => {
            mapAudience(full.audiences);
            setEditingTopicVisibility(full.visibility);
            setEditingTopicOriginalVisibility(full.visibility);
          })
          .catch((err) => {
            console.error('Failed to load topic audience for editing:', err);
            setEditingCustomAudience([]);
          })
          .finally(() => {
            setEditingAudienceLoading(false);
          });
      }
    } else {
      setEditingCustomAudience([]);
      setEditingAudienceLoading(false);
    }

    // Also start editing the shape if it exists
    const topicDrawing = getTopicDrawing(topic.id);
    if (topicDrawing && onStartEditDrawing) {
      onStartEditDrawing(topicDrawing);
    }
  };

  const startEditComment = (comment: Comment) => {
    // Ensure compose/new-comment UI is closed when entering edit mode
    if (onCancelComment) {
      try { onCancelComment(); } catch { }
    }
    // Prevent editing if already editing something else
    if (editingCommentId || editingTopicId) {
      return;
    }

    setEditingCommentId(comment.id);
    setEditContent(comment.content);

    // Also start editing the shape if it exists
    const commentDrawing = getCommentDrawing(comment.id);
    if (commentDrawing && onStartEditDrawing) {
      onStartEditDrawing(commentDrawing);
    }
  };

  const cancelEdit = React.useCallback(() => {
    setEditingTopicId(null);
    setEditingCommentId(null);
    setEditContent('');
    setEditingTopicVisibility(null);
    setEditingTopicOriginalVisibility(null);
    setEditingCustomAudience([]);
    setEditingAudienceSearchTerm('');
    setEditingAudienceSearchResults([]);
    setIsEditingAudienceSearching(false);
    setEditingAudienceLoading(false);
    // Reset editorial state when canceling edit
    setEditorialImport(null);
    setEditorialImportUserModified(false);
    setEditorialLoadedInfo(null);
    setEditorialStyle(DEFAULT_EDITORIAL_STYLE);

    // Also cancel shape editing
    if (onCancelEditShape) {
      onCancelEditShape();
    }
  }, [onCancelEditShape]);

  const saveEdit = React.useCallback(async () => {
    if (savingEditRef.current) return;
    const trimmedContent = editContent.trim();

    // Check if there's anything to save
    const hasContentToSave = (editingTopicId || editingCommentId) && trimmedContent;
    const hasShapeToSave = isEditingShape && onSaveEditShape;
    // Only save editorial changes if user actively modified them (uploaded new data or changed styling)
    const hasEditorialToSave = Boolean(
      editingTopicId && selectedTopic?.isEditorial && editorialImport && editorialImportUserModified && onApplyEditorialChanges
    );

    if (!hasContentToSave && !hasShapeToSave && !hasEditorialToSave) {
      return; // Nothing to save
    }

    // Save content/visibility changes for topics
    let updatePayload: { content?: string; visibility?: TopicVisibility; audienceUserIds?: string[] } | null = null;
    if (editingTopicId && onEditTopic) {
      updatePayload = {};
      const currentVisibility = selectedTopic && selectedTopic.id === editingTopicId
        ? selectedTopic.visibility
        : editingTopicOriginalVisibility ?? null;
      if (trimmedContent && (!selectedTopic || selectedTopic.content !== trimmedContent)) {
        updatePayload.content = trimmedContent;
      }

      const targetVisibility = editingTopicVisibility ?? currentVisibility ?? 'PUBLIC';
      if (currentVisibility && targetVisibility !== currentVisibility) {
        updatePayload.visibility = targetVisibility;
      }

      if (targetVisibility === 'CUSTOM') {
        if (editingCustomAudience.length === 0) {
          notify({ type: 'warning', message: 'Select at least one person to share with before saving.', persistent: false });
          return;
        }
        updatePayload.audienceUserIds = editingCustomAudience.map(user => user.id);
      }
    }

    savingEditRef.current = true;
    setSavingEdit(true);

    try {
      if (editingTopicId && onEditTopic && updatePayload && Object.keys(updatePayload).length > 0) {
        try {
          await Promise.resolve(onEditTopic(editingTopicId, updatePayload));
        } catch (err) {
          console.error('Failed to update topic during save:', err);
        }
      } else if (editingCommentId && onEditComment && trimmedContent) {
        await Promise.resolve(onEditComment(editingCommentId, trimmedContent));
      }

      // Save shape changes if shape editing is active
      let shapeSaved = true;
      if (isEditingShape && onSaveEditShape) {
        try {
          const res = onSaveEditShape();
          shapeSaved = typeof res === 'boolean' ? res : await res;
        } catch (e) {
          shapeSaved = false;
        }
      }

      let editorialSaved = true;
      if (editingTopicId && selectedTopic?.isEditorial && editorialImport && editorialImportUserModified && onApplyEditorialChanges) {
        if (editorialDrawings.length === 0) {
          notify({ type: 'warning', message: 'Curated import has no drawings to apply.', persistent: false });
          editorialSaved = false;
        } else {
          try {
            const res = onApplyEditorialChanges(editingTopicId, editorialDrawings);
            editorialSaved = typeof res === 'boolean' ? res : await res;
          } catch (e) {
            editorialSaved = false;
          }
        }
      }

      if (shapeSaved && editorialSaved) {
        cancelEdit();
      }
    } finally {
      savingEditRef.current = false;
      setSavingEdit(false);
    }
  }, [
    editContent,
    editingTopicId,
    editingCommentId,
    isEditingShape,
    onEditTopic,
    onEditComment,
    onSaveEditShape,
    cancelEdit,
    editingTopicVisibility,
    editingCustomAudience,
    selectedTopic,
    notify,
    editingTopicOriginalVisibility,
    editorialImport,
    editorialImportUserModified,
    editorialDrawings,
    onApplyEditorialChanges
  ]);

  // Listen for map save button trigger
  useEffect(() => {
    const handleUnifiedSave = () => {
      if (editingTopicId || editingCommentId || isEditingShape) {
        saveEdit();
      } else {
      }
    };

    window.addEventListener('trigger-unified-save', handleUnifiedSave as EventListener);

    return () => {
      window.removeEventListener('trigger-unified-save', handleUnifiedSave as EventListener);
    };
  }, [editingTopicId, editingCommentId, isEditingShape, saveEdit]);

  const handleDelete = (type: 'topic' | 'comment', id: string) => {
    const confirmMessage = type === 'topic'
      ? `Are you sure you want to delete this topic?\n\nThis will mark your topic as [deleted].\n- Your username will be hidden as [deleted]\n- The topic entry remains visible\n- Existing comments remain visible\n\nModerators can still remove it completely.`
      : `Are you sure you want to delete this comment?\n\nThis will permanently remove your comment (and any direct replies).`;
    if (window.confirm(confirmMessage)) {
      if (type === 'topic' && onDeleteTopic) {
        onDeleteTopic(id);
      } else if (type === 'comment' && onDeleteComment) {
        onDeleteComment(id);
      }
    }
  };

  const handleModeratorDelete = (type: 'topic' | 'comment', id: string) => {
    const confirmMessage = `[MODERATOR ACTION] Are you sure you want to delete this ${type}? 

This will:
• Remove the content immediately (hard delete)
• Generate a moderation record visible to the user
• This action cannot be undone

Type "DELETE" to confirm:`;

    const userInput = window.prompt(confirmMessage);
    if (userInput === 'DELETE') {
      // Ask for moderation reason
      const reason = window.prompt('Please provide a reason for this moderation action (optional):') || undefined;

      if (type === 'topic' && onModeratorDeleteTopic) {
        onModeratorDeleteTopic(id, reason);
      } else if (type === 'comment' && onModeratorDeleteComment) {
        onModeratorDeleteComment(id, reason);
      }
    }
  };

  // Ensure currently selected topic stays visible even if backend omits soft-deleted ones
  const topicsWithSelected = useMemo(() => {
    if (!selectedTopic) return topics;
    const exists = topics.some(t => t.id === selectedTopic.id);
    // Only inject if the selected topic is soft-deleted by user or otherwise missing
    if (!exists && selectedTopic.isDeleted && selectedTopic.deletedBy === 'USER') {
      return [selectedTopic, ...topics];
    }
    return topics;
  }, [topics, selectedTopic]);

  // Topics are already server-filtered by App when searchQuery is set
  const filteredTopics = topicsWithSelected;

  const topicsForActiveTab = useMemo(() => {
    const isPrivateTopic = (topic: Topic) => topic.visibility !== 'PUBLIC';

    const matchesActiveTab = (topic: Topic): boolean => {
      if (activeTab === 'private') {
        return isPrivateTopic(topic);
      }
      return !isPrivateTopic(topic);
    };

    return filteredTopics.filter((topic) => matchesActiveTab(topic));
  }, [filteredTopics, activeTab]);

  // When searching, proactively resolve reverse geocodes for visible topics so labels become searchable
  React.useEffect(() => {
    if (!searchQuery.trim()) return;
    const controller = new AbortController();
    const work = async () => {
      const toResolve = topics.slice(0, 30); // cap to avoid flooding
      for (const t of toResolve) {
        if (t.city) continue;
        if (typeof t.latitude !== 'number' || typeof t.longitude !== 'number') continue;
        if (getCachedLabel(t.latitude, t.longitude)) continue;
        try {
          await reverseGeocode(t.latitude, t.longitude, controller.signal);
        } catch { }
      }
    };
    work();
    return () => controller.abort();
  }, [searchQuery, topics]);

  const sortedTopics = [...topicsForActiveTab]
    // Filter out hard-deleted topics (moderator deleted with no soft deletion flag)
    .filter(topic => {
      // Show topic if it's not deleted at all
      if (!topic.isDeleted) return true;
      // Show topic if it's soft deleted (user deletion or moderator with soft delete flag)
      if (topic.deletedBy === 'USER') return true;
      // Hide topic if it's hard deleted by moderator (no soft delete preservation)
      return false;
    })
    .sort((a, b) => {
      if (activeTab === 'popular') {
        return (b.upvotes - b.downvotes) - (a.upvotes - a.downvotes);
      }
      if (activeTab === 'live') {
        // Recently active first, matching the server's 'active' sort
        const activityA = new Date(a.updatedAt || a.createdAt).getTime();
        const activityB = new Date(b.updatedAt || b.createdAt).getTime();
        if (activityB !== activityA) return activityB - activityA;
      }
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  const searchDrawings = useMemo(() => {
    if (allDrawings && allDrawings.length) return allDrawings;
    return drawings || [];
  }, [allDrawings, drawings]);

  const searchTopicsById = useMemo(() => {
    const map = new Map<string, Topic>();
    topics.forEach(topic => map.set(topic.id, topic));
    return map;
  }, [topics]);

  const searchTopicDrawings = useMemo(() => {
    const map = new Map<string, MapDrawing>();
    searchDrawings.forEach(drawing => {
      if (drawing.commentId) return;
      if (!map.has(drawing.topicId)) map.set(drawing.topicId, drawing);
    });
    return map;
  }, [searchDrawings]);

  const searchCommentDrawings = useMemo(() => {
    const map = new Map<string, MapDrawing>();
    searchDrawings.forEach(drawing => {
      if (!drawing.commentId) return;
      if (!map.has(drawing.commentId)) map.set(drawing.commentId, drawing);
    });
    return map;
  }, [searchDrawings]);

  // Filter comments for selected topic - exclude hard deleted topics from comment view
  const topicComments = useMemo(() => {
    if (!selectedTopic) return [] as Comment[];
    const scoped = comments.filter(comment => comment.topicId === selectedTopic.id);
    return [...scoped].sort((a, b) => {
      if (commentSortBy === 'popularity') {
        const scoreA = a.upvotes - a.downvotes;
        const scoreB = b.upvotes - b.downvotes;
        if (scoreB !== scoreA) {
          return scoreB - scoreA;
        }
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      if (commentSortBy === 'newest') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  }, [comments, commentSortBy, selectedTopic]);

  // Threading: one-level replies - filter out hard deleted comments
  const topLevelComments = useMemo(() =>
    topicComments
      .filter(c => !c.parentId)
      .filter(c => {
        // Hide all deleted comments (both user and moderator deletions)
        return !c.isDeleted;
      }),
    [topicComments]
  );
  const visibleTopLevelComments = useMemo(
    () => topLevelComments.slice(0, visibleCommentCount),
    [topLevelComments, visibleCommentCount]
  );
  const hasMoreTopLevelComments = visibleCommentCount < topLevelComments.length;
  const repliesMap = useMemo(() => {
    const map = new Map<string, Comment[]>();
    topicComments.forEach(c => {
      if (c.parentId) {
        // Hide all deleted replies (both user and moderator deletions)
        if (!c.isDeleted) {
          const arr = map.get(c.parentId) || [];
          arr.push(c);
          map.set(c.parentId, arr);
        }
      }
    });
    // Sort replies oldest-first for readability
    map.forEach((arr) => {
      arr.sort((a: Comment, b: Comment) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    });
    return map;
  }, [topicComments]);

  useEffect(() => {
    if (!selectedCommentId || !topLevelComments.length) return;
    const indexOfTopLevel = (id: string) => topLevelComments.findIndex(comment => comment.id === id);
    let targetIndex = indexOfTopLevel(selectedCommentId);
    if (targetIndex < 0) {
      const selected = topicComments.find(comment => comment.id === selectedCommentId);
      const parentId = selected?.parentId || null;
      if (parentId) {
        targetIndex = indexOfTopLevel(parentId);
      }
    }
    if (targetIndex >= 0 && targetIndex + 1 > visibleCommentCount) {
      setVisibleCommentCount(targetIndex + 1);
    }
  }, [selectedCommentId, topLevelComments, topicComments, visibleCommentCount]);

  // Scroll the selected comment into view when it changes (including repeated selections)
  useEffect(() => {
    if (!selectedCommentId) return;
    const timer = window.setTimeout(() => {
      const element = document.querySelector<HTMLElement>(`[data-comment-id="${selectedCommentId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('comment-focus');
        window.setTimeout(() => element.classList.remove('comment-focus'), 1200);
      } else {
        // If element not found, try again after a longer delay
        // This handles cases where comments are still loading
        const retryTimer = window.setTimeout(() => {
          const retryElement = document.querySelector<HTMLElement>(`[data-comment-id="${selectedCommentId}"]`);
          if (retryElement) {
            retryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            retryElement.classList.add('comment-focus');
            window.setTimeout(() => retryElement.classList.remove('comment-focus'), 1200);
          }
        }, 300);
        return () => window.clearTimeout(retryTimer);
      }
    }, 80);
    return () => window.clearTimeout(timer);
  }, [selectedCommentId, commentClickCounter, topicComments]);

  // Inline reply composer state
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState<string>('');

  // Profile modal state is now managed by App.tsx


  // Helper function to render user name with profile link or deleted state
  const renderUserName = (user: User, className?: string) => {
    if (user.isDeleted || user.username === 'deleted_user') {
      return (
        <span className={className} style={{ color: '#999', fontStyle: 'italic' }}>
          [Deleted User]
        </span>
      );
    }

    const displayName = user.username || user.email.split('@')[0];
    return (
      <button
        type="button"
        className={className}
        onClick={() => {
          openProfilePanel(user.id, 'profile-external');
        }}
        style={{
          background: 'none',
          border: 'none',
          color: '#2563eb',
          textDecoration: 'none',
          cursor: 'pointer',
          padding: 0,
          font: 'inherit'
        }}
        onMouseEnter={(e) => {
          const target = e.target as HTMLElement;
          target.style.textDecoration = 'underline';
        }}
        onMouseLeave={(e) => {
          const target = e.target as HTMLElement;
          target.style.textDecoration = 'none';
        }}
      >
        {displayName}
      </button>
    );
  };

  // Special render function for topic authors (Reddit-style deletion)
  // Confirms anonymity visually — especially for the author, who still sees
  // their own name on the post while everyone else sees "Anonymous".
  const renderAnonymousBadge = (item?: { isAnonymous?: boolean }) => {
    if (!item?.isAnonymous) return null;
    return (
      <span
        title="Posted anonymously — the author's name is hidden from other users"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          background: '#f3f4f6',
          color: '#4b5563',
          border: '1px solid #d1d5db',
          borderRadius: 9999,
          padding: '2px 10px',
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.2
        }}
      >
        🕶 Anonymous
      </span>
    );
  };

  const renderTopicAuthor = (topic: Topic, className?: string) => {
    // If user deleted their own topic, show [deleted] with a tiny hint
    if (topic.isDeleted && topic.deletedBy === 'USER') {
      return (
        <span
          className={className}
          style={{ color: '#6b7280', fontStyle: 'italic', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          title="Removed by author"
          aria-label="Removed by author"
        >
          [deleted]
          <span style={{ fontSize: 10, color: '#9ca3af', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0 6px', lineHeight: '16px' }}>
            author
          </span>
        </span>
      );
    }

    // Otherwise use normal user rendering
    return renderUserName(topic.user, className);
  };

  const renderVisibilityBadge = (topic: Topic) => {
    if (topic.visibility === 'PUBLIC') return null;
    const labelMap: Record<TopicVisibility, string> = {
      PUBLIC: 'Public',
      PRIVATE_SELF: 'Only me',
      FOLLOWERS: 'Followers',
      MUTUALS: 'Mutuals',
      CUSTOM: 'Shared'
    };
    return (
      <span
        style={{
          marginLeft: 8,
          padding: '2px 8px',
          borderRadius: 999,
          backgroundColor: '#e5e7eb',
          color: '#374151',
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5
        }}
      >
        {labelMap[topic.visibility] || 'Private'}
      </span>
    );
  };

  const renderEditorialBadge = (topic?: { isEditorial?: boolean }) => {
    if (!topic?.isEditorial) return null;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          background: '#fef3c7',
          color: '#92400e',
          border: '1px solid #f59e0b',
          borderRadius: 9999,
          padding: '2px 10px',
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.2
        }}
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M8 1.5l1.9 3.85 4.25.62-3.07 2.99.72 4.23L8 10.9l-3.8 2 .72-4.23L1.85 6l4.25-.62L8 1.5z" />
        </svg>
        Curated
      </span>
    );
  };

  // Redirect to login if unauthenticated
  const requireAuth = (): boolean => {
    if (currentUserId) return true;
    const dest = `/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    window.location.href = dest;
    return false;
  };

  const formatAudienceUser = (user: { id: string; username?: string | null; email?: string }): string => {
    return user.username || user.email || user.id;
  };

  const handleAddAudienceUser = (user: { id: string; username?: string | null; email?: string }) => {
    if (!user.id || customAudienceIdSet.has(user.id) || user.id === currentUserId) {
      return;
    }
    setCustomAudience(prev => [...prev, user]);
    setAudienceSearchTerm('');
    setAudienceSearchResults([]);
  };

  const handleRemoveAudienceUser = (userId: string) => {
    setCustomAudience(prev => prev.filter(user => user.id !== userId));
  };

  const handleEditingAddAudienceUser = (user: { id: string; username?: string | null; email?: string }) => {
    if (!editingTopicId) return;
    if (!user.id || editingCustomAudienceIdSet.has(user.id) || user.id === currentUserId) {
      return;
    }
    setEditingCustomAudience(prev => [...prev, {
      id: user.id,
      username: user.username ?? null,
      email: user.email
    }]);
    setEditingAudienceSearchTerm('');
    setEditingAudienceSearchResults([]);
  };

  const handleEditingRemoveAudienceUser = (userId: string) => {
    setEditingCustomAudience(prev => prev.filter(user => user.id !== userId));
  };

  const handleEditingVisibilityChange = (next: TopicVisibility) => {
    const originalVisibility = editingTopicOriginalVisibility ?? selectedTopic?.visibility ?? null;
    const currentVisibility = editingTopicVisibility ?? originalVisibility;

    if (currentVisibility && currentVisibility !== 'PUBLIC' && next === 'PUBLIC') {
      const confirmed = window.confirm('Making this topic public is permanent. Anyone will be able to see it and you will not be able to make it private again. Continue?');
      if (!confirmed) {
        return;
      }
    }

    setEditingTopicVisibility(next);
    if (next !== 'CUSTOM') {
      setEditingAudienceSearchTerm('');
      setEditingAudienceSearchResults([]);
      setIsAudienceSearching(false);
    }
  };

  const handleSaveTopicDraftClick = () => {
    if (!currentUserId) {
      notify({ type: 'warning', message: 'Sign in to save drafts.' });
      return;
    }
    if (!onSaveTopicDraft) {
      notify({ type: 'error', message: 'Unable to save drafts right now.' });
      return;
    }
    const hasAny = Boolean(newTitle.trim() || newContent.trim() || selectedLocation || pendingDrawing || pendingEditorialDrawingsCount || isNsfw || isEditorial || editorialImport);
    if (!hasAny) {
      notify({ type: 'info', message: 'Add a title, description, or location before saving a draft.' });
      return;
    }
    const saved = onSaveTopicDraft({
      id: loadedTopicDraftIdRef.current || undefined,
      title: newTitle.trim(),
      content: newContent.trim(),
      basemap: selectedBasemap,
      visibility: topicVisibility,
      audienceUserIds: topicVisibility === 'CUSTOM' ? customAudience.map(user => user.id) : undefined,
      isNsfw,
      isEditorial,
      location: selectedLocation ? { lat: selectedLocation.lat, lng: selectedLocation.lng } : undefined,
      drawing: pendingDrawing || undefined
    });
    if (saved?.id) {
      loadedTopicDraftIdRef.current = saved.id;
    }
    topicDraftSavedThisSessionRef.current = true;
    notify({ type: 'success', message: 'Topic draft saved.' });
  };

  const clearEditorialImport = () => {
    setEditorialImport(null);
    setEditorialLoadedInfo(null);
    setEditorialError(null);
    setEditorialStyle(DEFAULT_EDITORIAL_STYLE);
    setEditorialLoading(false);
    setEditorialImportUserModified(false);
    editorialStyleLoadedRef.current = null;
  };

  const handleEditorialFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;
    setEditorialLoading(true);
    setEditorialError(null);
    try {
      const text = await file.text();
      const parsed = parseEditorialText(text, file.name);
      const limited = limitEditorialFeatures(parsed.features || []);
      const warnings = [...(parsed.warnings || []), ...limited.warnings];
      const bounds = getEditorialBounds(limited.features || []);
      if (bounds && onEditorialPreviewFocusDrawing) {
        const bboxPolygon = {
          type: 'Polygon',
          coordinates: [[
            [bounds.minLng, bounds.minLat],
            [bounds.maxLng, bounds.minLat],
            [bounds.maxLng, bounds.maxLat],
            [bounds.minLng, bounds.maxLat],
            [bounds.minLng, bounds.minLat]
          ]]
        };
        onEditorialPreviewFocusDrawing({
          id: '__EDITORIAL_UPLOAD_BOUNDS__',
          geometry: JSON.stringify(bboxPolygon),
          type: 'POLYGON'
        });
      }
      setEditorialImport({
        source: parsed.source,
        features: limited.features,
        warnings
      });
      setEditorialImportUserModified(true); // User uploaded new data
    } catch (err: any) {
      setEditorialImport(null);
      setEditorialImportUserModified(false);
      setEditorialError(err?.message || 'Failed to read this file.');
    } finally {
      setEditorialLoading(false);
      input.value = '';
    }
  };

  const updateEditorialStyle = <K extends keyof EditorialStyleConfig>(section: K, patch: Partial<EditorialStyleConfig[K]>) => {
    setEditorialStyle((prev) => ({
      ...prev,
      [section]: { ...prev[section], ...patch }
    }));
    // If editing an existing editorial topic and user changes styling, mark as modified
    if (editingTopicId && selectedTopic?.isEditorial) {
      setEditorialImportUserModified(true);
    }
  };

  const resetTopicCreateState = () => {
    setNewTitle('');
    setNewContent('');
    setIsNsfw(false);
    setIsEditorial(false);
    setEditorialImport(null);
    setEditorialImportUserModified(false);
    setEditorialLoadedInfo(null);
    setEditorialStyle(DEFAULT_EDITORIAL_STYLE);
    setEditorialError(null);
    setEditorialLoading(false);
    loadedTopicDraftIdRef.current = null;
    topicDraftSavedThisSessionRef.current = false;
  };

  const leaveTopicCreate = (next?: () => void) => {
    resetTopicCreateState();
    if (onCancelCreate) onCancelCreate();
    if (next) next();
  };

  const guardLeaveTopicCreate = (next: () => void) => {
    const hasChanges = Boolean(newTitle.trim() || newContent.trim() || selectedLocation || pendingDrawing || pendingEditorialDrawingsCount || isNsfw || isEditorial || editorialImport);
    if (hasChanges) {
      const leave = window.confirm('Leave topic creation? Unsaved changes will be lost unless you save a draft.');
      if (!leave) return;
    }
    leaveTopicCreate(next);
  };

  const handleSaveCommentDraftClick = () => {
    if (!currentUserId) {
      notify({ type: 'warning', message: 'Sign in to save drafts.' });
      return;
    }
    if (!selectedTopic || !onSaveCommentDraft) {
      notify({ type: 'error', message: 'Unable to save this draft right now.' });
      return;
    }
    const hasAny = Boolean(newCommentContent.trim() || commentLocation || hasPendingDrawing || pendingDrawing);
    if (!hasAny) {
      notify({ type: 'info', message: 'Write a comment or add a location before saving a draft.' });
      return;
    }
    onSaveCommentDraft({
      topicId: selectedTopic.id,
      topicTitle: selectedTopic.title,
      content: newCommentContent.trim(),
      location: commentLocation ? { lat: commentLocation.lat, lng: commentLocation.lng } : undefined,
      drawing: pendingDrawing || undefined
    });
    notify({ type: 'success', message: 'Comment draft saved.' });
  };

  const handleExitTopicCreate = () => {
    const hasUnsaved = Boolean(newTitle.trim() || newContent.trim() || selectedLocation || pendingDrawing || pendingEditorialDrawingsCount || isNsfw || isEditorial || editorialImport);
    const hasSavedDraft = topicDraftSavedThisSessionRef.current || Boolean(loadedTopicDraftIdRef.current);
    if (hasUnsaved && !hasSavedDraft) {
      const shouldSave = window.confirm('Save this topic as a draft before leaving?\n\nOK: Save draft\nCancel: Discard changes');
      if (shouldSave) {
        handleSaveTopicDraftClick();
      }
    }
    resetTopicCreateState();
    if (onCancelCreate) onCancelCreate();
  };

  const handleCancelNewComment = () => {
    const hasUnsaved = Boolean(newCommentContent.trim() || commentLocation || hasPendingDrawing || pendingDrawing);
    const hasSavedDraft = Boolean(activeCommentDraft);
    if (hasUnsaved && !hasSavedDraft) {
      const shouldSave = window.confirm('Save this comment as a draft before leaving?\n\nOK: Save draft\nCancel: Discard changes');
      if (shouldSave) {
        handleSaveCommentDraftClick();
      }
    }
    setNewCommentContent('');
    if (onCancelComment) onCancelComment();
  };

  const panelClass = `topic-panel ${isPanelExpanded ? 'expanded' : 'collapsed'} ${selectedTopic && isPanelExpanded && !showUserMenu ? 'comments-mode' : ''} ${isSearchMode ? 'search-mode' : ''}`;

  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newCommentContent, setNewCommentContent] = useState('');
  const [isNsfw, setIsNsfw] = useState(false);
  const [isAnonymousPost, setIsAnonymousPost] = useState(false);
  const [isAnonymousComment, setIsAnonymousComment] = useState(false);
  const [lineEstimateEnabled, setLineEstimateEnabled] = useState(lineEstimatePreference?.enabled ?? false);
  const [lineEstimateMode, setLineEstimateMode] = useState<LineEstimateMode>(lineEstimatePreference?.mode ?? 'walking');
  const [lineEstimateUnit, setLineEstimateUnit] = useState<LineEstimateUnit>(lineEstimatePreference?.unit ?? 'km');
  const [lineEstimateKm, setLineEstimateKm] = useState<number | null>(null);
  const [areaEstimateEnabled, setAreaEstimateEnabled] = useState(areaEstimatePreference?.enabled ?? false);
  const [areaEstimateUnit, setAreaEstimateUnit] = useState<AreaEstimateUnit>(areaEstimatePreference?.unit ?? 'sq_km');
  const [areaEstimateSqM, setAreaEstimateSqM] = useState<number | null>(null);
  const loadedTopicDraftIdRef = useRef<string | null>(null);
  const loadedCommentDraftIdRef = useRef<string | null>(null);
  const topicDraftSavedThisSessionRef = useRef(false);
  const pointOpacityValue = editorialStyle.point.opacity ?? 0.9;
  const pointSizeValue = editorialStyle.point.size ?? 6;
  const pointSizeFieldValue = editorialStyle.point.sizeField ?? '';
  const pointSizeMinValue = editorialStyle.point.sizeMin ?? 4;
  const pointSizeMaxValue = editorialStyle.point.sizeMax ?? 12;
  const pointStrokeWidthValue = editorialStyle.point.strokeWidth ?? 2;
  const pointShapeValue = editorialStyle.point.shape ?? 'pin';
  const pointPaletteValue = editorialStyle.point.palette ?? 'default';
  const lineWidthValue = editorialStyle.line.width ?? 2;
  const lineOpacityValue = editorialStyle.line.opacity ?? 0.9;
  const linePaletteValue = editorialStyle.line.palette ?? 'default';
  const lineDashValue = editorialStyle.line.dash ?? 'solid';
  const polygonFillOpacityValue = editorialStyle.polygon.fillOpacity ?? 0.35;
  const polygonStrokeWidthValue = editorialStyle.polygon.strokeWidth ?? 2;
  const polygonOpacityValue = editorialStyle.polygon.opacity ?? 0.9;
  const polygonPaletteValue = editorialStyle.polygon.palette ?? 'amber';
  const polygonDashValue = editorialStyle.polygon.dash ?? 'solid';
  const editorialStats = useMemo(() => {
    if (!editorialImport) return null;
    let points = 0;
    let lines = 0;
    let polygons = 0;
    editorialImport.features.forEach((feature) => {
      const type = feature.geometry?.type;
      if (type === 'Point' || type === 'MultiPoint') points += 1;
      if (type === 'LineString' || type === 'MultiLineString') lines += 1;
      if (type === 'Polygon' || type === 'MultiPolygon') polygons += 1;
    });
    return {
      total: editorialImport.features.length,
      points,
      lines,
      polygons,
      source: editorialImport.source
    };
  }, [editorialImport]);
  // Combined stats from either editorialImport (new data) or editorialLoadedInfo (existing drawings)
  const displayStats = useMemo(() => {
    if (editorialStats) return editorialStats;
    if (editorialLoadedInfo) return {
      total: editorialLoadedInfo.pointCount + editorialLoadedInfo.lineCount + editorialLoadedInfo.polygonCount,
      points: editorialLoadedInfo.pointCount,
      lines: editorialLoadedInfo.lineCount,
      polygons: editorialLoadedInfo.polygonCount,
      source: 'existing' as const
    };
    return null;
  }, [editorialStats, editorialLoadedInfo]);
  const editorialLocation = useMemo(() => {
    if (!editorialImport?.features?.length) return null;
    return getEditorialRepresentativeLocation(editorialImport.features);
  }, [editorialImport]);
  const resolvedTopicLocation = selectedLocation ?? editorialLocation ?? null;
  const isEditorialLocationAuto = !selectedLocation && Boolean(editorialLocation);
  const editorialManualCount = pendingEditorialDrawingsCount ?? 0;
  const editorialManualDisplayCount = isEditorial ? Math.max(editorialManualCount, pendingDrawing ? 1 : 0) : 0;
  const editorialControlsDisabled = Boolean(editingCommentId || isEditingShape);

  const renderEditorialImportPanel = (opts: { inputId: string; note?: string }) => (
    <div className="form-group" style={{ border: '1px dashed #c7d2fe', borderRadius: 10, padding: '12px 14px', background: '#f8fafc' }}>
      <label htmlFor={opts.inputId} style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>Upload curated map data</label>
      <input
        id={opts.inputId}
        type="file"
        accept=".geojson,.json,.kml,.gpx,application/geo+json,application/json,application/vnd.google-earth.kml+xml,application/gpx+xml"
        onChange={handleEditorialFileChange}
        disabled={editorialLoading || editorialControlsDisabled}
      />
      <small style={{ display: 'block', marginTop: 6, color: '#6b7280' }}>
        Accepted: GeoJSON, KML, GPX. CRS must be EPSG:4326 (WGS84). Shapefile support is not in this first pass.
      </small>
      <small style={{ display: 'block', marginTop: 4, color: '#6b7280' }}>
        Optional: choose a representative point on the map, or we will derive one from the uploaded data.
      </small>
      {opts.note && (
        <small style={{ display: 'block', marginTop: 4, color: '#6b7280' }}>
          {opts.note}
        </small>
      )}

      {editorialLoading && (
        <div style={{ marginTop: 8, fontSize: 13, color: '#2563eb' }}>Reading file…</div>
      )}
      {editorialError && (
        <div style={{ marginTop: 8, fontSize: 13, color: '#b91c1c' }}>{editorialError}</div>
      )}

      {/* Show info from newly uploaded data OR loaded existing drawings */}
      {displayStats && (
        <div style={{ marginTop: 10, fontSize: 13, color: '#374151' }}>
          <div style={{ fontWeight: 600 }}>
            Loaded {displayStats.total} feature(s) · {displayStats.points} points · {displayStats.lines} lines · {displayStats.polygons} polygons
          </div>
          {(editorialImport?.warnings?.length || editorialLoadedInfo?.warning) ? (
            <div style={{ marginTop: 6, color: '#b45309' }}>
              {editorialImport?.warnings?.join(' ') || editorialLoadedInfo?.warning}
            </div>
          ) : null}

          <div style={{ marginTop: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Styling</div>
            {displayStats.polygons > 0 && (
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4, color: '#6b7280' }}>Polygons</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <select
                    value={editorialStyle.polygon.mode}
                    onChange={(e) => {
                      const mode = e.target.value as EditorialStyleConfig['polygon']['mode'];
                      const field = mode === 'choropleth' && editorialPropertyKeys.numericKeys[0]
                        ? editorialPropertyKeys.numericKeys[0]
                        : editorialStyle.polygon.field;
                      updateEditorialStyle('polygon', { mode, field });
                    }}
                  >
                    <option value="auto">Auto</option>
                    <option value="single">Single color</option>
                    <option value="choropleth">Choropleth (numeric)</option>
                  </select>
                  {editorialStyle.polygon.mode === 'single' && (
                    <input
                      type="color"
                      value={editorialStyle.polygon.color || '#10b981'}
                      onChange={(e) => updateEditorialStyle('polygon', { color: e.target.value })}
                      title="Polygon color"
                    />
                  )}
                  {editorialStyle.polygon.mode === 'choropleth' && (
                    <select
                      value={editorialStyle.polygon.field || ''}
                      onChange={(e) => updateEditorialStyle('polygon', { field: e.target.value })}
                      disabled={editorialPropertyKeys.numericKeys.length === 0}
                    >
                      {editorialPropertyKeys.numericKeys.length === 0 ? (
                        <option value="">No numeric fields</option>
                      ) : (
                        editorialPropertyKeys.numericKeys.map((key) => (
                          <option key={key} value={key}>{key}</option>
                        ))
                      )}
                    </select>
                  )}
                  {editorialStyle.polygon.mode === 'choropleth' && (
                    <select
                      value={polygonPaletteValue}
                      onChange={(e) => updateEditorialStyle('polygon', { palette: e.target.value })}
                    >
                      {EDITORIAL_CHORO_PALETTE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8, fontSize: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    Fill opacity
                    <input
                      type="range"
                      min={0.05}
                      max={1}
                      step={0.05}
                      value={polygonFillOpacityValue}
                      onChange={(e) => updateEditorialStyle('polygon', { fillOpacity: Number(e.target.value) })}
                    />
                    <span style={{ color: '#6b7280' }}>{polygonFillOpacityValue.toFixed(2)}</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    Border style
                    <select
                      value={polygonDashValue}
                      onChange={(e) => updateEditorialStyle('polygon', { dash: e.target.value as EditorialStyleConfig['polygon']['dash'] })}
                    >
                      {EDITORIAL_LINE_STYLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    Stroke width
                    <input
                      type="range"
                      min={1}
                      max={6}
                      step={1}
                      value={polygonStrokeWidthValue}
                      onChange={(e) => updateEditorialStyle('polygon', { strokeWidth: Number(e.target.value) })}
                    />
                    <span style={{ color: '#6b7280' }}>{polygonStrokeWidthValue}</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    Stroke opacity
                    <input
                      type="range"
                      min={0.1}
                      max={1}
                      step={0.05}
                      value={polygonOpacityValue}
                      onChange={(e) => updateEditorialStyle('polygon', { opacity: Number(e.target.value) })}
                    />
                    <span style={{ color: '#6b7280' }}>{polygonOpacityValue.toFixed(2)}</span>
                  </label>
                </div>
              </div>
            )}

            {displayStats.lines > 0 && (
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4, color: '#6b7280' }}>Lines</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <select
                    value={editorialStyle.line.mode}
                    onChange={(e) => {
                      const mode = e.target.value as EditorialStyleConfig['line']['mode'];
                      const field = mode === 'categorized' && editorialPropertyKeys.categoricalKeys[0]
                        ? editorialPropertyKeys.categoricalKeys[0]
                        : editorialStyle.line.field;
                      updateEditorialStyle('line', { mode, field });
                    }}
                  >
                    <option value="auto">Auto</option>
                    <option value="single">Single color</option>
                    <option value="categorized">Categorized (string)</option>
                  </select>
                  {editorialStyle.line.mode === 'single' && (
                    <input
                      type="color"
                      value={editorialStyle.line.color || '#2563eb'}
                      onChange={(e) => updateEditorialStyle('line', { color: e.target.value })}
                      title="Line color"
                    />
                  )}
                  {editorialStyle.line.mode === 'categorized' && (
                    <select
                      value={editorialStyle.line.field || ''}
                      onChange={(e) => updateEditorialStyle('line', { field: e.target.value })}
                      disabled={editorialPropertyKeys.categoricalKeys.length === 0}
                    >
                      {editorialPropertyKeys.categoricalKeys.length === 0 ? (
                        <option value="">No categorical fields</option>
                      ) : (
                        editorialPropertyKeys.categoricalKeys.map((key) => (
                          <option key={key} value={key}>{key}</option>
                        ))
                      )}
                    </select>
                  )}
                  {editorialStyle.line.mode === 'categorized' && (
                    <select
                      value={linePaletteValue}
                      onChange={(e) => updateEditorialStyle('line', { palette: e.target.value })}
                    >
                      {EDITORIAL_CATEGORY_PALETTE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8, fontSize: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    Line style
                    <select
                      value={lineDashValue}
                      onChange={(e) => updateEditorialStyle('line', { dash: e.target.value as EditorialStyleConfig['line']['dash'] })}
                    >
                      {EDITORIAL_LINE_STYLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    Width
                    <input
                      type="range"
                      min={1}
                      max={8}
                      step={1}
                      value={lineWidthValue}
                      onChange={(e) => updateEditorialStyle('line', { width: Number(e.target.value) })}
                    />
                    <span style={{ color: '#6b7280' }}>{lineWidthValue}</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    Opacity
                    <input
                      type="range"
                      min={0.1}
                      max={1}
                      step={0.05}
                      value={lineOpacityValue}
                      onChange={(e) => updateEditorialStyle('line', { opacity: Number(e.target.value) })}
                    />
                    <span style={{ color: '#6b7280' }}>{lineOpacityValue.toFixed(2)}</span>
                  </label>
                </div>
              </div>
            )}

            {displayStats.points > 0 && (
              <div>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4, color: '#6b7280' }}>Points</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <select
                    value={editorialStyle.point.mode}
                    onChange={(e) => {
                      const mode = e.target.value as EditorialStyleConfig['point']['mode'];
                      let field = editorialStyle.point.field;
                      if (mode === 'categorized' && editorialPropertyKeys.categoricalKeys[0]) {
                        field = editorialPropertyKeys.categoricalKeys[0];
                      } else if (mode === 'choropleth' && editorialPropertyKeys.numericKeys[0]) {
                        field = editorialPropertyKeys.numericKeys[0];
                      }
                      updateEditorialStyle('point', { mode, field });
                    }}
                  >
                    <option value="auto">Auto</option>
                    <option value="single">Single color</option>
                    <option value="categorized">Categorized (string)</option>
                    <option value="choropleth">Choropleth (numeric)</option>
                  </select>
                  {editorialStyle.point.mode === 'single' && (
                    <input
                      type="color"
                      value={editorialStyle.point.color || '#f59e0b'}
                      onChange={(e) => updateEditorialStyle('point', { color: e.target.value })}
                      title="Point color"
                    />
                  )}
                  {editorialStyle.point.mode === 'categorized' && (
                    <select
                      value={editorialStyle.point.field || ''}
                      onChange={(e) => updateEditorialStyle('point', { field: e.target.value })}
                      disabled={editorialPropertyKeys.categoricalKeys.length === 0}
                    >
                      {editorialPropertyKeys.categoricalKeys.length === 0 ? (
                        <option value="">No categorical fields</option>
                      ) : (
                        editorialPropertyKeys.categoricalKeys.map((key) => (
                          <option key={key} value={key}>{key}</option>
                        ))
                      )}
                    </select>
                  )}
                  {editorialStyle.point.mode === 'categorized' && (
                    <select
                      value={pointPaletteValue}
                      onChange={(e) => updateEditorialStyle('point', { palette: e.target.value })}
                    >
                      {EDITORIAL_CATEGORY_PALETTE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  )}
                  {editorialStyle.point.mode === 'choropleth' && (
                    <select
                      value={editorialStyle.point.field || ''}
                      onChange={(e) => updateEditorialStyle('point', { field: e.target.value })}
                      disabled={editorialPropertyKeys.numericKeys.length === 0}
                    >
                      {editorialPropertyKeys.numericKeys.length === 0 ? (
                        <option value="">No numeric fields</option>
                      ) : (
                        editorialPropertyKeys.numericKeys.map((key) => (
                          <option key={key} value={key}>{key}</option>
                        ))
                      )}
                    </select>
                  )}
                  {editorialStyle.point.mode === 'choropleth' && (
                    <select
                      value={editorialStyle.point.palette || 'heat'}
                      onChange={(e) => updateEditorialStyle('point', { palette: e.target.value })}
                    >
                      {EDITORIAL_CHORO_PALETTE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  )}
                  {editorialStyle.point.mode === 'choropleth' && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                      <input
                        type="checkbox"
                        checked={editorialStyle.point.paletteReverse || false}
                        onChange={(e) => updateEditorialStyle('point', { paletteReverse: e.target.checked })}
                      />
                      Reverse
                    </label>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8, fontSize: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    Shape
                    <select
                      value={pointShapeValue}
                      onChange={(e) => updateEditorialStyle('point', { shape: e.target.value as EditorialStyleConfig['point']['shape'] })}
                    >
                      {EDITORIAL_POINT_SHAPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    Size by
                    <select
                      value={pointSizeFieldValue}
                      onChange={(e) => {
                        const nextField = e.target.value;
                        updateEditorialStyle('point', {
                          sizeField: nextField || undefined
                        });
                      }}
                      disabled={editorialPropertyKeys.numericKeys.length === 0}
                    >
                      <option value="">Fixed size</option>
                      {editorialPropertyKeys.numericKeys.map((key) => (
                        <option key={key} value={key}>{key}</option>
                      ))}
                    </select>
                  </label>
                  {pointSizeFieldValue ? (
                    <>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        Min size
                        <input
                          type="number"
                          min={2}
                          max={Math.max(pointSizeMinValue, pointSizeMaxValue - 1)}
                          value={pointSizeMinValue}
                          onChange={(e) => {
                            const raw = Number(e.target.value);
                            const next = Number.isFinite(raw) ? raw : pointSizeMinValue;
                            const clamped = Math.max(2, Math.min(next, pointSizeMaxValue - 1));
                            updateEditorialStyle('point', { sizeMin: clamped });
                          }}
                          style={{ width: 56 }}
                        />
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        Max size
                        <input
                          type="number"
                          min={Math.max(3, pointSizeMinValue + 1)}
                          max={40}
                          value={pointSizeMaxValue}
                          onChange={(e) => {
                            const raw = Number(e.target.value);
                            const next = Number.isFinite(raw) ? raw : pointSizeMaxValue;
                            const clamped = Math.max(pointSizeMinValue + 1, Math.min(next, 40));
                            updateEditorialStyle('point', { sizeMax: clamped });
                          }}
                          style={{ width: 56 }}
                        />
                      </label>
                    </>
                  ) : (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      Size
                      <input
                        type="range"
                        min={3}
                        max={16}
                        step={1}
                        value={pointSizeValue}
                        onChange={(e) => updateEditorialStyle('point', { size: Number(e.target.value) })}
                      />
                      <span style={{ color: '#6b7280' }}>{pointSizeValue}</span>
                    </label>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    Opacity
                    <input
                      type="range"
                      min={0.1}
                      max={1}
                      step={0.05}
                      value={pointOpacityValue}
                      onChange={(e) => updateEditorialStyle('point', { opacity: Number(e.target.value) })}
                    />
                    <span style={{ color: '#6b7280' }}>{pointOpacityValue.toFixed(2)}</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    Stroke width
                    <input
                      type="range"
                      min={0}
                      max={4}
                      step={1}
                      value={pointStrokeWidthValue}
                      onChange={(e) => updateEditorialStyle('point', { strokeWidth: Number(e.target.value) })}
                    />
                    <span style={{ color: '#6b7280' }}>{pointStrokeWidthValue}</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          <div style={{ marginTop: 10 }}>
            <button type="button" className="btn btn-ghost" onClick={clearEditorialImport}>
              Remove data
            </button>
            <span style={{ marginLeft: 8, fontSize: 12, color: '#6b7280' }}>
              {editorialImport
                ? `${editorialDrawings.length} drawing(s) ready to import.`
                : `${displayStats?.total ?? 0} existing drawing(s).`
              }
            </span>
          </div>
        </div>
      )}
    </div>
  );
  const activeCommentDraft = useMemo(() => {
    const topicId = selectedTopic?.id;
    if (!topicId) return null;
    if (resumeCommentDraft && resumeCommentDraft.topicId === topicId) return resumeCommentDraft;
    if (commentDrafts && topicId in commentDrafts) return commentDrafts[topicId];
    return null;
  }, [commentDrafts, resumeCommentDraft, selectedTopic?.id]);

  useEffect(() => {
    if (!lineEstimatePreference) return;
    setLineEstimateEnabled(lineEstimatePreference.enabled);
    setLineEstimateMode(lineEstimatePreference.mode);
    setLineEstimateUnit(lineEstimatePreference.unit);
  }, [lineEstimatePreference]);

  useEffect(() => {
    if (!areaEstimatePreference) return;
    setAreaEstimateEnabled(areaEstimatePreference.enabled);
    setAreaEstimateUnit(areaEstimatePreference.unit);
  }, [areaEstimatePreference]);

  const resolveLineGeometryForEstimate = React.useCallback(() => {
    const draft = getDraftGeometry?.({ silent: true }) ?? null;
    if (draft && draft.type === 'LineString') {
      return draft;
    }
    if (pendingDrawing?.type === 'LINE') {
      try {
        const parsed = JSON.parse(pendingDrawing.geometry);
        if (parsed && parsed.type === 'LineString') {
          return parsed;
        }
      } catch {}
    }
    if (isEditingShape && editingDrawingData?.type === 'LINE' && (drawingMode === 'none' || drawingMode === 'line')) {
      try {
        const parsed = JSON.parse(editingDrawingData.geometry);
        if (parsed && parsed.type === 'LineString') {
          return parsed;
        }
      } catch {}
    }
    return null;
  }, [getDraftGeometry, pendingDrawing, isEditingShape, editingDrawingData, drawingMode]);

  const resolvePolygonGeometryForEstimate = React.useCallback(() => {
    const draft = getDraftGeometry?.({ silent: true }) ?? null;
    if (draft && (draft.type === 'Polygon' || draft.type === 'MultiPolygon')) {
      return draft;
    }
    if (pendingDrawing?.type === 'POLYGON') {
      try {
        const parsed = JSON.parse(pendingDrawing.geometry);
        if (parsed && (parsed.type === 'Polygon' || parsed.type === 'MultiPolygon')) {
          return parsed;
        }
      } catch {}
    }
    if (isEditingShape && editingDrawingData?.type === 'POLYGON' && (drawingMode === 'none' || drawingMode === 'polygon')) {
      try {
        const parsed = JSON.parse(editingDrawingData.geometry);
        if (parsed && (parsed.type === 'Polygon' || parsed.type === 'MultiPolygon')) {
          return parsed;
        }
      } catch {}
    }
    return null;
  }, [getDraftGeometry, pendingDrawing, isEditingShape, editingDrawingData, drawingMode]);

  useEffect(() => {
    if (!lineEstimateEnabled) {
      setLineEstimateKm(null);
      return;
    }

    const updateEstimate = () => {
      const geometry = resolveLineGeometryForEstimate();
      if (!geometry) {
        setLineEstimateKm(null);
        return;
      }
      const distanceKm = lineLengthKm(geometry);
      if (!Number.isFinite(distanceKm) || distanceKm <= 0) {
        setLineEstimateKm(null);
        return;
      }
      const normalized = Math.round(distanceKm * 1000) / 1000;
      setLineEstimateKm(prev => (prev === normalized ? prev : normalized));
    };

    const shouldPoll = drawingMode === 'line'
      || (isEditingShape && editingDrawingData?.type === 'LINE' && drawingMode === 'none');
    if (shouldPoll) {
      updateEstimate();
      const id = window.setInterval(updateEstimate, 350);
      return () => window.clearInterval(id);
    }

    updateEstimate();
  }, [lineEstimateEnabled, drawingMode, isEditingShape, editingDrawingData, resolveLineGeometryForEstimate]);

  useEffect(() => {
    if (!areaEstimateEnabled) {
      setAreaEstimateSqM(null);
      return;
    }

    const updateEstimate = () => {
      const geometry = resolvePolygonGeometryForEstimate();
      if (!geometry) {
        setAreaEstimateSqM(null);
        return;
      }
      const areaSqM = polygonAreaSqM(geometry);
      if (!Number.isFinite(areaSqM) || areaSqM <= 0) {
        setAreaEstimateSqM(null);
        return;
      }
      const normalized = Math.round(areaSqM);
      setAreaEstimateSqM(prev => (prev === normalized ? prev : normalized));
    };

    const shouldPoll = drawingMode === 'polygon'
      || (isEditingShape && editingDrawingData?.type === 'POLYGON' && drawingMode === 'none');
    if (shouldPoll) {
      updateEstimate();
      const id = window.setInterval(updateEstimate, 350);
      return () => window.clearInterval(id);
    }

    updateEstimate();
  }, [areaEstimateEnabled, drawingMode, isEditingShape, editingDrawingData, resolvePolygonGeometryForEstimate]);

  const handleLineEstimateEnabledChange = (enabled: boolean) => {
    setLineEstimateEnabled(enabled);
    onLineEstimatePreferenceChange?.({ enabled, mode: lineEstimateMode, unit: lineEstimateUnit });
  };

  const handleLineEstimateModeChange = (mode: LineEstimateMode) => {
    setLineEstimateMode(mode);
    onLineEstimatePreferenceChange?.({ enabled: lineEstimateEnabled, mode, unit: lineEstimateUnit });
  };

  const handleLineEstimateUnitChange = (unit: LineEstimateUnit) => {
    setLineEstimateUnit(unit);
    onLineEstimatePreferenceChange?.({ enabled: lineEstimateEnabled, mode: lineEstimateMode, unit });
  };

  const handleAreaEstimateEnabledChange = (enabled: boolean) => {
    setAreaEstimateEnabled(enabled);
    onAreaEstimatePreferenceChange?.({ enabled, unit: areaEstimateUnit });
  };

  const handleAreaEstimateUnitChange = (unit: AreaEstimateUnit) => {
    setAreaEstimateUnit(unit);
    onAreaEstimatePreferenceChange?.({ enabled: areaEstimateEnabled, unit });
  };

  const renderLineEstimateControls = (idPrefix: string) => {
    const isLineContext = drawingMode === 'line'
      || pendingDrawing?.type === 'LINE'
      || (isEditingShape && editingDrawingData?.type === 'LINE' && drawingMode === 'none');
    if (!isLineContext) return null;
    const supportsDuration = lineEstimateMode !== 'distance';
    const speedKmh = supportsDuration ? LINE_ESTIMATE_SPEEDS_KMH[lineEstimateMode as Exclude<LineEstimateMode, 'distance'>] : null;
    const durationMinutes = (lineEstimateKm !== null && supportsDuration && speedKmh)
      ? (lineEstimateKm / speedKmh) * 60
      : null;
    const distanceLabel = lineEstimateKm !== null ? formatLineDistance(lineEstimateKm, lineEstimateUnit) : '';
    const durationLabel = durationMinutes !== null ? formatLineDuration(durationMinutes) : '';
    const toggleId = `${idPrefix}-line-estimate-toggle`;
    const unitId = `${idPrefix}-line-estimate-unit`;
    const distanceId = `${idPrefix}-line-estimate-distance`;
    const walkingId = `${idPrefix}-line-estimate-walking`;
    const cyclingId = `${idPrefix}-line-estimate-cycling`;
    const drivingId = `${idPrefix}-line-estimate-driving`;
    const modeName = `${idPrefix}-line-estimate-mode`;

    return (
      <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f8fafc' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            id={toggleId}
            type="checkbox"
            checked={lineEstimateEnabled}
            onChange={(e) => handleLineEstimateEnabledChange(e.target.checked)}
          />
          <label htmlFor={toggleId} style={{ fontWeight: 600, cursor: 'pointer' }}>
            Show line estimate?
          </label>
        </div>
        {lineEstimateEnabled && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 6, fontSize: 12 }}>
              <span style={{ color: '#6b7280' }}>Mode:</span>
              <label htmlFor={distanceId} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input
                  id={distanceId}
                  type="radio"
                  name={modeName}
                  value="distance"
                  checked={lineEstimateMode === 'distance'}
                  onChange={() => handleLineEstimateModeChange('distance')}
                />
                Length only
              </label>
              <label htmlFor={walkingId} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input
                  id={walkingId}
                  type="radio"
                  name={modeName}
                  value="walking"
                  checked={lineEstimateMode === 'walking'}
                  onChange={() => handleLineEstimateModeChange('walking')}
                />
                Walking
              </label>
              <label htmlFor={cyclingId} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input
                  id={cyclingId}
                  type="radio"
                  name={modeName}
                  value="cycling"
                  checked={lineEstimateMode === 'cycling'}
                  onChange={() => handleLineEstimateModeChange('cycling')}
                />
                Cycling
              </label>
              <label htmlFor={drivingId} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input
                  id={drivingId}
                  type="radio"
                  name={modeName}
                  value="driving"
                  checked={lineEstimateMode === 'driving'}
                  onChange={() => handleLineEstimateModeChange('driving')}
                />
                Driving
              </label>
              {supportsDuration && (
                <span style={{ color: '#9ca3af' }}>
                  {lineEstimateMode === 'walking' ? '5 km/h' : lineEstimateMode === 'cycling' ? '15 km/h' : '50 km/h'}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6, fontSize: 12 }}>
              <label htmlFor={unitId} style={{ color: '#6b7280' }}>Unit:</label>
              <select
                id={unitId}
                value={lineEstimateUnit}
                onChange={(e) => handleLineEstimateUnitChange(e.target.value as LineEstimateUnit)}
                style={{ padding: '2px 6px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 }}
              >
                {LINE_UNIT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#374151', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {lineEstimateKm !== null ? (
                <>
                  <span><strong>Distance:</strong> {distanceLabel}</span>
                  {supportsDuration && durationLabel ? (
                    <span><strong>Time:</strong> {durationLabel}</span>
                  ) : null}
                </>
              ) : (
                <span style={{ color: '#6b7280' }}>Add at least two points to see an estimate.</span>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderAreaEstimateControls = (idPrefix: string) => {
    const isPolygonContext = drawingMode === 'polygon'
      || pendingDrawing?.type === 'POLYGON'
      || (isEditingShape && editingDrawingData?.type === 'POLYGON' && drawingMode === 'none');
    if (!isPolygonContext) return null;
    const toggleId = `${idPrefix}-area-estimate-toggle`;
    const unitId = `${idPrefix}-area-estimate-unit`;

    return (
      <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f8fafc' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            id={toggleId}
            type="checkbox"
            checked={areaEstimateEnabled}
            onChange={(e) => handleAreaEstimateEnabledChange(e.target.checked)}
          />
          <label htmlFor={toggleId} style={{ fontWeight: 600, cursor: 'pointer' }}>
            Show area size estimate?
          </label>
        </div>
        {areaEstimateEnabled && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6, fontSize: 12 }}>
              <label htmlFor={unitId} style={{ color: '#6b7280' }}>Unit:</label>
              <select
                id={unitId}
                value={areaEstimateUnit}
                onChange={(e) => handleAreaEstimateUnitChange(e.target.value as AreaEstimateUnit)}
                style={{ padding: '2px 6px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 }}
              >
                {AREA_UNIT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#374151', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {areaEstimateSqM !== null ? (
                <span><strong>Area:</strong> {formatArea(areaEstimateSqM, areaEstimateUnit)}</span>
              ) : (
                <span style={{ color: '#6b7280' }}>Add at least three points to see an estimate.</span>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderLineEstimateSummary = (drawing?: MapDrawing | null) => {
    if (!drawing || drawing.type !== 'LINE') return null;
    const meta = (drawing.style as any)?.meta;
    if (!meta?.showLineEstimate) return null;
    let geometry: any = null;
    try {
      geometry = JSON.parse(drawing.geometry);
    } catch {}
    if (!geometry || geometry.type !== 'LineString') return null;
    const distanceKm = lineLengthKm(geometry);
    if (!Number.isFinite(distanceKm) || distanceKm <= 0) return null;
    const mode: LineEstimateMode = meta.lineEstimateMode === 'distance'
      ? 'distance'
      : meta.lineEstimateMode === 'cycling'
        ? 'cycling'
        : meta.lineEstimateMode === 'driving'
          ? 'driving'
          : 'walking';
    const unit: LineEstimateUnit = meta.lineEstimateUnit === 'mi'
      ? 'mi'
      : meta.lineEstimateUnit === 'm'
        ? 'm'
        : meta.lineEstimateUnit === 'ft'
          ? 'ft'
          : 'km';
    const distanceLabel = formatLineDistance(distanceKm, unit);
    if (mode === 'distance') {
      return (
        <span style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
          Distance: {distanceLabel}
        </span>
      );
    }
    const speedKmh = LINE_ESTIMATE_SPEEDS_KMH[mode];
    const durationMinutes = (distanceKm / speedKmh) * 60;
    return (
      <span style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
        Estimate: {distanceLabel} · {formatLineDuration(durationMinutes)} ({mode})
      </span>
    );
  };

  const renderAreaEstimateSummary = (drawing?: MapDrawing | null) => {
    if (!drawing || drawing.type !== 'POLYGON') return null;
    const meta = (drawing.style as any)?.meta;
    if (!meta?.showAreaEstimate) return null;
    let geometry: any = null;
    try {
      geometry = JSON.parse(drawing.geometry);
    } catch {}
    if (!geometry || (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')) return null;
    const areaSqM = polygonAreaSqM(geometry);
    if (!Number.isFinite(areaSqM) || areaSqM <= 0) return null;
    const unit: AreaEstimateUnit = meta.areaEstimateUnit === 'sq_mi'
      ? 'sq_mi'
      : meta.areaEstimateUnit === 'ha'
        ? 'ha'
        : meta.areaEstimateUnit === 'sq_m'
          ? 'sq_m'
          : meta.areaEstimateUnit === 'acres'
            ? 'acres'
            : 'sq_km';
    return (
      <span style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
        Area: {formatArea(areaSqM, unit)}
      </span>
    );
  };

  // ESC warning while creating a comment
  React.useEffect(() => {
    if (!isCreatingComment) return;
    const handler = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        const hasChanges = Boolean(newCommentContent.trim() || commentLocation || hasPendingDrawing);
        if (!hasChanges || window.confirm('Discard your new comment and any drawing?')) {
          setNewCommentContent('');
          onCancelComment && onCancelComment();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isCreatingComment, newCommentContent, commentLocation, hasPendingDrawing, onCancelComment]);

  // Always start with empty content when entering comment creation
  React.useEffect(() => {
    if (isCreatingComment) {
      setNewCommentContent('');
    }
  }, [isCreatingComment]);

  useEffect(() => {
    if (!isCreating) {
      loadedTopicDraftIdRef.current = null;
      topicDraftSavedThisSessionRef.current = false;
      return;
    }
    const draft = resumeTopicDraft;
    if (!draft || loadedTopicDraftIdRef.current === draft.id) return;
    loadedTopicDraftIdRef.current = draft.id;
    setNewTitle(draft.title || '');
    setNewContent(draft.content || '');
    setIsNsfw(Boolean(draft.isNsfw));
    setIsEditorial(Boolean(draft.isEditorial));
    if (draft.visibility) {
      setTopicVisibility(draft.visibility);
    }
    if (onApplyTopicDraftLocation) {
      onApplyTopicDraftLocation(draft);
    }
    if (draft.basemap && onBasemapChange) {
      onBasemapChange(draft.basemap);
    }
    if (resumeTopicDraft && onConsumeTopicResumeDraft) {
      onConsumeTopicResumeDraft();
    }
  }, [isCreating, resumeTopicDraft, onConsumeTopicResumeDraft, onApplyTopicDraftLocation, onBasemapChange]);

  useEffect(() => {
    if (!isCreatingComment || !selectedTopic) {
      loadedCommentDraftIdRef.current = null;
      return;
    }
    const draft = activeCommentDraft;
    if (!draft || loadedCommentDraftIdRef.current === draft.id) return;
    loadedCommentDraftIdRef.current = draft.id;
    setNewCommentContent(draft.content || '');
    if (onApplyCommentDraftLocation) {
      onApplyCommentDraftLocation(draft);
    }
    if (resumeCommentDraft && onConsumeCommentResumeDraft && draft.id === resumeCommentDraft.id) {
      onConsumeCommentResumeDraft(draft.topicId);
    }
  }, [isCreatingComment, selectedTopic, activeCommentDraft, resumeCommentDraft, onConsumeCommentResumeDraft, onApplyCommentDraftLocation]);

  React.useEffect(() => {
    if (!isCreating && !editingTopicId) {
      setTopicVisibility('PUBLIC');
      setCustomAudience([]);
      setFollowerOptions([]);
      setAudienceSearchTerm('');
      setAudienceSearchResults([]);
      setIsAudienceSearching(false);
      setIsNsfw(false);
      setIsEditorial(false);
      setEditorialImport(null);
      setEditorialLoadedInfo(null);
      setEditorialStyle(DEFAULT_EDITORIAL_STYLE);
      setEditorialError(null);
      setEditorialLoading(false);
    }
  }, [isCreating, editingTopicId]);

  useEffect(() => {
    if (!isCreating) return;
    onEditorialChange?.(isEditorial);
  }, [isCreating, isEditorial, onEditorialChange]);

  useEffect(() => {
    if (!onEditorialPreviewDrawingsChange) return;
    const previewActive = Boolean(
      (isCreating && isEditorial) ||
      (editingTopicId && selectedTopic?.isEditorial && editorialImportUserModified)
    );
    if (!previewActive || !editorialImport) {
      onEditorialPreviewDrawingsChange([]);
      return;
    }
    onEditorialPreviewDrawingsChange(editorialDrawings);
  }, [
    isCreating,
    isEditorial,
    editingTopicId,
    selectedTopic?.isEditorial,
    editorialImport,
    editorialDrawings,
    editorialImportUserModified,
    onEditorialPreviewDrawingsChange
  ]);

  React.useEffect(() => {
    if (!isCreating || !currentUserId) return;

    let cancelled = false;

    const loadFollowers = async () => {
      try {
        const response = await usersApi.getFollowers(currentUserId);
        if (!cancelled) {
          setFollowerOptions(response.followers || []);
        }
      } catch (err) {
        console.error('Failed to load followers for audience selection:', err);
      }
    };

    loadFollowers();

    return () => {
      cancelled = true;
    };
  }, [isCreating, currentUserId]);

  React.useEffect(() => {
    if (!isCreating || topicVisibility !== 'CUSTOM') {
      setIsAudienceSearching(false);
      if (!audienceSearchTerm) {
        setAudienceSearchResults([]);
      }
      return;
    }

    const term = audienceSearchTerm.trim();
    if (term.length < 2) {
      setAudienceSearchResults([]);
      setIsAudienceSearching(false);
      return;
    }

    let cancelled = false;
    setIsAudienceSearching(true);
    const timeout = setTimeout(async () => {
      try {
        const response = await searchApi.searchGroup(term, 'users', { limit: 8 });
        if (!cancelled) {
          setAudienceSearchResults(response.users?.items || []);
        }
      } catch (err) {
        console.error('Failed to search users for custom audience:', err);
        if (!cancelled) setAudienceSearchResults([]);
      } finally {
        if (!cancelled) setIsAudienceSearching(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [audienceSearchTerm, isCreating, topicVisibility]);

  useEffect(() => {
    if (!editingTopicId || editingTopicVisibility !== 'CUSTOM') {
      if (editingAudienceSearchResults.length) {
        setEditingAudienceSearchResults([]);
      }
      setIsEditingAudienceSearching(false);
      return;
    }

    const term = editingAudienceSearchTerm.trim();
    if (term.length < 2) {
      if (editingAudienceSearchResults.length) {
        setEditingAudienceSearchResults([]);
      }
      setIsEditingAudienceSearching(false);
      return;
    }

    let cancelled = false;
    setIsEditingAudienceSearching(true);
    const timeout = window.setTimeout(async () => {
      try {
        const response = await searchApi.searchGroup(term, 'users', { limit: 8 });
        if (!cancelled) {
          setEditingAudienceSearchResults(response.users?.items || []);
        }
      } catch (err) {
        console.error('Failed to search users for editing audience:', err);
        if (!cancelled) setEditingAudienceSearchResults([]);
      } finally {
        if (!cancelled) setIsEditingAudienceSearching(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [editingAudienceSearchTerm, editingTopicId, editingTopicVisibility, editingAudienceSearchResults.length]);


  // Show comments view when topic is selected and panel is expanded, unless user menu is open
  if (selectedTopic && isPanelExpanded && !showUserMenu && !isSearchMode) {
    return (
      <React.Fragment>
        <div className={panelClass}>
          {/* Comment View Header */}
          <div className="panel-header">
            <div
              className="panel-title"
              style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 6, padding: '0 6px', alignItems: 'stretch' }}
            >
              <div className="comment-view-header-row">
                <div className="comment-view-header-left">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setTimeout(() => {
                        const isEditingContent = editingTopicId || editingCommentId;
                        const isEditingShapes = isEditingShape && editingDrawingData;
                        const hasCommentChanges = isCreatingComment && Boolean(newCommentContent.trim() || commentLocation || hasPendingDrawing);

                        if (isEditingContent || isEditingShapes || hasCommentChanges) {
                          let message = 'You have unsaved changes:\n\n';
                          if (isEditingContent) message += '• Content editing in progress\n';
                          if (isEditingShapes) message += '• Shape editing in progress\n';
                          if (hasCommentChanges) message += '• New comment with content/location/drawing\n';
                          message += '\nAll changes will be lost. Do you want to continue?';

                          const shouldCancel = window.confirm(message);
                          if (!shouldCancel) return;

                          if (isEditingShapes && onCancelEditShape) {
                            onCancelEditShape();
                          }
                          if (isEditingContent) {
                            setEditingTopicId(null);
                            setEditingCommentId(null);
                            setEditContent('');
                            setEditingTopicVisibility(null);
                            setEditingTopicOriginalVisibility(null);
                            setEditingCustomAudience([]);
                            setEditingAudienceSearchTerm('');
                            setEditingAudienceSearchResults([]);
                            setIsEditingAudienceSearching(false);
                            setEditingAudienceLoading(false);
                          }
                          if (hasCommentChanges) {
                            setNewCommentContent('');
                            onCancelComment && onCancelComment();
                          }
                        }

                        try { onClosePopups && onClosePopups(); } catch (err) { }

                        const returnContext = profileReturnContextRef.current;
                        if (returnContext?.mode === 'messages') {
                          profileReturnContextRef.current = null;
                          const target = returnContext.userId || messageTargetUserId;
                          if (target) {
                            setMessageTargetUserId(target);
                          }
                          setPanelProfileUserId(null);
                          onSetProfileUserId?.(null);
                          if (returnContext.overlay) {
                            setMessagesOpen(true);
                            if (typeof onShowUserMenu === 'function') onShowUserMenu(false);
                          } else {
                            setMessagesOpen(false);
                            setUserMenuContent('messages');
                            setUserMenuEntry('messages-from-profile');
                            onExpandPanel?.();
                            onShowUserMenu?.(true);
                          }
                          sessionStorage.removeItem('returnToProfile');
                          return;
                        }

                        const returnToProfile = sessionStorage.getItem('returnToProfile');
                        if (returnToProfile) {
                          try {
                            const profileState = JSON.parse(returnToProfile);
                            sessionStorage.removeItem('returnToProfile');
                            if (profileState.entry === 'profile-from-messages') {
                              const target = profileState.messageUserId || messageTargetUserId || profileState.userId;
                              if (target) {
                                setMessageTargetUserId(target);
                              }
                              setPanelProfileUserId(null);
                              onSetProfileUserId?.(null);
                              if (profileState.overlay) {
                                setMessagesOpen(true);
                                if (typeof onShowUserMenu === 'function') onShowUserMenu(false);
                              } else {
                                setMessagesOpen(false);
                                setUserMenuContent('messages');
                                setUserMenuEntry('messages-from-profile');
                                onExpandPanel?.();
                                onShowUserMenu?.(true);
                              }
                              profileReturnContextRef.current = null;
                            } else {
                              if (profileState.userId === currentUserId) {
                                setPanelProfileUserId(null);
                                onSetProfileUserId?.(null);
                                setUserMenuEntry('profile-panel');
                              } else {
                                setPanelProfileUserId(profileState.userId);
                                onSetProfileUserId?.(profileState.userId);
                                setUserMenuEntry(profileState.entry || 'profile-external');
                              }
                              if (profileState.tab) {
                                setProfileInitialTab(profileState.tab);
                                onProfileTabChange?.(profileState.tab);
                              }
                              setUserMenuContent('profile');
                              onShowUserMenu?.(true);
                            }
                            return;
                          } catch (e) {
                            sessionStorage.removeItem('returnToProfile');
                          }
                        }

                        if (userMenuEntry === 'profile-from-messages') {
                          profileReturnContextRef.current = null;
                          setPanelProfileUserId(null);
                          onSetProfileUserId?.(null);
                          if (messagesOpen) {
                            if (typeof onShowUserMenu === 'function') onShowUserMenu(false);
                          } else {
                            setUserMenuContent('messages');
                            setUserMenuEntry('messages-from-profile');
                            onExpandPanel?.();
                            onShowUserMenu?.(true);
                          }
                          return;
                        }

                        handleBackToTopics();
                      }, 40);
                    }}
                    className="back-button"
                    style={{ marginLeft: -4 }}
                  >
                    <ArrowLeftIcon />
                    <span>Back</span>
                  </button>
                </div>

                <div className="comment-view-header-center">
                  {!(editingTopicId || editingCommentId || isEditingShape) && (isMultiSelectActive || (commentsWithDrawings && commentsWithDrawings.length > 1)) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button
                        className="btn btn-primary multi-select-toggle"
                        style={{ padding: '6px 12px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        onClick={(e) => { e.stopPropagation(); onToggleMultiSelect && onToggleMultiSelect(); }}
                        title={isMultiSelectActive ? 'Finish selecting drawings' : 'Select multiple drawings to show on the map (up to 10)'}
                        aria-label={isMultiSelectActive ? 'Finish selecting drawings' : 'Select multiple drawings to show on the map (up to 10)'}
                      >
                        <MultiSelectIcon size={14} />
                        {isMultiSelectActive ? (
                          `Done (${multiCount})`
                        ) : (
                          <span className="multi-select-label">
                            <span className="multi-select-label-short">Multi View</span>
                            <span className="multi-select-label-long">Show Multiple Drawings</span>
                          </span>
                        )}
                      </button>
                      {isMultiSelectActive && multiCount > 0 && (
                        <button
                          className="btn btn-ghost"
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          onClick={(e) => { e.stopPropagation(); onMultiSelectedChange && onMultiSelectedChange([]); }}
                          title="Clear selection"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="comment-view-header-right">
                  {(editingTopicId || editingCommentId || isEditingShape) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 6 }}>
                      <button
                        className="btn btn-primary"
                        style={{ padding: '8px 16px', fontSize: '14px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: 6, backgroundColor: '#10b981', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                        onClick={(e) => { e.stopPropagation(); saveEdit(); }}
                        disabled={savingEdit}
                        title="Save all changes"
                      >
                        {savingEdit ? (
                          <>
                            <span className="spinner" style={{ width: 12, height: 12, border: '2px solid #fff', borderRightColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin .8s linear infinite' }} />
                            Saving…
                          </>
                        ) : (
                          <>
                            <DiskIcon size={12} /> Save
                          </>
                        )}
                      </button>
                      <button
                        className="btn btn-ghost"
                        style={{ padding: '8px 12px', fontSize: '14px' }}
                        onClick={(e) => { e.stopPropagation(); cancelEdit(); }}
                        title="Cancel editing"
                        disabled={savingEdit}
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  {isPortrait && (
                    <div className="comment-sort-wrapper compact">
                      <div className="comment-sort-control compact">
                        <button
                          type="button"
                          ref={sortToggleRef}
                          className={`comment-sort-button compact ${showSortMenu ? 'open' : ''}`}
                          onClick={(e) => { e.stopPropagation(); setShowSortMenu((prev) => !prev); }}
                          aria-haspopup="menu"
                        aria-expanded={showSortMenu}
                        aria-label={`Change comment sort order (currently ${currentSortLabel})`}
                        title={`Sort by: ${currentSortLabel}`}
                      >
                        <SortIcon size={16} />
                      </button>
                        {sortMenu}
                      </div>
                    </div>
                  )}

                  {currentUserId && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: -4 }}>
                      <button
                        type="button"
                        className={`search-toggle-btn ${isSearchDrawerOpen ? 'open' : ''} ${hasActiveSearchFilter ? 'has-active' : ''}`}
                        aria-expanded={isSearchDrawerOpen}
                        aria-label="Search data and filters"
                        title="Search app data and apply filters (not map address search)"
                        onClick={() => {
                          const next = !isSearchDrawerOpen;
                          if (next) {
                            openSearchDrawer();
                          } else {
                            handleCloseSearchDrawer();
                          }
                        }}
                      >
                        <SearchIcon size={14} />
                        <span className="search-toggle-text">Search</span>
                        {hasActiveSearchFilter && <span className="search-toggle-badge" aria-label="Active filters">●</span>}
                      </button>
                      <UserMenu
                        username={currentUser?.username}
                        email={currentUser?.email}
                        isModerator={currentUser?.role === 'MODERATOR'}
                        onOpenProfile={() => { pendingUserMenuIntentRef.current = 'profile'; onShowUserMenu?.(true); }}
                        onOpenMessages={() => { pendingUserMenuIntentRef.current = 'messages'; onShowUserMenu?.(true); }}
                        onLogout={onLogout}
                        unreadMessages={unreadDMs}
                        onShowUserMenu={() => { pendingUserMenuIntentRef.current = 'menu'; onShowUserMenu?.(true); }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {!isPortrait && (
                <div className="comment-view-header-row secondary">
                  <div className="comment-sort-wrapper">
                    <div className="comment-sort-control">
                      <span className="comment-sort-label">Sort by</span>
                      <button
                        type="button"
                        ref={sortToggleRef}
                        className={`comment-sort-button ${showSortMenu ? 'open' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setShowSortMenu((prev) => !prev); }}
                        aria-haspopup="menu"
                        aria-expanded={showSortMenu}
                        aria-label="Change comment sort order"
                      >
                        <span className="comment-sort-current">
                          {currentSortLabel}
                        </span>
                        <ChevronDownIcon size={14} className={`comment-sort-caret ${showSortMenu ? 'open' : ''}`} />
                      </button>
                      {sortMenu}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Removed multi-select hint banner per request */}

          {/* Profile page navigation handled via anchor links below */}

          {/* Topic Creator's Original Entry (First) */}
          <div className="topic-list">
            <div className={`topic-item creator-entry ${!selectedCommentId ? 'selected' : ''}`} onClick={(e) => {
              if ((e.target as HTMLElement).closest('button,a,textarea,select,label') || (e.target as HTMLElement).closest('input:not([disabled])')) return;
              // In multi-select mode, clicking should toggle the topic drawing
              if (isMultiSelectActive) {
                if (commentsWithDrawings.includes('__TOPIC__')) {
                  onMultiSelectedChange && onMultiSelectedChange(
                    multiSelectedCommentIds.includes('__TOPIC__')
                      ? multiSelectedCommentIds.filter(id => id !== '__TOPIC__')
                      : (multiSelectedCommentIds.length < 10 ? [...multiSelectedCommentIds, '__TOPIC__'] : multiSelectedCommentIds)
                  );
                }
                return;
              }
              // Clear selected comment to show topic-level drawing
              onCommentSelect && onCommentSelect(null);
              // Focus on topic location
              onFocusTopicLocation && onFocusTopicLocation();
            }}
              style={{ cursor: 'pointer' }}>
              <div className="comment-header">
                {/* Multi-select checkbox for topic-level drawing */}
                {isMultiSelectActive && (
                  <input
                    type="checkbox"
                    checked={multiSelectedCommentIds.includes('__TOPIC__')}
                    disabled={!commentsWithDrawings.includes('__TOPIC__')}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onMultiSelectedChange && onMultiSelectedChange(
                      multiSelectedCommentIds.includes('__TOPIC__')
                        ? multiSelectedCommentIds.filter(id => id !== '__TOPIC__')
                        : (multiSelectedCommentIds.length < 10 ? [...multiSelectedCommentIds, '__TOPIC__'] : multiSelectedCommentIds)
                    )}
                    style={{ marginRight: 8 }}
                    title={commentsWithDrawings.includes('__TOPIC__') ? 'Add topic drawing to the map' : 'This topic has no drawing'}
                  />
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <h3 className="topic-title" style={{ marginBottom: '6px' }}>
                    {selectedTopic.title}
                  </h3>
                  {selectedTopic.isNsfw && (
                    <span style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 9999, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                      NSFW
                    </span>
                  )}
                  {renderAnonymousBadge(selectedTopic)}
                  {renderEditorialBadge(selectedTopic)}
                </div>
                {editingTopicId === selectedTopic.id ? (
                  <div className="comment-edit-container" style={{ marginBottom: '12px' }}>
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="topic-edit-textarea"
                      placeholder="Edit your topic content..."
                      autoFocus
                    />
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                      Use the Save button in the header above to save changes.
                    </div>
                    {(() => {
                      const originalVisibility = editingTopicOriginalVisibility ?? selectedTopic.visibility;
                      const currentVisibility = editingTopicVisibility ?? originalVisibility;
                      const showVisibilityControls = originalVisibility !== 'PUBLIC';

                      if (!showVisibilityControls) {
                        return (
                          <small style={{ marginTop: '16px', display: 'block', color: '#6b7280' }}>
                            Public topics stay public after creation.
                          </small>
                        );
                      }

                      return (
                        <>
                          <div style={{ marginTop: '16px' }}>
                            <label htmlFor="edit-topic-visibility" style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Visibility</label>
                            <select
                              id="edit-topic-visibility"
                              value={currentVisibility}
                              onChange={(e) => handleEditingVisibilityChange(e.target.value as TopicVisibility)}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: 6,
                                backgroundColor: '#fff',
                                fontSize: '14px',
                                cursor: 'pointer'
                              }}
                            >
                              {(['PUBLIC', 'PRIVATE_SELF', 'FOLLOWERS', 'MUTUALS', 'CUSTOM'] as TopicVisibility[]).map((option) => (
                                <option key={option} value={option}>
                                  {option === 'PUBLIC' ? 'Public – anyone can view'
                                    : option === 'PRIVATE_SELF' ? 'Only me'
                                      : option === 'FOLLOWERS' ? 'All my followers'
                                        : option === 'MUTUALS' ? 'Mutual followers'
                                          : 'Specific people…'}
                                </option>
                              ))}
                            </select>
                            <small style={{ marginTop: '4px', display: 'block', color: '#6b7280' }}>
                              Update who can see this topic.
                            </small>
                          </div>

                          {currentVisibility === 'CUSTOM' && (
                            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px', marginTop: '12px' }}>
                              <label htmlFor="edit-audience-search" style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>Share with specific users</label>
                              <input
                                id="edit-audience-search"
                                type="text"
                                value={editingAudienceSearchTerm}
                                onChange={(e) => setEditingAudienceSearchTerm(e.target.value)}
                                placeholder="Search by username…"
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: 6,
                                  marginBottom: 8
                                }}
                                disabled={editingAudienceLoading}
                              />
                              {editingAudienceSearchTerm.trim().length >= 2 && (
                                <div style={{
                                  maxHeight: 160,
                                  overflowY: 'auto',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: 6,
                                  marginBottom: 8
                                }}>
                                  {isEditingAudienceSearching ? (
                                    <div style={{ padding: '8px 12px', fontSize: 13, color: '#6b7280' }}>Searching…</div>
                                  ) : editingAudienceSearchResults.length === 0 ? (
                                    <div style={{ padding: '8px 12px', fontSize: 13, color: '#6b7280' }}>No users found.</div>
                                  ) : (
                                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                                      {editingAudienceSearchResults.map((user) => (
                                        <li key={user.id}>
                                          <button
                                            type="button"
                                            onClick={() => handleEditingAddAudienceUser({ id: user.id, username: user.username || null, email: undefined })}
                                            style={{
                                              width: '100%',
                                              padding: '8px 10px',
                                              border: 'none',
                                              background: 'white',
                                              textAlign: 'left',
                                              borderBottom: '1px solid #f1f5f9',
                                              cursor: editingCustomAudienceIdSet.has(user.id) ? 'not-allowed' : 'pointer',
                                              color: editingCustomAudienceIdSet.has(user.id) ? '#9ca3af' : '#1f2937'
                                            }}
                                            disabled={editingCustomAudienceIdSet.has(user.id)}
                                          >
                                            <div style={{ fontWeight: 600 }}>{user.username || user.id}</div>
                                            <div style={{ fontSize: 12, color: '#6b7280' }}>Joined {new Date(user.createdAt).toLocaleDateString()}</div>
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              )}
                              <div>
                                {editingAudienceLoading ? (
                                  <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>Loading current audience…</p>
                                ) : editingCustomAudience.length === 0 ? (
                                  <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>No people selected yet.</p>
                                ) : (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {editingCustomAudience.map((user) => {
                                      const isFollower = followerIdSet.has(user.id);
                                      return (
                                        <div
                                          key={user.id}
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            borderRadius: 9999,
                                            border: '1px solid ' + (isFollower ? '#d1d5db' : '#f87171'),
                                            padding: '4px 10px',
                                            backgroundColor: isFollower ? '#f8fafc' : '#fef2f2'
                                          }}
                                        >
                                          <span style={{ fontSize: 13 }}>{formatAudienceUser(user)}</span>
                                          {!isFollower && (
                                            <span style={{ fontSize: 11, color: '#b91c1c' }}>Not following you</span>
                                          )}
                                          <button
                                            type="button"
                                            onClick={() => handleEditingRemoveAudienceUser(user.id)}
                                            style={{
                                              border: 'none',
                                              background: 'transparent',
                                              cursor: 'pointer',
                                              color: '#6b7280',
                                              fontWeight: 600
                                            }}
                                            aria-label={`Remove ${formatAudienceUser(user)}`}
                                          >
                                            ×
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                              <small style={{ display: 'block', marginTop: 8, color: '#6b7280' }}>
                                People highlighted in red do not currently follow you. They’ll still be added, but we’ll warn you so you can double-check.
                              </small>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    {selectedTopic.isEditorial && userRole === 'MODERATOR' && (
                      <div style={{ marginTop: '16px' }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>Curated map data</div>
                        {renderEditorialImportPanel({
                          inputId: 'editorial-upload-edit',
                          note: 'Uploading new data will replace existing curated drawings when you save.'
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {selectedTopic.isDeleted && selectedTopic.deletedBy === 'MODERATOR' ? (
                      <div className="deletion-info" style={{ marginBottom: '12px' }}>
                        <span className="deletion-badge" style={{
                          display: 'inline-block',
                          backgroundColor: '#fee2e2',
                          color: '#dc2626',
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight: 500
                        }}>
                          Deleted by moderator
                        </span>
                        {selectedTopic.moderationReason && (
                          <p className="moderation-reason" style={{
                            margin: '8px 0 0 0',
                            fontSize: 13,
                            color: '#dc2626',
                            backgroundColor: '#fef2f2',
                            padding: 8,
                            borderRadius: 4,
                            borderLeft: '3px solid #dc2626'
                          }}>
                            Reason: {selectedTopic.moderationReason}
                          </p>
                        )}
                      </div>
                    ) : (
                      <LinkifiedText
                        as="p"
                        className="topic-content"
                        style={{ marginBottom: '12px', fontSize: '14px' }}
                        text={selectedTopic.content}
                      />
                    )}
                  </>
                )}
                {/* votes moved under location in footer */}
                <div className="comment-footer">
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {(() => {
                      const topicDrawing = getTopicDrawing(selectedTopic.id);
                      if (topicDrawing) {
                        return (
                          <>
                            <GeoLabel
                              full
                              fallback="Topic area"
                              drawing={topicDrawing}
                            />
                            {renderLineEstimateSummary(topicDrawing)}
                            {renderAreaEstimateSummary(topicDrawing)}
                          </>
                        );
                      }
                      if (selectedTopic.latitude && selectedTopic.longitude) {
                        return (
                          <GeoLabel
                            lat={selectedTopic.latitude}
                            lng={selectedTopic.longitude}
                            initialText={selectedTopic.city || selectedTopic.region || selectedTopic.country}
                            fallback="Topic location"
                          />
                        );
                      }
                      return null;
                    })()}
                    <div className="comment-actions-row">
                      <div className="comment-stats">
                        <VoteButton
                          type="upvote"
                          count={selectedTopic.upvotes}
                          isActive={userVotes.topics[selectedTopic.id] === 'upvote'}
                          onVote={() => { if (!requireAuth()) return; onTopicVote(selectedTopic.id, 'upvote'); }}
                        />
                        <VoteButton
                          type="downvote"
                          count={selectedTopic.downvotes}
                          isActive={userVotes.topics[selectedTopic.id] === 'downvote'}
                          onVote={() => { if (!requireAuth()) return; onTopicVote(selectedTopic.id, 'downvote'); }}
                        />
                      </div>
                      <div className="action-buttons">
                        {canEdit(selectedTopic.userId) && !editingTopicId && !editingCommentId && !selectedTopic.isDeleted && (
                          <>
                            <button
                              type="button"
                              className="icon-btn"
                              title="Edit your topic"
                              onClick={(e) => { e.stopPropagation(); startEditTopic(selectedTopic); }}
                            >
                              <EditIcon size={14} />
                            </button>
                            <button
                              type="button"
                              className="icon-btn"
                              title="Delete your topic"
                              onClick={(e) => { e.stopPropagation(); handleDelete('topic', selectedTopic.id); }}
                              style={{ color: '#dc2626' }}
                            >
                              <DeleteIcon size={14} />
                            </button>
                          </>
                        )}
                        {isModerator && !canEdit(selectedTopic.userId) && !selectedTopic.isDeleted && (
                          <button
                            type="button"
                            className="icon-btn"
                            title="[MODERATOR] Delete this topic"
                            onClick={(e) => { e.stopPropagation(); handleModeratorDelete('topic', selectedTopic.id); }}
                            style={{
                              color: '#dc2626',
                              backgroundColor: '#fef2f2',
                              border: '1px solid #fecaca',
                              fontWeight: 'bold'
                            }}
                          >
                            <DeleteIcon size={14} />
                          </button>
                        )}
                        <button
                          type="button"
                          className="icon-btn"
                          title="Copy share link"
                          onClick={(e) => { e.stopPropagation(); onShareTopic && onShareTopic(selectedTopic.id); }}
                        >
                          <ShareIcon size={14} />
                        </button>
                        <SaveButton
                          contentType="TOPIC"
                          contentId={selectedTopic.id}
                          currentUserId={currentUserId}
                        />
                        <button
                          type="button"
                          className="icon-btn"
                          title="Report this topic"
                          onClick={(e) => { e.stopPropagation(); onReportTopic && onReportTopic(selectedTopic.id); }}
                        >
                          <FlagIcon size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 'auto', alignItems: 'flex-end', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {renderTopicAuthor(selectedTopic, 'comment-author-link')}
                    <span>
                      {new Date(selectedTopic.createdAt).toLocaleString()}
                      {(() => {
                        try {
                          const ca = new Date(selectedTopic.createdAt as any).getTime();
                          const ua = selectedTopic.updatedAt ? new Date(selectedTopic.updatedAt as any).getTime() : ca;
                          if (ua - ca > 10000) return <span style={{ color: '#6b7280', marginLeft: 6 }}>(edited)</span>;
                        } catch { }
                        return null;
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Basemap selection - show when editing comments or topics */}
              {(editingCommentId || editingTopicId) && (
                <div style={{
                  marginTop: '12px',
                  marginBottom: '12px',
                  padding: '12px',
                  backgroundColor: '#f8fafc',
                  borderRadius: 6,
                  border: '1px solid #e2e8f0'
                }}>
                  <label htmlFor="basemap-select" style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px', display: 'block' }}>
                    Default Basemap
                  </label>
                  <select
                    id="basemap-select"
                    value={selectedBasemap}
                    onChange={(e) => onBasemapChange?.(e.target.value as any)}
                    disabled={!onBasemapChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: 6,
                      backgroundColor: '#fff',
                      fontSize: '14px',
                      cursor: onBasemapChange ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {basemapOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                  <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Select the map style people will see first for this topic.
                  </small>
                </div>
              )}

              {/* Inline shape editing tools for topic */}
              {isEditingShape && editingDrawingData && !editingDrawingData.commentId && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: '#f1f5f9',
                  borderRadius: 6,
                  border: '2px solid #3b82f6'
                }}>
                  <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#1e40af' }}>
                      Editing Topic Shape
                    </span>
                    {drawingMode !== 'none' && (
                      <span style={{ fontSize: 11, color: '#6b7280' }}>
                        Selected: {drawingMode === 'marker' ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <PointIcon size={12} /> Point
                          </span>
                        ) : drawingMode === 'line' ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <LineIcon size={12} /> Line
                          </span>
                        ) : (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <PolygonIcon size={12} /> Area
                          </span>
                        )}
                      </span>
                    )}
                  </div>

                  {/* Panel Drawing Tools (Undo/Clear inline) */}
                  <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => onSetDrawingMode && onSetDrawingMode('marker')}
                      className="draw-btn"
                      style={{
                        padding: '4px 8px',
                        fontSize: 12,
                        backgroundColor: drawingMode === 'marker' ? '#3b82f6' : '#fff',
                        color: drawingMode === 'marker' ? '#fff' : '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}
                    >
                      <PointIcon size={12} /> Point
                    </button>
                    <button
                      type="button"
                      onClick={() => onSetDrawingMode && onSetDrawingMode('line')}
                      className="draw-btn"
                      style={{
                        padding: '4px 8px',
                        fontSize: 12,
                        backgroundColor: drawingMode === 'line' ? '#2563eb' : '#fff',
                        color: drawingMode === 'line' ? '#fff' : '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}
                    >
                      <LineIcon size={12} /> Line
                    </button>
                    <button
                      type="button"
                      onClick={() => onSetDrawingMode && onSetDrawingMode('polygon')}
                      className="draw-btn"
                      style={{
                        padding: '4px 8px',
                        fontSize: 12,
                        backgroundColor: drawingMode === 'polygon' ? '#059669' : '#fff',
                        color: drawingMode === 'polygon' ? '#fff' : '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}
                    >
                      <PolygonIcon size={12} /> Area
                    </button>
                    {/* Inline controls aligned right */}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                      {(drawingMode === 'line' || drawingMode === 'polygon') && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button type="button" onClick={onUndoPoint} className="btn btn-ghost" title="Undo last point">⤺ Undo</button>
                          <button type="button" onClick={onClearPath} className="btn btn-ghost" title="Clear drawing">✕ Clear</button>
                        </div>
                      )}
                      {drawingMode === 'marker' && (
                        <button type="button" onClick={onClearPath} className="btn btn-ghost" title="Clear drawing">✕ Clear</button>
                      )}
                    </div>
                  </div>
                  {renderLineEstimateControls('edit-topic-inline')}
                  {renderAreaEstimateControls('edit-topic-inline')}
                  <div style={{ fontSize: 12, color: '#666', fontStyle: 'italic' }}>💡 Use the Save button in the header to save all changes</div>
                </div>
              )}
            </div>


            {/* Comments from other users */}
            {visibleTopLevelComments.map((comment) => (
              <div
                key={comment.id}
                className={`topic-item ${selectedCommentId === comment.id ? 'selected' : ''}`}
                data-comment-id={comment.id}
                onClick={(e) => {
                  // Ignore clicks on interactive controls only (but allow disabled inputs)
                  const target = e.target as HTMLElement;
                  if (target.closest('button,a,textarea,select,label') || target.closest('input:not([disabled])')) return;
                  // In multi-select mode, clicking the comment item should toggle selection, not set single selection
                  if (isMultiSelectActive) {
                    if (drawingsSet.has(comment.id)) {
                      toggleMultiFor(comment.id);
                    }
                    return;
                  }
                  // Select the comment first (this will update selectedComment and trigger re-render)
                  onCommentSelect && onCommentSelect(comment);
                  // Then focus on the comment location (slight delay to ensure state update)
                  setTimeout(() => {
                    onFocusCommentLocation && onFocusCommentLocation(comment);
                  }, 10);
                }}
                style={{ cursor: 'pointer' }}>
                <div className="comment-header">
                  {isMultiSelectActive && (
                    <input
                      type="checkbox"
                      checked={multiSelectedCommentIds.includes(comment.id)}
                      disabled={!drawingsSet.has(comment.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => drawingsSet.has(comment.id) && toggleMultiFor(comment.id)}
                      style={{ marginRight: 8 }}
                      title={drawingsSet.has(comment.id) ? 'Add this drawing to the map' : 'This comment has no drawing'}
                    />
                  )}
                  {editingCommentId === comment.id ? (
                    <div className="comment-edit-container" style={{ marginBottom: '12px' }}>
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="comment-edit-textarea"
                        placeholder="Edit your comment..."
                        autoFocus
                      />

                      {/* Drawing tools for comment editing (hidden when shape editing panel is active for this comment) */}
                      {!(isEditingShape && editingDrawingData && editingDrawingData.commentId === comment.id) && (
                        <div style={{ marginTop: '12px', marginBottom: '8px' }}>
                          <div style={{ marginBottom: '8px', fontSize: '13px', color: '#666' }}>
                            📍 Add or edit shapes for this comment:
                          </div>
                          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                            <button
                              type="button"
                              onClick={() => onSetDrawingMode && onSetDrawingMode('marker')}
                              className={`draw-btn ${drawingMode === 'marker' ? 'active marker' : ''}`}
                            ><PointIcon size={12} /> Point</button>
                            <button
                              type="button"
                              onClick={() => onSetDrawingMode && onSetDrawingMode('line')}
                              className={`draw-btn ${drawingMode === 'line' ? 'active line' : ''}`}
                            ><LineIcon size={12} /> Line</button>
                            <button
                              type="button"
                              onClick={() => onSetDrawingMode && onSetDrawingMode('polygon')}
                              className={`draw-btn ${drawingMode === 'polygon' ? 'active polygon' : ''}`}
                            ><PolygonIcon size={12} /> Area</button>
                            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                              {(drawingMode === 'line' || drawingMode === 'polygon') && (
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button type="button" onClick={onUndoPoint} className="btn btn-ghost" title="Undo last point">⤺ Undo</button>
                                  <button type="button" onClick={onClearPath} className="btn btn-ghost" title="Clear drawing">✕ Clear</button>
                                </div>
                              )}
                              {drawingMode === 'marker' && (
                                <button type="button" onClick={onClearPath} className="btn btn-ghost" title="Clear drawing">✕ Clear</button>
                              )}
                            </div>
                          </div>

                          {(drawingMode === 'line' || drawingMode === 'polygon') && (
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button type="button" onClick={onFinishDrawing} className="btn btn-primary">✓ Finish</button>
                            </div>
                          )}

                          {hasPendingDrawing && (
                            <div style={{ marginTop: 8, color: '#059669', fontSize: 12 }}>
                              ✅ Geometry added. Choose a different type to replace.
                            </div>
                          )}
                        </div>
                      )}

                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                        Use the Save button in the header above to save changes.
                      </div>
                    </div>
                  ) : (
                    <>
                      {comment.isDeleted ? (
                        <div className="deletion-info" style={{ marginBottom: '12px' }}>
                          <span className="deletion-badge" style={{
                            display: 'inline-block',
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 500
                          }}>
                            Deleted by {comment.deletedBy === 'USER' ? 'author' : 'moderator'}
                          </span>
                          {comment.moderationReason && comment.deletedBy === 'MODERATOR' && (
                            <p className="moderation-reason" style={{
                              margin: '8px 0 0 0',
                              fontSize: 13,
                              color: '#dc2626',
                              backgroundColor: '#fef2f2',
                              padding: 8,
                              borderRadius: 4,
                              borderLeft: '3px solid #dc2626'
                            }}>
                              Reason: {comment.moderationReason}
                            </p>
                          )}
                        </div>
                      ) : (
                        <LinkifiedText
                          as="p"
                          className="topic-content"
                          style={{ marginBottom: '12px' }}
                          text={comment.content}
                        />
                      )}
                    </>
                  )}
                  {/* votes moved under location in footer */}
                  <div className="comment-footer">
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {(() => {
                        const commentDrawing = getCommentDrawing(comment.id);
                        if (commentDrawing) {
                          const initialLabel = comment.locationLabel || null;
                          const initialFullLabel = comment.locationFullLabel || comment.locationLabel || null;
                          return (
                            <>
                              <GeometryLabel
                                geometry={commentDrawing.geometry}
                                drawingType={commentDrawing.type}
                                fallback={initialLabel || 'Comment area'}
                                full={false}
                                initialLabel={initialLabel}
                                initialFullLabel={initialFullLabel}
                              />
                              {renderLineEstimateSummary(commentDrawing)}
                              {renderAreaEstimateSummary(commentDrawing)}
                            </>
                          );
                        } else if (comment.latitude && comment.longitude) {
                          return (
                            <GeoLabel
                              lat={comment.latitude}
                              lng={comment.longitude}
                              initialText={comment.locationLabel || undefined}
                              fallback={comment.locationLabel || 'Location'}
                            />
                          );
                        } else {
                          return null;
                        }
                      })()}
                      {editingCommentId !== comment.id && (
                        <div className="comment-actions-row">
                          <div className="comment-stats">
                            <VoteButton
                              type="upvote"
                              count={comment.upvotes}
                              isActive={userVotes.comments[comment.id] === 'upvote'}
                              onVote={() => { if (!requireAuth()) return; onCommentVote(comment.id, 'upvote'); }}
                            />
                            <VoteButton
                              type="downvote"
                              count={comment.downvotes}
                              isActive={userVotes.comments[comment.id] === 'downvote'}
                              onVote={() => { if (!requireAuth()) return; onCommentVote(comment.id, 'downvote'); }}
                            />
                          </div>
                          <div className="action-buttons">
                            {canEdit(comment.userId) && !editingCommentId && !editingTopicId && !comment.isDeleted && (
                              <>
                                <button
                                  type="button"
                                  className="icon-btn"
                                  title="Edit your comment"
                                  onClick={(e) => { e.stopPropagation(); startEditComment(comment); }}
                                >
                                  <EditIcon size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="icon-btn"
                                  title="Delete your comment"
                                  onClick={(e) => { e.stopPropagation(); handleDelete('comment', comment.id); }}
                                  style={{ color: '#dc2626' }}
                                >
                                  <DeleteIcon size={14} />
                                </button>
                              </>
                            )}
                            {isModerator && !canEdit(comment.userId) && !comment.isDeleted && (
                              <button
                                type="button"
                                className="icon-btn"
                                title="[MODERATOR] Delete this comment"
                                onClick={(e) => { e.stopPropagation(); handleModeratorDelete('comment', comment.id); }}
                                style={{
                                  color: '#dc2626',
                                  backgroundColor: '#fef2f2',
                                  border: '1px solid #fecaca',
                                  fontWeight: 'bold'
                                }}
                              >
                                <DeleteIcon size={14} />
                              </button>
                            )}
                            {currentUserId && !comment.isDeleted ? (
                              <button
                                type="button"
                                className="icon-btn"
                                title="Reply to this comment"
                                aria-label="Reply to this comment"
                                onClick={(e) => { e.stopPropagation(); setReplyingToId(comment.id); setReplyContent(''); }}
                              >
                                <ReplyIcon size={14} />
                              </button>
                            ) : currentUserId ? null : (
                              <button
                                type="button"
                                className="icon-btn"
                                title="Log in to reply"
                                aria-label="Log in to reply"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const dest = `/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;
                                  window.location.href = dest;
                                }}
                              >
                                <ReplyIcon size={14} />
                              </button>
                            )}
                            <button
                              type="button"
                              className="icon-btn"
                              title="Copy link to comment"
                              onClick={(e) => { e.stopPropagation(); onShareComment && onShareComment(comment.id); }}
                            >
                              <ShareIcon size={14} />
                            </button>
                            <SaveButton
                              contentType="COMMENT"
                              contentId={comment.id}
                              currentUserId={currentUserId}
                            />
                            <button
                              type="button"
                              className="icon-btn"
                              title="Report this comment"
                              onClick={(e) => { e.stopPropagation(); onReportComment && onReportComment(comment.id); }}
                            >
                              <FlagIcon size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className={`comment-meta-wrapper${isPortrait ? ' portrait' : ''}`}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {renderUserName(comment.user, 'comment-author-link')}
                        {renderAnonymousBadge(comment)}
                      </span>
                      <div className={`comment-meta-info${isPortrait ? ' portrait' : ''}`}>
                        <span className="comment-meta-timestamp">{new Date(comment.createdAt).toLocaleString()}</span>
                        {wasCommentEdited(comment) && (
                          <span className={`comment-meta-edited${isPortrait ? ' stacked' : ''}`}>(edited)</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Inline reply composer for this parent comment */}
                {replyingToId === comment.id && currentUserId && (
                  <div style={{ marginTop: '8px', paddingLeft: '8px' }}>
                    <textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="Write a reply..."
                      style={{ width: '100%', minHeight: '100px', padding: '12px 14px', border: '2px solid #e5e7eb', borderRadius: 4, fontSize: 14, boxSizing: 'border-box' }}
                    />
                    <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={(e) => { e.stopPropagation(); setReplyingToId(null); setReplyContent(''); }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!replyContent.trim()}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!onSubmitReply || !selectedTopic) return;
                          const content = replyContent.trim();
                          try {
                            onSubmitReply(comment.id, { content });
                            setReplyContent('');
                            setReplyingToId(null);
                          } catch (err) {
                            // failed to post reply
                          }
                        }}
                      >
                        Post Reply
                      </button>
                    </div>
                  </div>
                )}

                {/* Inline shape editing tools for comment */}
                {isEditingShape && editingDrawingData && editingDrawingData.commentId === comment.id && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#f1f5f9',
                    borderRadius: 6,
                    border: '2px solid #3b82f6'
                  }}>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1e40af' }}>
                        Editing Comment Shape
                      </span>
                    </div>
                    {/* Panel Drawing Tools (Undo/Clear inline like topic editing) */}
                    <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={() => onSetDrawingMode && onSetDrawingMode('marker')}
                        className="draw-btn"
                        style={{ padding: '4px 8px', fontSize: 12, backgroundColor: drawingMode === 'marker' ? '#3b82f6' : '#fff', color: drawingMode === 'marker' ? '#fff' : '#374151', border: '1px solid #d1d5db', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <PointIcon size={12} /> Point
                      </button>
                      <button
                        type="button"
                        onClick={() => onSetDrawingMode && onSetDrawingMode('line')}
                        className="draw-btn"
                        style={{ padding: '4px 8px', fontSize: 12, backgroundColor: drawingMode === 'line' ? '#2563eb' : '#fff', color: drawingMode === 'line' ? '#fff' : '#374151', border: '1px solid #d1d5db', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <LineIcon size={12} /> Line
                      </button>
                      <button
                        type="button"
                        onClick={() => onSetDrawingMode && onSetDrawingMode('polygon')}
                        className="draw-btn"
                        style={{ padding: '4px 8px', fontSize: 12, backgroundColor: drawingMode === 'polygon' ? '#059669' : '#fff', color: drawingMode === 'polygon' ? '#fff' : '#374151', border: '1px solid #d1d5db', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <PolygonIcon size={12} /> Area
                      </button>
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                        {(drawingMode === 'line' || drawingMode === 'polygon') && (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button type="button" onClick={onUndoPoint} className="btn btn-ghost" title="Undo last point">⤺ Undo</button>
                            <button type="button" onClick={onClearPath} className="btn btn-ghost" title="Clear drawing">✕ Clear</button>
                          </div>
                        )}
                        {drawingMode === 'marker' && (
                          <button type="button" onClick={onClearPath} className="btn btn-ghost" title="Clear drawing">✕ Clear</button>
                        )}
                      </div>
                    </div>
                    {renderLineEstimateControls('edit-comment-inline')}
                    {renderAreaEstimateControls('edit-comment-inline')}
                    <div style={{ fontSize: 12, color: '#666', fontStyle: 'italic' }}>Use the Save button in the header to save all changes</div>
                  </div>
                )}

                {/* Render replies (one-level) */}
                {repliesMap.get(comment.id)?.map((reply) => (
                  <div
                    key={reply.id}
                    className={`topic-item ${selectedCommentId === reply.id ? 'selected' : ''}`}
                    data-comment-id={reply.id}
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.closest('button,a,textarea,select,label') || target.closest('input:not([disabled])')) return;
                      onCommentSelect && onCommentSelect(reply);
                      setTimeout(() => { onFocusCommentLocation && onFocusCommentLocation(reply); }, 10);
                    }}
                    style={{ cursor: 'pointer', marginTop: 8, marginLeft: 16, borderLeft: '2px solid #eee', paddingLeft: 12 }}
                  >
                    <div className="comment-header">
                      {isMultiSelectActive && (
                        <input
                          type="checkbox"
                          checked={multiSelectedCommentIds.includes(reply.id)}
                          disabled={!drawingsSet.has(reply.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => drawingsSet.has(reply.id) && toggleMultiFor(reply.id)}
                          style={{ marginRight: 8 }}
                          title={drawingsSet.has(reply.id) ? 'Add this drawing to the map' : 'This comment has no drawing'}
                        />
                      )}
                      {editingCommentId === reply.id ? (
                        <div className="comment-edit-container" style={{ marginBottom: '12px' }}>
                          <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            className="comment-edit-textarea"
                            placeholder="Edit your comment..."
                            autoFocus
                          />

                          <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                            Use the Save button in the header above to save changes.
                          </div>
                        </div>
                      ) : (
                        <>
                          {reply.isDeleted ? (
                            <div className="deletion-info" style={{ marginBottom: '12px' }}>
                              <span className="deletion-badge" style={{
                                display: 'inline-block',
                                backgroundColor: '#fee2e2',
                                color: '#dc2626',
                                padding: '2px 8px',
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: 500
                              }}>
                                Deleted by {reply.deletedBy === 'USER' ? 'author' : 'moderator'}
                              </span>
                              {reply.moderationReason && reply.deletedBy === 'MODERATOR' && (
                                <p className="moderation-reason" style={{
                                  margin: '8px 0 0 0',
                                  fontSize: 13,
                                  color: '#dc2626',
                                  backgroundColor: '#fef2f2',
                                  padding: 8,
                                  borderRadius: 4,
                                  borderLeft: '3px solid #dc2626'
                                }}>
                                  Reason: {reply.moderationReason}
                                </p>
                              )}
                            </div>
                          ) : (
                            <LinkifiedText
                              as="p"
                              className="topic-content"
                              style={{ marginBottom: '12px', fontSize: '13px' }}
                              text={reply.content}
                            />
                          )}
                        </>
                      )}
                      <div className="comment-footer">
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          {(() => {
                            const replyDrawing = getCommentDrawing(reply.id);
                            if (replyDrawing) {
                              const initialLabel = reply.locationLabel || null;
                              const initialFullLabel = reply.locationFullLabel || reply.locationLabel || null;
                              return (
                                <>
                                  <GeometryLabel
                                    geometry={replyDrawing.geometry}
                                    drawingType={replyDrawing.type}
                                    fallback={initialLabel || 'Reply area'}
                                    full={false}
                                    initialLabel={initialLabel}
                                    initialFullLabel={initialFullLabel}
                                  />
                                  {renderLineEstimateSummary(replyDrawing)}
                                  {renderAreaEstimateSummary(replyDrawing)}
                                </>
                              );
                            } else if (reply.latitude && reply.longitude) {
                              return (
                                <GeoLabel
                                  lat={reply.latitude}
                                  lng={reply.longitude}
                                  initialText={reply.locationLabel || undefined}
                                  fallback={reply.locationLabel || 'Location'}
                                />
                              );
                            } else {
                              return null;
                            }
                          })()}
                          {editingCommentId !== reply.id && (
                            <div className="comment-actions-row">
                              <div className="comment-stats">
                                <VoteButton
                                  type="upvote"
                                  count={reply.upvotes}
                                  isActive={userVotes.comments[reply.id] === 'upvote'}
                                  onVote={() => onCommentVote(reply.id, 'upvote')}
                                />
                                <VoteButton
                                  type="downvote"
                                  count={reply.downvotes}
                                  isActive={userVotes.comments[reply.id] === 'downvote'}
                                  onVote={() => onCommentVote(reply.id, 'downvote')}
                                />
                              </div>
                              <div className="action-buttons">
                                {canEdit(reply.userId) && !editingCommentId && !editingTopicId && !reply.isDeleted && (
                                  <>
                                    <button
                                      type="button"
                                      className="icon-btn"
                                      title="Edit your comment"
                                      onClick={(e) => { e.stopPropagation(); startEditComment(reply); }}
                                    >
                                      <EditIcon size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      className="icon-btn"
                                      title="Delete your comment"
                                      onClick={(e) => { e.stopPropagation(); handleDelete('comment', reply.id); }}
                                      style={{ color: '#dc2626' }}
                                    >
                                      <DeleteIcon size={14} />
                                    </button>
                                  </>
                                )}
                                {isModerator && !canEdit(reply.userId) && !reply.isDeleted && (
                                  <button
                                    type="button"
                                    className="icon-btn"
                                    title="[MODERATOR] Delete this comment"
                                    onClick={(e) => { e.stopPropagation(); handleModeratorDelete('comment', reply.id); }}
                                    style={{
                                      color: '#dc2626',
                                      backgroundColor: '#fef2f2',
                                      border: '1px solid #fecaca',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    <DeleteIcon size={14} />
                                  </button>
                                )}
                                <SaveButton
                                  contentType="COMMENT"
                                  contentId={reply.id}
                                  currentUserId={currentUserId}
                                />
                                <button
                                  type="button"
                                  className="icon-btn"
                                  title="Copy link to comment"
                                  onClick={(e) => { e.stopPropagation(); onShareComment && onShareComment(reply.id); }}
                                >
                                  <ShareIcon size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="icon-btn"
                                  title="Report this comment"
                                  onClick={(e) => { e.stopPropagation(); onReportComment && onReportComment(reply.id); }}
                                >
                                  <FlagIcon size={14} />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 'auto', alignItems: 'flex-end', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {renderUserName(reply.user, 'comment-author-link')}
                          <span>{new Date(reply.createdAt).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {hasMoreTopLevelComments && (
              <div className="comment-load-more">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    setVisibleCommentCount((prev) => Math.min(prev + COMMENT_PAGE_SIZE, topLevelComments.length));
                  }}
                >
                  Show more comments ({topLevelComments.length - visibleCommentCount})
                </button>
              </div>
            )}

            {topicComments.length === 0 && (
              <div style={{ padding: '32px', textAlign: 'center', color: '#666' }}>
                <p>No comments yet.</p>
                <p style={{ fontSize: '12px', marginTop: '8px' }}>Be the first to comment!</p>
              </div>
            )}
          </div>

          {/* Add Comment Button or Form - gated by auth */}
          {!currentUserId ? (
            <div className="create-button-container" style={{ paddingTop: 0 }}>
              <div
                style={{
                  width: '100%',
                  borderTop: '1px solid #eee',
                  padding: '12px 0 0',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 8,
                  fontSize: 13,
                  color: '#555'
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  Log in to add a comment.
                </div>
                <a
                  href={`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                  className="create-button"
                  style={{ textAlign: 'center' }}
                >
                  Log in / Sign up
                </a>
              </div>
            </div>
          ) : userRole === 'READER' ? (
            <div className="create-button-container" style={{ paddingTop: 0 }}>
              <div
                style={{
                  width: '100%',
                  borderTop: '1px solid #eee',
                  padding: '12px 0 0',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 8,
                  fontSize: 13,
                  color: '#b91c1c'
                }}
              >
                <div style={{ textAlign: 'center', fontWeight: 600 }}>
                  Your account is read-only right now. Comments are disabled.
                </div>
              </div>
            </div>
          ) : (!isCreatingComment && !isEditingShape && !editingCommentId && !editingTopicId) ? (
            <div className="create-button-container">
              <button
                className="create-button"
                onClick={() => {
                  // Only prompt if there are actual unsaved changes
                  if (onConfirmIfDirty && !onConfirmIfDirty()) return;
                  onPrepareNewComment && onPrepareNewComment();
                  onCreateComment && onCreateComment();
                }}
                disabled={Boolean(editingTopicId || editingCommentId || isEditingShape || replyingToId || savingComment)}
                title={editingTopicId || editingCommentId || isEditingShape || replyingToId ? 'Please finish editing or replying before adding a comment' : 'Add a new comment'}
              >
                <MessagePlusIcon size={18} />
                {savingComment ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span className="spinner" style={{ width: 14, height: 14, border: '2px solid currentColor', borderRightColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin .8s linear infinite' }} />
                    Posting…
                  </span>
                ) : 'Add Comment'}
              </button>
            </div>
          ) : (isCreatingComment && !editingCommentId && !editingTopicId && !isEditingShape) ? (() => {
            const draftGeometry = getDraftGeometry?.({ silent: true }) ?? null;
            const draftType = draftGeometry?.type ? String(draftGeometry.type).toLowerCase() : null;
            const shouldUsePointCoords = !draftType || draftType === 'point';
            return (
              <div className="create-button-container" style={{ display: 'block', padding: '16px 0' }}>
                <div className="comment-form">
                  {activeCommentDraft && (
                    <div style={{ background: '#ecfdf3', color: '#166534', padding: '8px 10px', borderRadius: 8, marginBottom: 10, fontSize: 13 }}>
                      Draft loaded for this topic. You can edit and post or delete it from your profile.
                    </div>
                  )}
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label htmlFor="comment-content" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                      Your Comment
                    </label>
                    <textarea
                      id="comment-content"
                      value={newCommentContent}
                      onChange={(e) => setNewCommentContent(e.target.value)}
                      placeholder="Share your thoughts about this topic..."
                      rows={8}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '16px',
                        resize: 'vertical',
                        minHeight: '220px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <>
                    <div style={{ marginBottom: '12px', fontSize: '13px', color: '#666' }}>
                      {commentLocation ? (
                        <span style={{ color: '#059669' }}>
                          ✅ Location selected: {' '}
                          <GeoLabel
                            lat={shouldUsePointCoords ? commentLocation.lat : undefined}
                            lng={shouldUsePointCoords ? commentLocation.lng : undefined}
                            icon={null}
                            full
                            fallback={`${commentLocation.lat.toFixed(4)}, ${commentLocation.lng.toFixed(4)}`}
                            drawing={editingDrawingData && !editingDrawingData.commentId ? editingDrawingData : undefined}
                            geometry={draftGeometry ?? undefined}
                          />
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>
                          📍 Use tools below to add a point, line, or area (optional)
                        </span>
                      )}
                    </div>

                    {/* Drawing bar for comment creation */}
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <button
                          type="button"
                          onClick={() => onSetDrawingMode && onSetDrawingMode('marker')}
                          className={`draw-btn ${drawingMode === 'marker' ? 'active marker' : ''}`}
                        ><PointIcon size={12} /> Point</button>
                        <button
                          type="button"
                          onClick={() => onSetDrawingMode && onSetDrawingMode('line')}
                          className={`draw-btn ${drawingMode === 'line' ? 'active line' : ''}`}
                        ><LineIcon size={12} /> Line</button>
                        <button
                          type="button"
                          onClick={() => onSetDrawingMode && onSetDrawingMode('polygon')}
                          className={`draw-btn ${drawingMode === 'polygon' ? 'active polygon' : ''}`}
                        ><PolygonIcon size={12} /> Area</button>
                        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                          <button type="button" onClick={onUndoPoint} className="btn btn-ghost">⤺ Undo</button>
                          <button type="button" onClick={onClearPath} className="btn btn-ghost">✕ Clear</button>
                        </div>
                      </div>

                      {/* Finish below (optional) */}
                      {drawingMode !== 'none' && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button type="button" onClick={onFinishDrawing} className="btn btn-primary" >✓ Finish</button>
                        </div>
                      )}

                      {renderLineEstimateControls('comment')}
                      {renderAreaEstimateControls('comment')}
                    </div>
                  </>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={handleCancelNewComment}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={handleSaveCommentDraftClick}
                    >
                      Save Draft
                    </button>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6b7280', cursor: 'pointer', marginRight: 'auto' }}>
                      <input
                        type="checkbox"
                        checked={isAnonymousComment}
                        onChange={(e) => setIsAnonymousComment(e.target.checked)}
                        style={{ width: 15, height: 15, cursor: 'pointer' }}
                      />
                      Comment anonymously
                    </label>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={!newCommentContent.trim() || savingComment}
                      onClick={async () => {
                        if (onSubmitComment && newCommentContent.trim()) {
                          // Check if user has an unfinished drawing in progress
                          const hasUnfinishedDrawing = (() => {
                            if (drawingMode === 'line' || drawingMode === 'polygon') {
                              if (hasPendingDrawing) return false; // Already finished
                              // Check if there are pathPoints in progress but not enough to finish
                              if (hasPathPointsInProgress) {
                                return hasPathPointsInProgress();
                              }
                              return !hasPendingDrawing; // Fallback to previous logic
                            }
                            return false;
                          })();

                          if (hasUnfinishedDrawing && onFinishDrawing) {
                            const shouldFinish = window.confirm(
                              `You have an unfinished ${drawingMode} drawing. Would you like to:\n\n` +
                              `• Click "OK" to automatically finish your drawing and post\n` +
                              `• Click "Cancel" to go back and finish it manually`
                            );
                            if (!shouldFinish) return;

                            // Try to automatically finish the drawing
                            onFinishDrawing();

                            // Give a brief moment for the drawing state to update
                            await new Promise(resolve => setTimeout(resolve, 100));
                          }

                          onSubmitComment({
                            content: newCommentContent.trim(),
                            latitude: commentLocation?.lat,
                            longitude: commentLocation?.lng,
                            isAnonymous: isAnonymousComment
                          });
                          setNewCommentContent('');
                          setIsAnonymousComment(false);
                        }
                      }}
                    >
                      {savingComment ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <span className="spinner" style={{ width: 14, height: 14, border: '2px solid currentColor', borderRightColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin .8s linear infinite' }} />
                          Posting…
                        </span>
                      ) : 'Post Comment'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })() : null}

        </div>
        {/* Messages Modal - only show if not in user menu */}
        {messagesOpen && userMenuContent !== 'messages' && (
          <MessagesModal open={messagesOpen} onClose={() => setMessagesOpen(false)} currentUserId={currentUserId} onViewProfile={handleViewProfileFromMessages} />
        )}
      </React.Fragment>
    );
  }

  // Inline Create Topic view when expanded and creating
  if (isPanelExpanded && isCreating) {
    return (
      <>
        <div className={panelClass}>
          <div className="panel-header">
            <div className="panel-title" style={{ justifyContent: 'space-between', width: '100%' }}>
              <button onClick={handleExitTopicCreate} className="back-button">
                <ArrowLeftIcon />
                <span>Back</span>
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: '8px' }}>
                <h1 style={{ fontSize: '16px', margin: 0 }}>Create Topic</h1>
                {loadedTopicDraftIdRef.current && (
                  <span style={{ fontSize: 12, color: '#059669', background: '#ecfdf3', padding: '4px 8px', borderRadius: 9999 }}>
                    Draft loaded
                  </span>
                )}
              </div>
              {currentUserId && (
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    type="button"
                    className={`search-toggle-btn ${isSearchDrawerOpen ? 'open' : ''} ${hasActiveSearchFilter ? 'has-active' : ''}`}
                    aria-expanded={isSearchDrawerOpen}
                    aria-label="Search data and filters"
                    title="Search app data and apply filters (not map address search)"
                    onClick={() => {
                      guardLeaveTopicCreate(() => {
                        openSearchDrawer();
                      });
                    }}
                  >
                    <SearchIcon size={14} />
                    <span className="search-toggle-text">Search</span>
                    {hasActiveSearchFilter && <span className="search-toggle-badge" aria-label="Active filters">●</span>}
                  </button>
                  <UserMenu
                    username={currentUser?.username}
                    email={currentUser?.email}
                    isModerator={currentUser?.role === 'MODERATOR'}
                    onOpenProfile={() => {
                      guardLeaveTopicCreate(() => {
                        onShowUserMenu?.(true);
                        messagesEntryFromProfileRef.current = false;
                        messagesOriginRef.current = 'inbox';
                        setUserMenuContent('profile');
                        setPanelProfileUserId(currentUser?.id || null);
                        onSetProfileUserId?.(currentUser?.id || null);
                        onExpandPanel?.();
                        setUserMenuEntry('profile-panel');
                      });
                    }}
                    onOpenMessages={() => {
                      guardLeaveTopicCreate(() => {
                        onShowUserMenu?.(true);
                        messagesEntryFromProfileRef.current = false;
                        messagesOriginRef.current = 'inbox';
                        setUserMenuContent('messages');
                        setUserMenuEntry('messages');
                      });
                    }}
                    onLogout={onLogout}
                    unreadMessages={unreadDMs}
                    onShowUserMenu={() => guardLeaveTopicCreate(() => onShowUserMenu?.(true))}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="topic-list" style={{ padding: '0 16px 16px' }}>
            <div className="form-group">
              <label htmlFor="new-title">Topic Title</label>
              <input
                id="new-title"
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g. Safer bike lane on Main St"
                maxLength={200}
                disabled={Boolean(editingTopicId || editingCommentId || isEditingShape)}
              />
              <small>{newTitle.length}/200 characters</small>
            </div>

            <div className="form-group">
              <label htmlFor="new-content">Description</label>
              <textarea
                id="new-content"
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder="Describe the topic, context, ideas..."
                rows={4}
                disabled={Boolean(editingTopicId || editingCommentId || isEditingShape)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="new-topic-basemap">Default Basemap</label>
              <select
                id="new-topic-basemap"
                value={selectedBasemap}
                onChange={(e) => onBasemapChange?.(e.target.value as any)}
                disabled={!onBasemapChange || Boolean(editingTopicId || isEditingShape)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  backgroundColor: '#fff',
                  fontSize: '14px',
                  cursor: (onBasemapChange && !Boolean(editingTopicId || isEditingShape)) ? 'pointer' : 'not-allowed'
                }}
              >
                {basemapOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
              <small style={{ marginTop: '4px', display: 'block' }}>Select the map style people will see first for this topic.</small>
            </div>

            <div className="form-group">
              <label htmlFor="topic-visibility">Visibility</label>
              <select
                id="topic-visibility"
                value={topicVisibility}
                onChange={(e) => setTopicVisibility(e.target.value as TopicVisibility)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  backgroundColor: '#fff',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <option value="PUBLIC">Public – anyone can view</option>
                <option value="PRIVATE_SELF">Only me</option>
                <option value="FOLLOWERS">All my followers</option>
                <option value="MUTUALS">Mutual followers</option>
                <option value="CUSTOM">Specific people…</option>
              </select>
              <small style={{ marginTop: '4px', display: 'block' }}>
                Control who can see this topic after it’s created.
              </small>
            </div>

            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                id="topic-nsfw"
                type="checkbox"
                checked={isNsfw}
                onChange={(e) => setIsNsfw(e.target.checked)}
                disabled={Boolean(editingTopicId || editingCommentId || isEditingShape)}
                style={{ width: 18, height: 18, cursor: (editingTopicId || editingCommentId || isEditingShape) ? 'not-allowed' : 'pointer' }}
              />
              <label htmlFor="topic-nsfw" style={{ margin: 0 }}>
                <div style={{ fontWeight: 600 }}>Mark as NSFW / sensitive</div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>Hide previews and warn readers that this topic may contain sensitive content.</div>
              </label>
            </div>

            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                id="topic-anonymous"
                type="checkbox"
                checked={isAnonymousPost}
                onChange={(e) => setIsAnonymousPost(e.target.checked)}
                disabled={Boolean(editingTopicId || editingCommentId || isEditingShape)}
                style={{ width: 18, height: 18, cursor: (editingTopicId || editingCommentId || isEditingShape) ? 'not-allowed' : 'pointer' }}
              />
              <label htmlFor="topic-anonymous" style={{ margin: 0 }}>
                <div style={{ fontWeight: 600 }}>Post anonymously</div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>Your name will not be shown with this topic and it will not appear on your public profile.</div>
              </label>
            </div>

            {userRole === 'MODERATOR' && (
              <>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    id="topic-editorial"
                    type="checkbox"
                    checked={isEditorial}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setIsEditorial(next);
                      if (!next) clearEditorialImport();
                    }}
                    disabled={Boolean(editingTopicId || editingCommentId || isEditingShape)}
                    style={{ width: 18, height: 18, cursor: (editingTopicId || editingCommentId || isEditingShape) ? 'not-allowed' : 'pointer' }}
                  />
                  <label htmlFor="topic-editorial" style={{ margin: 0 }}>
                    <div style={{ fontWeight: 600 }}>Curated map</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                      Highlighted topic curated by moderators.
                    </div>
                  </label>
                </div>

                {isEditorial && (
                  renderEditorialImportPanel({ inputId: 'editorial-upload' })
                )}
              </>
            )}

            {topicVisibility === 'CUSTOM' && (
              <div className="form-group" style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px' }}>
                <label htmlFor="audience-search" style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>Share with specific users</label>
                <input
                  id="audience-search"
                  type="text"
                  value={audienceSearchTerm}
                  onChange={(e) => setAudienceSearchTerm(e.target.value)}
                  placeholder="Search by username…"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    marginBottom: 8
                  }}
                />
                {audienceSearchTerm.trim().length >= 2 && (
                  <div style={{
                    maxHeight: 160,
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    marginBottom: 8
                  }}>
                    {isAudienceSearching ? (
                      <div style={{ padding: '8px 12px', fontSize: 13, color: '#6b7280' }}>Searching…</div>
                    ) : audienceSearchResults.length === 0 ? (
                      <div style={{ padding: '8px 12px', fontSize: 13, color: '#6b7280' }}>No users found.</div>
                    ) : (
                      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {audienceSearchResults.map((user) => (
                          <li key={user.id}>
                            <button
                              type="button"
                              onClick={() => handleAddAudienceUser({ id: user.id, username: user.username || null, email: undefined })}
                              style={{
                                width: '100%',
                                padding: '8px 10px',
                                border: 'none',
                                background: 'white',
                                textAlign: 'left',
                                borderBottom: '1px solid #f1f5f9',
                                cursor: customAudienceIdSet.has(user.id) ? 'not-allowed' : 'pointer',
                                color: customAudienceIdSet.has(user.id) ? '#9ca3af' : '#1f2937'
                              }}
                              disabled={customAudienceIdSet.has(user.id)}
                            >
                              <div style={{ fontWeight: 600 }}>{user.username || user.id}</div>
                              <div style={{ fontSize: 12, color: '#6b7280' }}>Joined {new Date(user.createdAt).toLocaleDateString()}</div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                <div>
                  {customAudience.length === 0 ? (
                    <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>No people selected yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {customAudience.map((user) => {
                        const isFollower = followerIdSet.has(user.id);
                        return (
                          <div
                            key={user.id}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 8,
                              borderRadius: 9999,
                              border: '1px solid ' + (isFollower ? '#d1d5db' : '#f87171'),
                              padding: '4px 10px',
                              backgroundColor: isFollower ? '#f8fafc' : '#fef2f2'
                            }}
                          >
                            <span style={{ fontSize: 13 }}>{formatAudienceUser(user)}</span>
                            {!isFollower && (
                              <span style={{ fontSize: 11, color: '#b91c1c' }}>Not following you</span>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveAudienceUser(user.id)}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                color: '#6b7280',
                                fontWeight: 600
                              }}
                              aria-label={`Remove ${formatAudienceUser(user)}`}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <small style={{ display: 'block', marginTop: 8, color: '#6b7280' }}>
                  People highlighted in red do not currently follow you. They’ll still be added, but we’ll warn you so you can double-check.
                </small>
              </div>
            )}

            <div className="location-info">
              <h4>📍 Location / Geometry</h4>
              {resolvedTopicLocation ? (
                <p className="location-selected">
                  ✅ Location: {resolvedTopicLocation.lat.toFixed(4)}, {resolvedTopicLocation.lng.toFixed(4)}
                  {isEditorialLocationAuto && <span> (from editorial data)</span>}
                </p>
              ) : (
                <p className="location-missing">
                  Use the tools below to choose a point, draw a line, or outline an area, then click on the map.
                </p>
              )}

              {/* Drawing bar (panel-driven) */}
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => onSetDrawingMode && onSetDrawingMode('marker')}
                    className="draw-btn"
                    style={{
                      padding: '6px 10px',
                      borderRadius: 6,
                      border: '1px solid',
                      borderColor: drawingMode === 'marker' ? '#3b82f6' : '#e5e7eb',
                      backgroundColor: drawingMode === 'marker' ? '#3b82f6' : 'white',
                      color: drawingMode === 'marker' ? 'white' : '#374151'
                    }}
                  >
                    <PointIcon size={12} /> Point
                  </button>
                  <button
                    type="button"
                    onClick={() => onSetDrawingMode && onSetDrawingMode('line')}
                    className="draw-btn"
                    style={{
                      padding: '6px 10px',
                      borderRadius: 6,
                      border: '1px solid',
                      borderColor: drawingMode === 'line' ? '#2563eb' : '#e5e7eb',
                      backgroundColor: drawingMode === 'line' ? '#2563eb' : 'white',
                      color: drawingMode === 'line' ? 'white' : '#374151'
                    }}
                  >
                    <LineIcon size={12} /> Line
                  </button>
                  <button
                    type="button"
                    onClick={() => onSetDrawingMode && onSetDrawingMode('polygon')}
                    className="draw-btn"
                    style={{
                      padding: '6px 10px',
                      borderRadius: 6,
                      border: '1px solid',
                      borderColor: drawingMode === 'polygon' ? '#059669' : '#e5e7eb',
                      backgroundColor: drawingMode === 'polygon' ? '#059669' : 'white',
                      color: drawingMode === 'polygon' ? 'white' : '#374151'
                    }}
                  >
                    <PolygonIcon size={12} /> Area
                  </button>
                  {/* Inline Undo/Clear next to shape buttons */}
                  <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                    <button type="button" onClick={onUndoPoint} className="btn btn-ghost">⤺ Undo</button>
                    <button type="button" onClick={onClearPath} className="btn btn-ghost">✕ Clear</button>
                  </div>
                </div>

                {/* Finish below (optional) */}
                {drawingMode !== 'none' && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button type="button" onClick={onFinishDrawing} className="btn btn-primary" >✓ Finish</button>
                  </div>
                )}

                {renderLineEstimateControls('topic')}
                {renderAreaEstimateControls('topic')}

                {hasPendingDrawing && (
                  <div style={{ marginTop: 8, color: '#059669', fontSize: 12 }}>
                    {isEditorial && editorialManualDisplayCount > 0
                      ? `✅ ${editorialManualDisplayCount} shape${editorialManualDisplayCount === 1 ? '' : 's'} added. You can add more.`
                      : '✅ Geometry added. Choose a different type to replace.'}
                  </div>
                )}
              </div>
            </div>

            <div className="form-actions" style={{ gap: 8 }}>
              <button type="button" onClick={handleExitTopicCreate} className="btn btn-ghost">Cancel</button>
              <button type="button" onClick={handleSaveTopicDraftClick} className="btn btn-ghost">Save Draft</button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={savingTopic || !newTitle.trim() || !newContent.trim() || !resolvedTopicLocation}
                onClick={async () => {
                  if (!onSubmitCreate) return;

                  // Check if user has an unfinished drawing in progress
                  const hasUnfinishedDrawing = (() => {
                    if (drawingMode === 'line' || drawingMode === 'polygon') {
                      if (hasPendingDrawing) return false; // Already finished
                      // Check if there are pathPoints in progress but not enough to finish
                      if (hasPathPointsInProgress) {
                        return hasPathPointsInProgress();
                      }
                      return !hasPendingDrawing; // Fallback to previous logic
                    }
                    return false;
                  })();

                  if (hasUnfinishedDrawing && onFinishDrawing) {
                    const shouldFinish = window.confirm(
                      `You have an unfinished ${drawingMode} drawing. Would you like to:\n\n` +
                      `• Click "OK" to automatically finish your drawing and create topic\n` +
                      `• Click "Cancel" to go back and finish it manually`
                    );
                    if (!shouldFinish) return;

                    // Try to automatically finish the drawing
                    onFinishDrawing();

                    // Give a brief moment for the drawing state to update
                    await new Promise(resolve => setTimeout(resolve, 100));
                  }

                  const effectiveLocation = selectedLocation ?? editorialLocation;
                  if (!effectiveLocation) {
                    notify({ type: 'warning', message: 'Add a location or upload map data before creating this topic.' });
                    return;
                  }

                  if (topicVisibility === 'CUSTOM' && customAudience.length === 0) {
                    notify({ type: 'warning', message: 'Select at least one person to share with when choosing specific people.' });
                    return;
                  }

                  try {
                    await onSubmitCreate({
                      title: newTitle.trim(),
                      content: newContent.trim(),
                      basemap: selectedBasemap,
                      location: {
                        latitude: effectiveLocation.lat,
                        longitude: effectiveLocation.lng
                      },
                      language: 'en',
                      visibility: topicVisibility,
                      audienceUserIds: topicVisibility === 'CUSTOM' ? customAudience.map(user => user.id) : undefined,
                      isNsfw,
                      isEditorial,
                      isAnonymous: isAnonymousPost,
                      editorialDrawings: isEditorial ? editorialDrawings : undefined
                    });
                    setNewTitle('');
                    setNewContent('');
                    setCustomAudience([]);
                    setAudienceSearchTerm('');
                    setIsNsfw(false);
                    setIsAnonymousPost(false);
                    setIsEditorial(false);
                    clearEditorialImport();
                  } catch (err) {
                    if (err instanceof Error) {
                      notify({ type: 'error', message: err.message });
                    } else {
                      notify({ type: 'error', message: 'Failed to create topic.' });
                    }
                  }
                }}
              >
                {savingTopic ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span className="spinner" style={{ width: 14, height: 14, border: '2px solid currentColor', borderRightColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin .8s linear infinite' }} />
                    Creating…
                  </span>
                ) : 'Create Topic'}
              </button>
            </div>
          </div>
        </div>
        {/* Messages Modal - only show if not in user menu */}
        {messagesOpen && userMenuContent !== 'messages' && (
          <MessagesModal open={messagesOpen} onClose={() => setMessagesOpen(false)} currentUserId={currentUserId} onViewProfile={handleViewProfileFromMessages} />
        )}
      </>
    );
  }

  // Show user menu panel view (only for authenticated users)
  if (showUserMenu && currentUserId) {
    const renderUserMenuHeader = (title: string, onBack: () => void) => (
      <div className="panel-header">
        <div className="panel-title" style={{ justifyContent: 'space-between', width: '100%' }}>
          <div className="panel-logo-title">
            <button type="button" className="logo-home-button" onClick={handleLogoClick} aria-label="Go to main page">
              <AppLogo size={28} />
              <span className="logo-home-text">LoConvo</span>
            </button>
            {title !== 'LoConvo' && <h1>{title}</h1>}
          </div>
          <button onClick={onBack} className="back-button">
            <ArrowLeftIcon />
            <span>Back</span>
          </button>
        </div>
      </div>
    );

    return (
      <div className={panelClass}>
        {userMenuContent === 'menu' && renderUserMenuHeader('LoConvo', () => {
          // If there's a selected topic, return to expanded view, otherwise collapse
          if (selectedTopic) {
            onExpandPanel?.();
          } else {
            onCollapsePanel?.();
          }
          // Close user menu after panel state is set
          setTimeout(() => {
            onShowUserMenu?.(false);
          }, 0);
        })}
        {userMenuContent !== 'menu' && renderUserMenuHeader(
          userMenuContent === 'profile' ? 'Profile' :
            userMenuContent === 'messages' ? 'Messages' :
              userMenuContent === 'notifications' ? 'Notifications' : 'Menu',
          () => {
            // Messages back: prefer returning to the originating profile
                if (userMenuContent === 'messages') {
              const me = currentUserId ?? currentUser?.id ?? null;
              const panelProfileCandidate =
                panelProfileUserId && panelProfileUserId !== me ? panelProfileUserId : null;
              const effectiveProfileCandidate =
                effectiveProfileUserId && effectiveProfileUserId !== me ? effectiveProfileUserId : null;
              const candidates = [
                messagesReturnProfileIdRef.current,
                messageTargetUserId,
                lastActiveMessagePeerIdRef.current,
                lastMessagingProfileUserIdRef.current,
                getLastMessagePeerFromStorage(),
                readProfileOrigin(),
                panelProfileCandidate,
                effectiveProfileCandidate
              ].filter((id): id is string => Boolean(id && id !== me));

              const fromProfileIntent =
                userMenuEntry === 'messages-from-profile' ||
                messagesEntryFromProfileRef.current ||
                messagesOriginRef.current === 'profile' ||
                Boolean(messagesReturnProfileIdRef.current || panelProfileCandidate || effectiveProfileCandidate);
              const returnProfileId = candidates[0] || null;
              const fallbackProfileId = fromProfileIntent && !returnProfileId
                ? (panelProfileCandidate || effectiveProfileCandidate)
                : null;
              const targetProfileId = returnProfileId || fallbackProfileId || null;

              if (fromProfileIntent && targetProfileId) {
                const isSelfProfile = targetProfileId === me;
                setPanelProfileUserId(targetProfileId);
                onSetProfileUserId?.(targetProfileId);
                setUserMenuEntry(isSelfProfile ? 'profile-panel' : 'profile-external');
                setUserMenuContent('profile');
                messagesOriginRef.current = 'profile';
                if (!isSelfProfile) {
                  rememberProfileOrigin(targetProfileId);
                }
                onExpandPanel?.();
                return;
              }

              // Messages fallback
              messagesEntryFromProfileRef.current = false;
              messagesReturnProfileIdRef.current = null;
              setMessageTargetUserId(null);
              setMessagesInstanceKey((v) => v + 1);
              setUserMenuEntry('menu');
              setUserMenuContent('menu');
              messagesOriginRef.current = 'inbox';
              onExpandPanel?.();
              return;
            }
            // Handle all profile back navigation the same way if we have saved state
            if (userMenuContent === 'profile') {

              // If privacy settings modal is open, just close it and stay on profile
              if (showAccessRightsModal) {

                setShowAccessRightsModal(null);
                return;
              }
              // Check entry type first before any other logic
              if (userMenuEntry === 'profile-from-messages') {

                const target = profileReturnContextRef.current?.userId || messageTargetUserId;
                if (target) {
                  setMessageTargetUserId(target);
                }
                profileReturnContextRef.current = null;
                try { sessionStorage.removeItem('returnToProfile'); } catch { }
                setPanelProfileUserId(null);
                onSetProfileUserId?.(null);
                setUserMenuContent('messages');
                setUserMenuEntry('messages-from-profile');
                onExpandPanel?.();
                return;
              }
              if (!panelProfileUserId && !effectiveProfileUserId) {
                const storedReturn = sessionStorage.getItem('returnToProfile');
                if (storedReturn) {
                  try {
                    const profileState = JSON.parse(storedReturn);
                    sessionStorage.removeItem('returnToProfile');

                    // Handle messages case specifically
                    if (profileState?.entry === 'profile-from-messages') {

                      const target = profileState?.messageUserId || profileState?.userId;
                      if (target) {
                        setMessageTargetUserId(target);
                      }
                      profileReturnContextRef.current = null;
                      setPanelProfileUserId(null);
                      onSetProfileUserId?.(null);
                      setUserMenuContent('messages');
                      setUserMenuEntry('messages-from-profile');
                      onExpandPanel?.();
                      onShowUserMenu?.(true);
                      setTimeout(() => {

                      }, 100);
                      return;
                    }

                    const returnUserId: string | null = profileState?.userId || null;
                    const returnTab = profileState?.tab as ('topics' | 'comments' | 'notifications' | 'moderation' | 'mod-activities' | 'saved' | undefined);
                    if (returnTab) {
                      setProfileInitialTab(returnTab);
                      onProfileTabChange?.(returnTab);
                    }
                    if (returnUserId && returnUserId !== currentUserId) {
                      setPanelProfileUserId(returnUserId);
                      onSetProfileUserId?.(returnUserId);
                      setUserMenuEntry('profile-external');
                    } else {
                      setPanelProfileUserId(null);
                      onSetProfileUserId?.(null);
                      setUserMenuEntry('profile-panel');
                    }
                    return;
                  } catch (err) {
                    sessionStorage.removeItem('returnToProfile');
                  }
                }
              }
              if (shouldReturnToSearch) {

                setShouldReturnToSearch(false);
                clearProfileView({ collapse: false, closeMenu: true });
                onBackToSearch?.();
                return;
              }
              if (userMenuEntry === 'profile-panel') {

                // Go back to menu list and collapse the panel
                clearProfileView({ collapse: true });
                return;
              }
              // If we saved the expanded state, restore it (direct back to topic)
              if (wasExpandedBeforeProfile) {

                clearProfileView({ collapse: false, closeMenu: true });
                // If we have a saved topic, restore it; otherwise just expand
                if (topicBeforeProfile) {
                  onTopicSelect(topicBeforeProfile);
                  setTopicBeforeProfile(null);
                } else {
                  onExpandPanel?.();
                }
                setWasExpandedBeforeProfile(false);
                return;
              }
              if (userMenuEntry === 'profile-external') {

                clearProfileView({ collapse: true });
                onBackToSearch?.();
                return;
              }
            }
            // Default fallback: collapse menu

            clearProfileView({ collapse: true });
          }
        )}

        {/* User Menu Content */}
        <div className="panel-content" style={{ padding: '20px 16px' }}>
            {userMenuContent === 'menu' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* User Info Header */}
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>
                  Signed in as
                </div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                  {currentUser?.username || currentUser?.email || 'Account'}
                </div>
              </div>

              {/* Menu Items */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button
                  className="menu-item"
                  onClick={() => {

                    // Switch to profile view within the user menu
                    if (currentUser?.id && onSetProfileUserId) {

                      onSetProfileUserId(currentUser.id);
                      setUserMenuContent('profile');
                      setPanelProfileUserId(currentUser.id);
                      setUserMenuEntry('profile-panel');
                      onExpandPanel?.(); // Expand the panel to show the profile
                      // Keep the user menu open to show the profile
                    } else {

                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textAlign: 'left',
                    width: '100%'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <span>View My Profile</span>
                </button>

                <a
                  href="/settings"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textAlign: 'left',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    textDecoration: 'none',
                    color: 'inherit'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                  </svg>
                  <span>Account Settings</span>
                </a>

                <button
                  className="menu-item"
                  onClick={() => {
                    pendingMessageTargetRef.current = null;
                    try { sessionStorage.removeItem('messages:pendingTarget'); } catch { }
                    setUserMenuContent('messages');
                    messagesEntryFromProfileRef.current = false;
                    messagesOriginRef.current = 'inbox';
                    onExpandPanel?.();
                    setUserMenuEntry('messages');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textAlign: 'left',
                    width: '100%',
                    justifyContent: 'space-between'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                    <span>Messages</span>
                  </div>
                  {unreadDMs > 0 && (
                    <span style={{
                      background: '#ef4444',
                      color: 'white',
                      borderRadius: '9999px',
                      fontSize: '11px',
                      padding: '2px 6px',
                      minWidth: '18px',
                      textAlign: 'center'
                    }}>
                      {unreadDMs}
                    </span>
                  )}
                </button>

                <button
                  className="menu-item"
                  onClick={() => {
                    // Route to Profile view with Notifications tab
                    setProfileInitialTab('notifications');
                    onProfileTabChange?.('notifications');
                    setUserMenuContent('profile');
                    setPanelProfileUserId(null);
                    onSetProfileUserId?.(null);
                    onExpandPanel?.();
                    setUserMenuEntry('profile-panel');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textAlign: 'left',
                    width: '100%',
                    justifyContent: 'space-between'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                      <path d="M13.73 21a2 2 0 01-3.46 0" />
                    </svg>
                    <span>Notifications</span>
                    {persistentUnreadCount > 0 ? (
                      <span style={{ marginLeft: 'auto', background: '#ef4444', color: 'white', borderRadius: '9999px', fontSize: '11px', padding: '2px 6px', minWidth: '18px', textAlign: 'center' }}>{persistentUnreadCount}</span>
                    ) : null}
                  </div>
                </button>

                {currentUser?.role === 'MODERATOR' && (
                  <button
                    className="menu-item"
                    onClick={() => {
                      setProfileInitialTab('moderation');
                      onProfileTabChange?.('moderation');
                      setUserMenuContent('profile');
                      setPanelProfileUserId(null);
                      onSetProfileUserId?.(null);
                      onExpandPanel?.();
                      setUserMenuEntry('profile-panel');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      textAlign: 'left',
                      width: '100%',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <circle cx="12" cy="12" r="9" />
                        <polyline points="12 7 12 12 15 15" />
                      </svg>
                      <span>Moderation History</span>
                    </div>
                  </button>
                )}

                {currentUser?.role === 'MODERATOR' && (
                  <a
                    className="menu-item"
                    href="/admin"
                    onClick={() => { onShowUserMenu?.(false); setPendingReports(0); }}
                    style={{
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      textAlign: 'left',
                      width: '100%',
                      color: 'inherit',
                      boxSizing: 'border-box'
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M12 3 19 7v5c0 5-3.5 7.5-7 9-3.5-1.5-7-4-7-9V7l7-4z" />
                      <path d="m9 12 2 2 4-4" />
                    </svg>
                    <span>Admin Panel</span>
                    {pendingReports > 0 && (
                      <span style={{
                        background: '#dc2626',
                        color: '#fff',
                        borderRadius: '9999px',
                        fontSize: '11px',
                        padding: '2px 6px',
                        minWidth: '18px',
                        textAlign: 'center',
                        marginLeft: 'auto'
                      }} title={`${pendingReports} pending reports`}>
                        {pendingReports}
                      </span>
                    )}
                  </a>
                )}
              </div>

              {/* Divider */}
              <div style={{ height: '1px', backgroundColor: '#e2e8f0', margin: '8px 0' }}></div>

              {/* Sign Out Button */}
              <button
                className="menu-item"
                onClick={() => {
                  onShowUserMenu?.(false);
                  onLogout?.();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  backgroundColor: 'white',
                  border: '1px solid #ef4444',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  textAlign: 'left',
                  width: '100%',
                  color: '#dc2626'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>Sign Out</span>
              </button>
            </div>
          )}

          {userMenuContent === 'profile' && (
            <ProfileContentPanel
              currentUser={currentUser}
              profileUserId={panelProfileUserId ?? effectiveProfileUserId ?? null}
              userMenuEntry={['profile-panel', 'profile-menu', 'profile-external', 'profile-from-messages'].includes(userMenuEntry)
                ? (userMenuEntry as 'profile-panel' | 'profile-menu' | 'profile-external' | 'profile-from-messages')
                : undefined}
              onMessageUser={(userId) => {
                pendingMessageTargetRef.current = userId;
                lastMessagingProfileUserIdRef.current = userId;
                messagesReturnProfileIdRef.current = userId;
                messagesEntryFromProfileRef.current = true;
                messagesOriginRef.current = 'profile';
                rememberProfileOrigin(userId);
                try {
                  sessionStorage.setItem('messages:pendingTarget', JSON.stringify({ userId, ts: Date.now() }));
                  sessionStorage.setItem('messages:lastPeerId', userId);
                } catch { }
                setMessageTargetUserId(userId);
                setUserMenuContent('messages');
                setUserMenuEntry('messages-from-profile');
              }}
              onOpenMessages={() => {
                pendingMessageTargetRef.current = null;
                try { sessionStorage.removeItem('messages:pendingTarget'); } catch { }
                setMessageTargetUserId(null);
                messagesEntryFromProfileRef.current = true;
                messagesOriginRef.current = 'profile';
                setUserMenuContent('messages');
                setUserMenuEntry('messages-from-profile');
                onExpandPanel?.();
                onShowUserMenu?.(true);
              }}
              initialTab={resolvedProfileTab ?? undefined}
              topics={topics}
              drafts={drafts}
              onTopicSelect={onTopicSelect}
              onCommentSelect={onCommentSelect}
              onCommentSelectById={onCommentSelectById}
              onSetProfileUserId={(userId) => {
                setPanelProfileUserId(userId);
              }}
              onSetUserMenuEntry={(entry) => {
                setUserMenuEntry(entry);
              }}
              showAccessRightsModal={showAccessRightsModal}
              onSetShowAccessRightsModal={setShowAccessRightsModal}
              onActiveTabChange={onProfileTabChange}
              onResumeDraft={onResumeDraft}
              onDeleteTopicDraft={onDeleteTopicDraft}
              onDeleteCommentDraft={onDeleteCommentDraft}
              onCloseProfile={() => {
                const returnContext = profileReturnContextRef.current;
                profileReturnContextRef.current = null;

                // Handle profile-from-messages restoration
                if (userMenuEntry === 'profile-from-messages' || (returnContext && returnContext.mode === 'messages')) {
                  const target = returnContext?.userId || messageTargetUserId;
                  if (target) {
                    setMessageTargetUserId(target);
                  }
                  setPanelProfileUserId(null);
                  onSetProfileUserId?.(null);
                  if (returnContext && returnContext.overlay) {
                    setMessagesOpen(true);
                    onShowUserMenu?.(false);
                  } else {
                    setMessagesOpen(false);
                    setUserMenuContent('messages');
                    setUserMenuEntry('messages-from-profile');
                    onExpandPanel?.();
                    onShowUserMenu?.(true);
                  }
                  return;
                }

                if (shouldReturnToSearch) {
                  setShouldReturnToSearch(false);
                  setPanelProfileUserId(null);
                  onSetProfileUserId?.(null);
                  onBackToSearch?.();
                  onShowUserMenu?.(false);
                } else if (topicBeforeProfile) {
                  // Restore the topic we were viewing before opening profile
                  setPanelProfileUserId(null);
                  onSetProfileUserId?.(null);
                  onTopicSelect(topicBeforeProfile);
                  setTopicBeforeProfile(null);
                  onShowUserMenu?.(false);
                } else {
                  setPanelProfileUserId(null);
                  onSetProfileUserId?.(null);
                  setUserMenuContent('menu');
                  setUserMenuEntry('menu');
                  onShowUserMenu?.(false);
                }
              }}
            />
          )}

          {(() => {
            return userMenuContent === 'messages';
          })() && (
	              <div style={{ height: 'calc(100vh - 120px)', padding: 0 }}>
              <MessagesModal
                  key={`messages-inline-${messagesInstanceKey}-${messageTargetUserId || 'inbox'}`}
                  inline
                  open={true}
                  onClose={() => { }}
                  currentUserId={currentUserId}
                  initialTargetUserId={messageTargetUserId || undefined}
                  cameFromProfile={userMenuEntry === 'messages-from-profile' || messagesEntryFromProfileRef.current || messagesOriginRef.current === 'profile'}
                  onViewProfile={handleViewProfileFromMessages}
                  onActivePeerChange={(peerId) => {
                    lastActiveMessagePeerIdRef.current = peerId;
                    if (peerId) {
                      lastMessagingProfileUserIdRef.current = peerId;
                      try { sessionStorage.setItem('messages:lastPeerId', peerId); } catch { }
                      const me = currentUserId ?? currentUser?.id ?? null;
                      if (peerId !== me && (messagesEntryFromProfileRef.current || messagesOriginRef.current === 'profile')) {
                        messagesReturnProfileIdRef.current = peerId;
                      }
                    }
                  }}
                  onBackFromThread={() => {
                    // When back is pressed from a message thread that was opened from a profile,
                    // navigate back to that user's profile
                    const targetUserId = lastActiveMessagePeerIdRef.current
                      || messageTargetUserId
                      || messagesReturnProfileIdRef.current
                      || lastMessagingProfileUserIdRef.current;
                    if (targetUserId) {
                      // Reset message-related state before navigating
                      messagesEntryFromProfileRef.current = false;
                      messagesOriginRef.current = 'inbox';
                      setMessageTargetUserId(null);
                      // Navigate to profile
                      setPanelProfileUserId(targetUserId);
                      onSetProfileUserId?.(targetUserId);
                      setUserMenuContent('profile');
                      setUserMenuEntry('profile-external');
                    }
                  }}
                />
              </div>
            )}

          {userMenuContent === 'notifications' && (
            <div style={{ height: 'calc(100vh - 120px)', overflow: 'auto' }}>
              <div style={{ padding: '16px' }}>
                <div style={{ textAlign: 'center', color: '#6b7280', paddingTop: '40px' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ margin: '0 auto 16px', opacity: 0.5 }}>
                    <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 01-3.46 0" />
                  </svg>
                  <h3 style={{ margin: '0 0 8px', fontSize: '16px' }}>No new notifications</h3>
                  <p style={{ margin: '0', fontSize: '14px' }}>You're all caught up!</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show topic list (default view)
  return (
    <div className={panelClass}>
      {/* Header */}
      <div className="panel-header">
        <div className="panel-title" style={{ justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {isSearchDrawerOpen ? (
              <button
                type="button"
                className="back-button"
                onClick={handleCloseSearchDrawer}
                aria-label="Back"
              >
                <ArrowLeftIcon />
                <span>Back</span>
              </button>
            ) : (
              <div className="panel-logo-title">
                <button type="button" className="logo-home-button" onClick={handleLogoClick} aria-label="Go to main page">
                  <AppLogo size={32} />
                  <span className="logo-home-text">LoConvo</span>
                </button>
              </div>
            )}
          </div>

          {/* Account Menu + Search toggle */}
          <div className="panel-actions">
            <button
              type="button"
              className={`search-toggle-btn ${isSearchDrawerOpen ? 'open' : ''} ${hasActiveSearchFilter ? 'has-active' : ''}`}
              aria-expanded={isSearchDrawerOpen}
              aria-label="Search data and filters"
              title="Search app data and apply filters (not map address search)"
              onClick={() => {
                const next = !isSearchDrawerOpen;
                if (next) {
                  openSearchDrawer();
                } else {
                  handleCloseSearchDrawer();
                }
              }}
            >
              <SearchIcon size={14} />
              <span className="search-toggle-text">Search</span>
              {hasActiveSearchFilter && <span className="search-toggle-badge" aria-label="Active filters">●</span>}
            </button>

            {currentUserId && (
              <UserMenu
                username={currentUser?.username}
                email={currentUser?.email}
                isModerator={currentUser?.role === 'MODERATOR'}
                onOpenProfile={() => {
                  onShowUserMenu?.(true);
                  setUserMenuContent('profile');
                  setPanelProfileUserId(currentUser?.id || null); // Set to show own profile
                  onSetProfileUserId?.(currentUser?.id || null);
                  onExpandPanel?.();
                  setUserMenuEntry('profile-panel');
                }}
                onOpenMessages={() => {
                  onShowUserMenu?.(true);
                  setUserMenuContent('messages');
                  onExpandPanel?.();
                }}
                onLogout={onLogout}
                unreadMessages={unreadDMs}
                onShowUserMenu={() => onShowUserMenu?.(true)}
              />
            )}
          </div>

          {/* Unified Save Button for main view */}
          {(editingTopicId || editingCommentId || isEditingShape) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                className="btn btn-primary"
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  backgroundColor: '#10b981',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  saveEdit();
                }}
                title="Save all changes"
                disabled={savingEdit}
              >
                {savingEdit ? (
                  <>
                    <span className="spinner" style={{ width: 12, height: 12, border: '2px solid #fff', borderRightColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin .8s linear infinite' }} />
                    Saving…
                  </>
                ) : (
                  <>
                    <DiskIcon size={12} /> Save
                  </>
                )}
              </button>
              <button
                className="btn btn-ghost"
                style={{ padding: '8px 12px', fontSize: '14px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  cancelEdit();
                }}
                title="Cancel editing"
                disabled={savingEdit}
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Search + location filters (collapsed behind header control) */}
        {shouldShowSearchDrawer && (
          <div className={`search-drawer ${isSearchDrawerOpen ? 'open' : 'collapsed'}`}>
            <div className="global-search-wrapper">
              <div className="global-search-combo stacked">
                <div className="global-search-section text-search-section">
                  <div className="global-search-section-label text-search-label">Text search</div>
                  <div className="global-search-fieldset search-text-fieldset text-search-fieldset">
                    <div className={`global-search-input ${searchLoading ? 'loading' : ''}`}>
                      <SearchIcon size={14} className="global-search-leading-icon" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search users, topics, comments, places…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="global-search-field"
                        onFocus={() => {
                          if (suggestionHideTimeout.current) {
                            window.clearTimeout(suggestionHideTimeout.current);
                          }
                          setIsSearchFocused(true);
                        }}
                        onBlur={() => {
                          if (suggestionHideTimeout.current) {
                            window.clearTimeout(suggestionHideTimeout.current);
                          }
                          suggestionHideTimeout.current = window.setTimeout(() => setIsSearchFocused(false), 140);
                        }}
                        autoComplete="off"
                        aria-label="Search people, topics, comments"
                      />
                      {searchQuery.trim().length > 0 && (
                        <button
                          type="button"
                          className="global-search-clear"
                          aria-label="Clear search"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSearchQuery('');
                            setIsSearchFocused(true);
                            requestAnimationFrame(() => {
                              try { searchInputRef.current?.focus(); } catch { }
                            });
                          }}
                        >
                          ×
                        </button>
                      )}
                      {searchLoading && <span className="global-search-spinner">⏳</span>}
                    </div>
                    {isSearchFocused && searchSuggestions.length > 0 && searchQuery.trim().length > 0 && (
                      <div
                        className="global-search-suggestions"
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {searchSuggestions.map((suggestion) => (
                          <button
                            key={suggestion.id}
                            type="button"
                            className="global-search-suggestion"
                            onClick={() => handleSuggestionSelect(suggestion)}
                          >
                            <span className="suggestion-group">{suggestion.group.charAt(0).toUpperCase() + suggestion.group.slice(1)}</span>
                            <span className="suggestion-label">{highlightSearchText(suggestion.label)}</span>
                            {suggestion.description && (
                              <span className="suggestion-desc">{highlightSearchText(suggestion.description)}</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="global-search-section location-search-section">
                  <div className="global-search-section-label location-search-label">Location Filter</div>
                  <div className="global-search-fieldset location-search-fieldset">
                    {showSpatialPanel && !selectedTopic ? (
                      <div className="spatial-search-dropdown" ref={spatialFiltersRef}>
                        <div className="spatial-search-card">
                          <div className="spatial-search-head">
                            <div className="spatial-search-pill">
                              <PinIcon size={12} />
                              <span>Filter by Location</span>
                            </div>
                            <span className="spatial-search-status">{spatialStatusText}</span>
                            {spatialSearchCenter && (
                              <button
                                type="button"
                                className="spatial-search-reset"
                                onClick={() => onSpatialSearchCenterClear?.()}
                                title="Clear spatial filter"
                              >
                                Clear
                              </button>
                            )}
                          </div>
                          <div className="spatial-search-grid">
                            <div className="spatial-search-block">
                              <div className="spatial-search-block-label">1. Pick a center</div>
                              <button
                                type="button"
                                className={`btn btn-primary spatial-cta ${spatialSearchPicking ? 'is-picking' : ''}`}
                                onClick={() => onSpatialSearchPickRequest?.()}
                                disabled={!onSpatialSearchPickRequest || spatialSearchPicking}
                                title="Pick a point on the map to filter search results"
                              >
                                {spatialSearchPicking ? 'Waiting for map click…' : 'Select on Map'}
                              </button>
                              <p className="spatial-hint">
                                {spatialSearchPicking ? 'Tap on the map to set the center.' : 'Drop a point on the map to anchor results.'}
                              </p>
                            </div>
                            <div className="spatial-search-block">
                              <div className="spatial-search-block-label">2. Set radius</div>
                              <label className="spatial-search-radius">
                                <div className="spatial-radius-shell">
                                  <input
                                    type="number"
                                    min={0.1}
                                    max={500}
                                    step={0.5}
                                    value={spatialSearchRadiusKm}
                                    onChange={(e) => handleSpatialRadiusChange(parseFloat(e.target.value))}
                                    disabled={!onSpatialSearchRadiusChange}
                                  />
                                  <span className="spatial-radius-unit">km</span>
                                </div>
                              </label>
                              <p className="spatial-hint">Distance from your center to search within.</p>
                            </div>
                          </div>
                          <div className="spatial-search-foot">
                            {spatialSearchFilter
                              ? 'Adjust the radius or clear to reset location filtering.'
                              : 'Pick a center on the map, then choose a radius to see nearby results.'}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="global-search-filter global-search-filter-primary"
                        onClick={() => {
                          setShowSpatialFilters(true);
                          onExpandPanel?.();
                        }}
                        aria-haspopup="true"
                        aria-expanded={showSpatialFilters}
                        title="Filter topics by location in the app database"
                      >
                        <PinIcon size={14} /> Filter by location
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs - hide when search mode is open to keep panel focused on search */}
        {!selectedTopic && !isSearchMode && (
          <div className="tab-buttons">
            <button
              onClick={() => setActiveTab('popular')}
              className={`tab-button ${activeTab === 'popular' ? 'active' : ''}`}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <TrendingUpIcon size={14} /> <span className="tab-text">Popular</span>
              </span>
            </button>
            <button
              onClick={() => setActiveTab('live')}
              className={`tab-button ${activeTab === 'live' ? 'active' : ''}`}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <LiveDotIcon size={10} /> <span className="tab-text">Live</span>
              </span>
            </button>
            {Boolean(currentUserId) && (
              <button
                onClick={() => setActiveTab('private')}
                className={`tab-button ${activeTab === 'private' ? 'active' : ''}`}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <LockIcon size={14} /> <span className="tab-text">Private</span>
                </span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Shape editing toolbar (panel-based) */}
      {isEditingShape && (
        <div style={{
          padding: '12px',
          borderBottom: '2px solid #3b82f6',
          backgroundColor: '#f8fafc',
          display: 'flex',
          flexDirection: 'column',
          gap: 12
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#1e40af' }}>
                🎨 Editing {selectedComment ? 'Comment' : 'Topic'} Shape
              </span>
              <span style={{ fontSize: 12, color: '#64748b' }}>Click on map to modify shape</span>
            </div>
            <div style={{ fontSize: 12, color: '#666', fontStyle: 'italic' }}>
              💡 Use the Save button in the header to save all changes
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#374151' }}>Shape Type:</span>
            <button
              type="button"
              onClick={() => onSetDrawingMode && onSetDrawingMode('marker')}
              className={`draw-btn ${drawingMode === 'marker' ? 'active marker' : ''}`}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid',
                borderColor: drawingMode === 'marker' ? '#3b82f6' : '#d1d5db',
                backgroundColor: drawingMode === 'marker' ? '#3b82f6' : '#fff',
                color: drawingMode === 'marker' ? '#fff' : '#374151',
                fontSize: 12,
                fontWeight: 500
              }}
            >
              <PointIcon size={12} /> Point
            </button>
            <button
              type="button"
              onClick={() => onSetDrawingMode && onSetDrawingMode('line')}
              className={`draw-btn ${drawingMode === 'line' ? 'active line' : ''}`}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid',
                borderColor: drawingMode === 'line' ? '#2563eb' : '#d1d5db',
                backgroundColor: drawingMode === 'line' ? '#2563eb' : '#fff',
                color: drawingMode === 'line' ? '#fff' : '#374151',
                fontSize: 12,
                fontWeight: 500
              }}
            >
              <LineIcon size={12} /> Line
            </button>
            <button
              type="button"
              onClick={() => onSetDrawingMode && onSetDrawingMode('polygon')}
              className={`draw-btn ${drawingMode === 'polygon' ? 'active polygon' : ''}`}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid',
                borderColor: drawingMode === 'polygon' ? '#059669' : '#d1d5db',
                backgroundColor: drawingMode === 'polygon' ? '#059669' : '#fff',
                color: drawingMode === 'polygon' ? '#fff' : '#374151',
                fontSize: 12,
                fontWeight: 500
              }}
            >
              <PolygonIcon size={12} /> Area
            </button>
            {/* Edit existing shape button (panel) */}
            {(() => {
              const drawing = selectedComment?.id ? getCommentDrawing(selectedComment.id) : selectedTopic ? getTopicDrawing(selectedTopic.id) : null;
              const canEdit = drawing && currentUserId && String(currentUserId) === String(drawing.userId);
              const canStartEdit = !editingCommentId && !editingTopicId;
              if (!drawing || !canEdit || !canStartEdit) return null;
              return (
                <button type="button" onClick={() => onStartEditDrawing && onStartEditDrawing(drawing)} className="draw-btn" title="Edit existing shape" style={{ marginLeft: 6 }}>
                  ✏️ Edit shape
                </button>
              );
            })()}
          </div>

          {(drawingMode === 'line' || drawingMode === 'polygon') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 8, borderTop: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#374151' }}>Edit Tools:</span>
              <button type="button" onClick={onUndoPoint} className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>
                ⤺ Undo Point
              </button>
              <button type="button" onClick={onClearPath} className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>
                🗑️ Clear Drawing
              </button>
            </div>
          )}

          {renderLineEstimateControls('edit-toolbar')}
          {renderAreaEstimateControls('edit-toolbar')}

          <div style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
            <span>{'\uD83D\uDCA1 Tip: '}</span>
            <span>
              {drawingMode === 'marker' ? 'Click on map to move the point' :
                drawingMode === 'line' ? 'Click on map to add points to the line, or drag existing vertices. Use "Clear Drawing" to start over.' :
                  drawingMode === 'polygon' ? 'Click on map to add points to the area, or drag existing vertices. Use "Clear Drawing" to start over.' :
                    'Select a shape type above to start editing'}
            </span>
          </div>
        </div>
      )}

      <div className="panel-body" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Topic List or Search Results */}
        {isSearchMode ? (
          <div className="global-search-results">
            {spatialSearchFilter && (
              <div className="search-status" style={{ marginBottom: 6 }}>
                Filtering within {spatialSearchFilter.radiusKm.toFixed(1)} km of {spatialSearchFilter.lat.toFixed(3)}, {spatialSearchFilter.lng.toFixed(3)}.
                {spatialSearchPicking ? ' Click the map to pick a new center.' : ''}
              </div>
            )}
            {searchLoading && (
              <div className="search-status">Searching…</div>
            )}
            {!searchLoading && searchErrorMessage && (
              <div className="search-error">{searchErrorMessage}</div>
            )}
            {!searchLoading && !searchErrorMessage && (
              <>
                <div className="search-group">
                  <div className="search-group-header">
                    <h4>Topics</h4>
                    {topicResults.hasMore && (
                      <button
                        type="button"
                        className="search-more-btn"
                        onClick={() => handleLoadMore('topics')}
                        disabled={loadingGroups.topics}
                      >
                        {loadingGroups.topics ? 'Loading…' : 'More'}
                      </button>
                    )}
                  </div>
                  {topicResults.items.length === 0 ? (
                    <div className="search-empty">
                      {hasQuery
                        ? <>No topics matched “{searchQuery}”.</>
                        : hasSpatialFilter
                          ? 'No topics found in this area.'
                          : 'Start typing to search for topics.'}
                    </div>
                  ) : (
                    <ul className="search-group-list">
                      {topicResults.items.map((topic) => (
                        <li key={topic.id}>
                          <button type="button" className="search-result-item" onClick={() => handleTopicResultSelect(topic)}>
                            <span className="result-primary">{highlightSearchText(topic.title)}</span>
                            {formatDistance(topic.distanceKm) && (
                              <span className="result-secondary">{formatDistance(topic.distanceKm)}</span>
                            )}
                            {(() => {
                              const locationText = (topic.location || '').trim();
                              if (locationText) {
                                return <span className="result-secondary">{highlightSearchText(locationText)}</span>;
                              }
                              const drawing = searchTopicDrawings.get(topic.id) || null;
                              const baseTopic = searchTopicsById.get(topic.id);
                              const hasCoords = typeof baseTopic?.latitude === 'number' && typeof baseTopic?.longitude === 'number';
                              if (drawing || hasCoords) {
                                return (
                                  <GeoLabel
                                    className="result-secondary"
                                    lat={baseTopic?.latitude}
                                    lng={baseTopic?.longitude}
                                    initialText={baseTopic?.city || baseTopic?.region || baseTopic?.country || undefined}
                                    drawing={drawing}
                                    fallback={topic.excerpt || 'Location'}
                                    icon={null}
                                    style={{ display: 'inline', gap: 0 }}
                                  />
                                );
                              }
                              return topic.excerpt
                                ? <span className="result-secondary">{highlightSearchText(topic.excerpt)}</span>
                                : null;
                            })()}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="search-group">
                  <div className="search-group-header">
                    <h4>Comments</h4>
                    {commentResults.hasMore && (
                      <button
                        type="button"
                        className="search-more-btn"
                        onClick={() => handleLoadMore('comments')}
                        disabled={loadingGroups.comments}
                      >
                        {loadingGroups.comments ? 'Loading…' : 'More'}
                      </button>
                    )}
                  </div>
                  {commentResults.items.length === 0 ? (
                    <div className="search-empty">
                      {hasQuery
                        ? <>No comments matched “{searchQuery}”.</>
                        : hasSpatialFilter
                          ? 'No comments found in this area.'
                          : 'Enter a term to search for comments.'}
                    </div>
                  ) : (
                    <ul className="search-group-list">
                      {commentResults.items.map((comment) => (
                        <li key={comment.id}>
                          <button type="button" className="search-result-item" onClick={() => handleCommentResultSelect(comment)}>
                            <span className="result-primary">{highlightSearchText(comment.snippet)}</span>
                            {formatDistance(comment.distanceKm) && (
                              <span className="result-secondary">{formatDistance(comment.distanceKm)}</span>
                            )}
                            {(() => {
                              const locationText = (comment.location || '').trim();
                              if (locationText) {
                                return <span className="result-secondary">Location · {highlightSearchText(locationText)}</span>;
                              }
                              const drawing = searchCommentDrawings.get(comment.id) || null;
                              if (!drawing) return null;
                              return (
                                <span className="result-secondary">
                                  Location ·{' '}
                                  <GeoLabel
                                    as="span"
                                    drawing={drawing}
                                    fallback="Location"
                                    icon={null}
                                    style={{ display: 'inline', gap: 0 }}
                                  />
                                </span>
                              );
                            })()}
                            {comment.topic && (
                              <span className="result-secondary">Topic · {highlightSearchText(comment.topic.title)}</span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="search-group">
                  <div className="search-group-header">
                    <h4>Users</h4>
                    {userResults.hasMore && (
                      <button
                        type="button"
                        className="search-more-btn"
                        onClick={() => handleLoadMore('users')}
                        disabled={loadingGroups.users}
                      >
                        {loadingGroups.users ? 'Loading…' : 'More'}
                      </button>
                    )}
                  </div>
                  {userResults.items.length === 0 ? (
                    <div className="search-empty">
                      {hasQuery
                        ? <>No users matched “{searchQuery}”.</>
                        : 'Enter a term to search for users.'}
                    </div>
                  ) : (
                    <ul className="search-group-list">
                      {userResults.items
                        .filter(user => user.username)
                        .map((user) => (
                          <li key={user.id}>
                            <button type="button" className="search-result-item" onClick={() => handleUserResultSelect(user)}>
                              <span className="result-primary">@{highlightSearchText(user.username || '')}</span>
                              <span className="result-secondary">{user.reputation} reputation</span>
                            </button>
                          </li>
                        ))}
                      {userResults.items.filter(user => user.username).length === 0 && (
                        <li>
                          <div className="search-empty">No usernames matched “{searchQuery}”.</div>
                        </li>
                      )}
                    </ul>
                  )}
                </div>

                {!hasSearchResults && (
                  <div className="search-empty" style={{ marginTop: 16 }}>
                    {hasQuery || hasSpatialFilter
                      ? <>No results for “{searchQuery}”. Try a different search.</>
                      : 'Start typing to search people, topics, comments, or use the location filter.'}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="topic-list">
            {sortedTopics.map((topic) => (
              <div
                key={topic.id}
                onClick={() => onTopicSelect(topic)}
                className={`topic-item ${selectedTopic?.id === topic.id ? 'selected' : ''
                  }`}
              >
                {isPanelExpanded ? (
                  <>
                    <div className="topic-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <h3 className="topic-title">
                          {topic.title}
                        </h3>
                        {topic.isNsfw && (
                          <span style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 9999, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                            NSFW
                          </span>
                        )}
                        {renderAnonymousBadge(topic)}
                        {renderEditorialBadge(topic)}
                        {renderVisibilityBadge(topic)}
                      </div>
                      <div className="topic-stats">
                        <span style={{ marginLeft: '8px', color: '#666', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <ChatIcon size={12} /> {topic.commentCount}
                        </span>
                      </div>
                    </div>
                    {isPanelExpanded && (
                      <>
                        {topic.isDeleted && topic.deletedBy === 'MODERATOR' ? (
                          <div className="deletion-info" style={{ marginBottom: '12px' }}>
                            <span className="deletion-badge" style={{
                              display: 'inline-block',
                              backgroundColor: '#fee2e2',
                              color: '#dc2626',
                              padding: '2px 8px',
                              borderRadius: 4,
                              fontSize: 12,
                              fontWeight: 500
                            }}>
                              Deleted by moderator
                            </span>
                            {topic.moderationReason && (
                              <p className="moderation-reason" style={{
                                margin: '8px 0 0 0',
                                fontSize: 13,
                                color: '#dc2626',
                                backgroundColor: '#fef2f2',
                                padding: 8,
                                borderRadius: 4,
                                borderLeft: '3px solid #dc2626'
                              }}>
                                Reason: {topic.moderationReason}
                              </p>
                            )}
                          </div>
                        ) : (
                          <LinkifiedText
                            as="p"
                            className="topic-content"
                            text={topic.content}
                          />
                        )}
                      </>
                    )}
                    <div className="topic-footer">
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <GeoLabel
                          lat={topic.latitude}
                          lng={topic.longitude}
                          initialText={topic.city || topic.region || topic.country}
                          fallback="Location"
                          drawing={drawings.find(d => d.topicId === topic.id && !d.commentId)}
                        />
                        <div className="comment-stats">
                          <VoteButton
                            type="upvote"
                            count={topic.upvotes}
                            isActive={userVotes.topics[topic.id] === 'upvote'}
                            onVote={() => { if (!requireAuth()) return; onTopicVote(topic.id, 'upvote'); }}
                          />
                          <VoteButton
                            type="downvote"
                            count={topic.downvotes}
                            isActive={userVotes.topics[topic.id] === 'downvote'}
                            onVote={() => { if (!requireAuth()) return; onTopicVote(topic.id, 'downvote'); }}
                          />
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 'auto', alignItems: 'flex-end', textAlign: 'right', maxWidth: '40%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {renderTopicAuthor(topic, 'comment-author-link')}
                        <span>{new Date(topic.createdAt).toLocaleDateString()}{(() => {
                          try {
                            const ca = new Date((selectedTopic?.createdAt || topic.createdAt) as any).getTime();
                            const ua = (selectedTopic?.updatedAt || (topic as any).updatedAt) ? new Date((selectedTopic?.updatedAt || (topic as any).updatedAt) as any).getTime() : ca;
                            if (ua - ca > 10000) return <span style={{ color: '#6b7280', marginLeft: 6 }}>(edited)</span>;
                          } catch { }
                          return null;
                        })()}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="topic-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <h3 className="topic-title">{topic.title}</h3>
                        {topic.isNsfw && (
                          <span style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 9999, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                            NSFW
                          </span>
                        )}
                        {renderAnonymousBadge(topic)}
                        {renderEditorialBadge(topic)}
                        {renderVisibilityBadge(topic)}
                      </div>
                      <span className="comment-count-badge">
                        <ChatIcon size={12} /> {topic.commentCount}
                      </span>
                    </div>
                  </>
                )}
              </div>
            ))}

            {loading && (
              <div style={{ padding: '32px', textAlign: 'center', color: '#666' }}>
                <p>Loading topics...</p>
              </div>
            )}

            {error && !loading && (
              <div style={{ padding: '32px', textAlign: 'center', color: '#dc2626' }}>
                <p>❌ Failed to load topics</p>
                <p style={{ fontSize: '12px', marginTop: '8px' }}>Check your connection and try refreshing the page.</p>
              </div>
            )}

            {!loading && !error && sortedTopics.length === 0 && (
              <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>🗺️</div>
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                  {activeTab === 'private' ? 'No private topics yet' : 'No topics found here'}
                </h3>
                <p style={{ fontSize: '14px', lineHeight: 1.5 }}>
                  {searchQuery && activeTab !== 'private'
                    ? 'Try adjusting your search terms to find what you’re looking for.'
                    : activeTab === 'private'
                      ? 'Topics you create or that are shared with you will appear here.'
                      : 'Be the first to start a conversation in this area!'}
                </p>
                {!searchQuery && activeTab !== 'private' && Boolean(currentUserId) && (
                  <div style={{ marginTop: '16px', fontSize: '13px', color: '#4b5563' }}>
                    Use the “New Topic” button below to kick off the conversation.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Create Topic Button — only when logged in */}
        {/* Keep create button at the bottom even during search */}
        {Boolean(currentUserId) && userRole === 'READER' && !isCreating && (
          <div className="create-button-container" style={{ marginTop: 'auto' }}>
            <div style={{ color: '#b91c1c', textAlign: 'center', fontWeight: 600, fontSize: 13, padding: '8px 0' }}>
              Your account is read-only right now. Creating topics is disabled.
            </div>
          </div>
        )}
        {Boolean(currentUserId) && userRole !== 'READER' && isPanelExpanded && !isCreating && (
          <div className="create-button-container" style={{ marginTop: 'auto' }}>
            <button
              onClick={onCreateTopic}
              className="create-button"
            >
              <PlusIcon size={18} />
              {isPanelExpanded ? 'New Topic' : 'New'}
            </button>
          </div>
        )}

        {/* Collapsed panel bottom create button — only when logged in */}
        {Boolean(currentUserId) && userRole !== 'READER' && !isPanelExpanded && (
          <div className="create-button-container" style={{ marginTop: 'auto' }}>
            <button onClick={onCreateTopic} className="create-button">
              <PlusIcon size={18} />
              New Topic
            </button>
          </div>
        )}

        {/* Auth CTA — show when not logged in */}
        {!currentUserId && (
          <div className="create-button-container" style={{ paddingTop: 0, marginTop: 'auto' }}>
            <div
              style={{
                width: '100%',
                borderTop: '1px solid #eee',
                padding: '12px 0 0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 8,
                fontSize: 13,
                color: '#555'
              }}
            >
              <div style={{ textAlign: 'center' }}>
                Have ideas to share? Log in to create topics.
              </div>
              <a
                href={`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                className="create-button"
                style={{ textAlign: 'center' }}
              >
                Log in / Sign up
              </a>
            </div>
          </div>
        )}

        {/* Static links */}
        <div
          style={{
            width: '100%',
            padding: '10px 0 12px',
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            justifyContent: 'center',
            fontSize: 13,
            color: '#4b5563'
          }}
        >
          <a href="/about" style={{ color: '#2563eb', textDecoration: 'none' }}>About</a>
          <a href="/how-to" style={{ color: '#2563eb', textDecoration: 'none' }}>How To</a>
          <a href="mailto:support@loconvo.com" style={{ color: '#2563eb', textDecoration: 'none' }}>support@loconvo.com</a>
        </div>
      </div>

      {/* Messages Modal - only show if not in user menu */}
      {messagesOpen && userMenuContent !== 'messages' && (
        <MessagesModal open={messagesOpen} onClose={() => setMessagesOpen(false)} currentUserId={currentUserId} onViewProfile={handleViewProfileFromMessages} />
      )}
    </div>
  );
};

export default TopicPanel;
